/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";

import ProfileFavorites from "../components/ProfileFavorites";
import ForumSpeciesPhotoCard from "../components/ForumSpeciesPhotoCard";
import SpeciesPhotoModal from "../components/SpeciesPhotoModal";
import ContentReportModal from "../components/ContentReportModal";
import SearchModal from "../components/SearchModal";
import { ForumLinkInsert, ForumTopicLinks } from "../components/ForumTopicLinks";

import { auth, db, storage } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";

import { iconUrl, defaultImg } from "../utils/icons";
import { getProfileCountry } from "../utils/profileCountries";

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  startAfter,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";

import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";

import {
  DEFAULT_FORUM_CATEGORY,
  FORUM_CATEGORIES,
  isValidForumCategory,
} from "../utils/forumCategories";
import { resolveTitle } from "../utils/titles";
import CommunityNavBadge from "../components/CommunityNavBadge";
import "../styles/BioIndexCommunityMobile.css";
import {
  useNotificationBadges,
  activityTime,
  activityDayKey,
  bogotaDayKey,
  personalActivityDayKey,
  isRankingNotification,
} from "../context/NotificationBadgeContext";

function getGenusImage(genusName) {
  if (!genusName) return defaultImg;
  return iconUrl(`${genusName}.png`);
}

const MAX_TOPIC_TITLE_LENGTH = 160;
const MAX_TOPIC_BODY_LENGTH = 1000;
const MAX_TOPIC_IMAGE_SIZE = 8 * 1024 * 1024;
const TOPICS_PER_PAGE = 20;
const NOTIFICATIONS_PER_PAGE = 7;
const COMMUNITY_ACTIVITIES_PER_PAGE = 7;

function getCurrentTitleDisplay(titleSnapshot) {
  const storedLabel = titleSnapshot?.titleLabel || titleSnapshot?.detail || "";
  const activityKey = String(
    titleSnapshot?.activityKey || titleSnapshot?.id || "",
  );
  const titleUnlockMarker = "_title_unlocked_";
  const markerIndex = activityKey.lastIndexOf(titleUnlockMarker);
  let titleId = titleSnapshot?.titleId || "";

  if (!titleId && markerIndex >= 0) {
    const encodedTitleId = activityKey.slice(
      markerIndex + titleUnlockMarker.length,
    );

    try {
      titleId = decodeURIComponent(encodedTitleId);
    } catch {
      titleId = encodedTitleId;
    }
  }

  const currentTitle = resolveTitle(titleId, storedLabel);

  return {
    label: currentTitle?.label || storedLabel || "New Title",
    description:
      currentTitle?.description || titleSnapshot?.titleDescription || "",
  };
}

function getCurrentNotificationMessage(notification) {
  const message = String(notification?.message || "");

  if (
    notification?.type !== "kudos_received" ||
    notification?.activityType !== "title_unlocked"
  ) {
    return message;
  }

  const currentTitle = getCurrentTitleDisplay(notification);

  return message.replace(
    /unlocking the title [“"][^”"]+[”"]/,
    `unlocking the title “${currentTitle.label}”`,
  );
}

function timestampToMillis(value) {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (value.seconds) return value.seconds * 1000;

  return Number(value) || 0;
}

function formatRelativeTime(value) {
  const time = timestampToMillis(value);

  if (!time) return "Just now";

  const difference = Math.max(0, Date.now() - time);
  const minutes = Math.floor(difference / 60000);
  const hours = Math.floor(difference / 3600000);
  const days = Math.floor(difference / 86400000);

  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;

  return new Date(time).toLocaleDateString();
}

function formatTitleUnlockCondition(value) {
  const condition = String(value || "")
    .trim()
    .replace(/[.!]+$/, "");

  if (!condition) return "";

  const verbReplacements = [
    [/^Record\b/i, "recording"],
    [/^Upload\b/i, "uploading"],
    [/^Unlock\b/i, "unlocking"],
    [/^Reach\b/i, "reaching"],
    [/^Photograph\b/i, "photographing"],
    [/^Observe\b/i, "observing"],
    [/^Document\b/i, "documenting"],
    [/^Identify\b/i, "identifying"],
    [/^Discover\b/i, "discovering"],
    [/^Complete\b/i, "completing"],
    [/^Collect\b/i, "collecting"],
    [/^Capture\b/i, "capturing"],
    [/^Have\b/i, "having"],
    [/^Find\b/i, "finding"],
    [/^Add\b/i, "adding"],
    [/^Earn\b/i, "earning"],
    [/^Get\b/i, "getting"],
    [/^Log\b/i, "logging"],
    [/^See\b/i, "seeing"],
  ];

  for (const [pattern, replacement] of verbReplacements) {
    if (pattern.test(condition)) {
      return condition.replace(pattern, replacement);
    }
  }

  return condition.charAt(0).toLowerCase() + condition.slice(1);
}

function formatCommunityActivityDate(dateKey) {
  if (!dateKey) return "Recent uploads";

  const [year, month, day] = String(dateKey).split("-").map(Number);

  if (!year || !month || !day) return "Recent uploads";

  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

// English calendar heading for personal activity cards. The stored day key
// comes from the Bogotá calendar, so do not convert it through UTC.
function formatAccomplishmentHeading(dayKey, subject = "Accomplishments") {
  const parts = String(dayKey || "")
    .split("-")
    .map(Number);
  const [year, month, day] = parts;
  if (parts.length !== 3 || !year || !month || !day || month > 12 || day > 31) {
    return subject === "Accomplishments"
      ? "Recent accomplishments"
      : "Recent kudos";
  }

  const monthLabel = new Date(year, month - 1, 1).toLocaleString("en-US", {
    month: "long",
  });
  const suffix =
    day % 100 >= 11 && day % 100 <= 13
      ? "th"
      : { 1: "st", 2: "nd", 3: "rd" }[day % 10] || "th";

  return `${subject} from ${monthLabel} ${day}${suffix}, ${year}`;
}

function PersonalActivityIcon({ type }) {
  const common = {
    className: "h-4 w-4",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
  };

  return (
    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#374049]/5 text-[#374049]/70">
      <svg {...common}>
        {type === "photos" ? (
          <>
            <path d="M4 7h3l2-2h6l2 2h3v12H4z" />
            <circle cx="12" cy="13" r="3.2" />
          </>
        ) : type === "genus" ? (
          <>
            <path d="M20 4c-8.8 0-15 4.8-15 11a5 5 0 0 0 5 5c6.2 0 11-6.2 10-16Z" />
            <path d="M5 20c3-5 7-8 12-11" />
          </>
        ) : type === "titles" ? (
          <>
            <circle cx="12" cy="8" r="5" />
            <path d="m8.6 12-1 9 4.4-2.6L16.4 21l-1-9" />
          </>
        ) : (
          <>
            <path d="m12 3 2.1 5.9L20 11l-5.9 2.1L12 19l-2.1-5.9L4 11l5.9-2.1Z" />
            <path d="M19 17v4M17 19h4" />
          </>
        )}
      </svg>
    </span>
  );
}

function sortForumTopics(topics) {
  return [...topics].sort((a, b) => {
    if (!!a.pinned !== !!b.pinned) {
      return a.pinned ? -1 : 1;
    }

    return (
      timestampToMillis(b.lastActivityAt || b.createdAt) -
      timestampToMillis(a.lastActivityAt || a.createdAt)
    );
  });
}

function getRankingSpeciesCount(profile = {}) {
  const value = Number(
    profile.rankingSpeciesCount ?? profile.speciesCount ?? 0,
  );

  return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

function getRankingPhotoCount(profile = {}) {
  const value = Number(
    profile.rankingPhotoCount ?? profile.photoCount ?? profile.totalPhotos ?? 0,
  );

  return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

const RANKING_TAXA = [
  { key: "all", label: "All Species" },
  { key: "Animalia", label: "Animalia" },
  { key: "Plantae", label: "Plantae" },
  { key: "Fungi", label: "Fungi" },
  { key: "Mammalia", label: "Mammalia" },
  { key: "Aves", label: "Aves" },
  { key: "Reptilia", label: "Reptilia" },
  { key: "Amphibia", label: "Amphibia" },
  { key: "Arthropoda", label: "Arthropoda" },
  { key: "Mollusca", label: "Mollusca" },
  { key: "Cnidaria", label: "Cnidaria" },
  { key: "Echinodermata", label: "Echinodermata" },
  { key: "Actinopterygii", label: "Actinopterygii" },
  { key: "Chondrichthyes", label: "Chondrichthyes" },
];

function CommunityRankingsView({
  currentUid,
  profiles,
  communityTotals,
  friends,
  sentRequests,
  receivedRequests,
  loading,
  error,
  view,
  onChangeView,
  taxon,
  onChangeTaxon,
  taxonomicData,
  taxonomicLoading,
  taxonomicError,
  onRequestProfile,
  onRequestFriend,
  onAcceptRequest,
  onDeclineRequest,
  actionUid,
}) {
  const [openFriendActionsUid, setOpenFriendActionsUid] = useState("");
  const [taxonMenuOpen, setTaxonMenuOpen] = useState(false);

  const filteredTaxon = taxon !== "all";
  const selectedTotals = filteredTaxon
    ? taxonomicData?.communityTotalsByCategory?.[taxon] || null
    : communityTotals;

  const selectedTaxonLabel =
    RANKING_TAXA.find((item) => item.key === taxon)?.label || "All Species";

  const rankedProfiles = useMemo(() => {
    const friendUidSet = new Set(friends);
    const taxonProfileCounts = taxonomicData?.profileCountsByUid || {};

    const eligibleProfiles = profiles
      .filter((profile) => profile?.uid)
      .map((profile) => {
        if (!filteredTaxon) return profile;

        const categoryCounts = taxonProfileCounts[profile.uid]?.[taxon] || {};

        return {
          ...profile,
          rankingSpeciesCount: Number(categoryCounts.speciesCount || 0),
          rankingPhotoCount: Number(categoryCounts.photoCount || 0),
        };
      })
      .filter(
        (profile) => !filteredTaxon || getRankingSpeciesCount(profile) > 0,
      )
      .filter((profile) => {
        if (view === "all") return true;
        return profile.uid === currentUid || friendUidSet.has(profile.uid);
      })
      .sort((a, b) => {
        const speciesDifference =
          getRankingSpeciesCount(b) - getRankingSpeciesCount(a);

        if (speciesDifference !== 0) return speciesDifference;

        const photoDifference =
          getRankingPhotoCount(b) - getRankingPhotoCount(a);

        if (photoDifference !== 0) return photoDifference;

        return String(a.name || "BioIndex User").localeCompare(
          String(b.name || "BioIndex User"),
        );
      });

    return eligibleProfiles.map((profile) => ({
      ...profile,
      rankingPosition:
        1 +
        eligibleProfiles.filter(
          (candidate) =>
            getRankingSpeciesCount(candidate) > getRankingSpeciesCount(profile),
        ).length,
    }));
  }, [
    currentUid,
    friends,
    profiles,
    view,
    taxon,
    filteredTaxon,
    taxonomicData,
  ]);

  const friendUidSet = useMemo(() => new Set(friends), [friends]);
  const sentRequestUidSet = useMemo(
    () => new Set(sentRequests.map((request) => request.toUid)),
    [sentRequests],
  );
  const receivedRequestByUid = useMemo(
    () =>
      new Map(receivedRequests.map((request) => [request.fromUid, request])),
    [receivedRequests],
  );

  return (
    <section>
      <div className="flex flex-col gap-3 border-b border-[#93CA87]/30 pb-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div className="w-full">
            <h2
              className="text-center text-3xl text-[#374049] sm:text-left"
              style={{ fontFamily: "Cormorant Garamond" }}
            >
              {taxon === "all" ? "Species Rankings" : `${taxon} Rankings`}
            </h2>

            {selectedTotals && (
              <p
                className="mt-2 text-center text-sm leading-relaxed text-slate-500 sm:text-left"
                aria-label={`${selectedTotals.uniqueSpecies.toLocaleString()} unique species and ${selectedTotals.totalPhotos.toLocaleString()} photos documented in this ranking`}
              >
                <span className="font-medium text-[#374049]">
                  BioIndex community total:
                </span>{" "}
                <strong className="font-semibold text-[#01767A]">
                  {selectedTotals.uniqueSpecies.toLocaleString()}
                </strong>{" "}
                unique species <span className="mx-1 text-[#93CA87]">•</span>
                <strong className="font-semibold text-[#01767A]">
                  {selectedTotals.totalPhotos.toLocaleString()}
                </strong>{" "}
                photos documented
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div
            className="inline-flex rounded-xl border border-[#93CA87]/45 bg-[#FDFBF8] p-1"
            role="group"
            aria-label="Ranking scope"
          >
            {[
              ["all", "All BioIndex"],
              ["friends", "My friends"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={view === key}
                onClick={() => onChangeView(key)}
                className={`rounded-lg px-3 py-2 text-xs font-semibold transition sm:px-4 ${
                  view === key
                    ? "bg-[#01767A] text-white"
                    : "text-[#374049]/65 hover:bg-white hover:text-[#374049]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="relative">
            <button
              type="button"
              onClick={() => setTaxonMenuOpen((open) => !open)}
              aria-expanded={taxonMenuOpen}
              aria-haspopup="menu"
              className="inline-flex items-center gap-2 rounded-xl border border-[#93CA87]/55 bg-white px-3 py-2 text-xs font-semibold text-[#374049] shadow-sm transition hover:border-[#24C3BE]/70 hover:bg-[#24C3BE]/5"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4 w-4 text-[#01767A]"
                aria-hidden="true"
              >
                <path d="M4 7h16" />
                <path d="M7 12h10" />
                <path d="M10 17h4" />
              </svg>
              <span className="hidden sm:inline">Filter:</span>
              <span>{selectedTaxonLabel}</span>
            </button>

            {taxonMenuOpen && (
              <div
                className="absolute right-0 z-30 mt-2 max-h-80 w-56 overflow-y-auto rounded-2xl border border-[#93CA87]/45 bg-white p-1.5 shadow-[0_12px_28px_rgba(55,64,73,0.16)]"
                role="menu"
                aria-label="Ranking taxonomic group"
              >
                {RANKING_TAXA.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    role="menuitemradio"
                    aria-checked={taxon === item.key}
                    onClick={() => {
                      onChangeTaxon(item.key);
                      setTaxonMenuOpen(false);
                    }}
                    className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition ${
                      taxon === item.key
                        ? "bg-[#01767A] text-white"
                        : "text-[#374049] hover:bg-[#F3FAF8]"
                    }`}
                  >
                    <span>{item.label}</span>
                    {taxon === item.key && (
                      <span aria-hidden="true" className="text-xs">
                        ✓
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {taxonomicError && filteredTaxon && (
        <p role="alert" className="mt-3 text-sm text-red-700">
          {taxonomicError}
        </p>
      )}

      {error && (
        <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ||
      (filteredTaxon &&
        (taxonomicLoading || (!taxonomicData && !taxonomicError))) ? (
        <div className="flex min-h-64 flex-col items-center justify-center text-center">
          <div className="h-9 w-9 animate-spin rounded-full border-4 border-[#93CA87]/40 border-t-[#24C3BE]" />
          <div className="mt-3 text-sm text-slate-500">
            {filteredTaxon
              ? `Preparing ${taxon} rankings...`
              : "Preparing the rankings..."}
          </div>
        </div>
      ) : filteredTaxon && !taxonomicData ? (
        <p className="py-10 text-center text-sm text-slate-500">
          Taxonomic rankings are not available yet.
        </p>
      ) : rankedProfiles.length > 0 ? (
        <div className="mt-2 divide-y divide-[#93CA87]/25">
          {rankedProfiles.map((profile) => {
            const isCurrentUser = profile.uid === currentUid;
            const isFriend = friendUidSet.has(profile.uid);
            const canViewProfile = isCurrentUser || isFriend;
            const requestSent = sentRequestUidSet.has(profile.uid);
            const receivedRequest = receivedRequestByUid.get(profile.uid);
            const busy = actionUid === profile.uid;
            const profileName = profile.name || "BioIndex User";
            const initial =
              String(profileName).trim().charAt(0).toUpperCase() || "B";
            const speciesCount = getRankingSpeciesCount(profile);
            const photoCount = getRankingPhotoCount(profile);

            return (
              <article
                key={profile.uid}
                className="relative grid grid-cols-[2.5rem_minmax(0,1fr)] items-center gap-x-3 gap-y-1 border-b border-[#93CA87]/25 py-4 pr-1 last:border-b-0 sm:grid-cols-[3.25rem_minmax(0,1fr)_auto] sm:gap-x-4 sm:border-b-0 sm:px-2"
              >
                {!isCurrentUser && (
                  <div className="absolute right-0 top-3 z-20 sm:hidden">
                    {isFriend ? (
                      <span
                        title="Friends"
                        aria-label="Friends"
                        className="inline-flex h-7 w-7 items-center justify-center text-[#01767A]"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="h-5 w-5"
                          aria-hidden="true"
                        >
                          <circle cx="10" cy="8" r="3.2" />
                          <path d="M4 19v-1.2a6 6 0 0 1 12 0V19" />
                          <path d="m16 15.5 1.6 1.6 3-3.2" />
                        </svg>
                      </span>
                    ) : requestSent ? (
                      <span
                        title="Friend request sent"
                        aria-label="Friend request sent"
                        className="inline-flex h-7 w-7 items-center justify-center text-[#374049]/45"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="h-5 w-5"
                          aria-hidden="true"
                        >
                          <circle cx="10" cy="8" r="3.2" />
                          <path d="M4 19v-1.2a6 6 0 0 1 12 0V19" />
                          <path d="M18.2 9.2v5.2M15.6 11.8h5.2" />
                        </svg>
                      </span>
                    ) : (
                      <button
                        type="button"
                        disabled={busy}
                        title={
                          receivedRequest
                            ? "Respond to friend request"
                            : "Add friend"
                        }
                        aria-label={
                          receivedRequest
                            ? `Respond to ${profileName}'s friend request`
                            : `Add ${profileName} as a friend`
                        }
                        onClick={() => {
                          if (receivedRequest) {
                            setOpenFriendActionsUid((uid) =>
                              uid === profile.uid ? "" : profile.uid,
                            );
                          } else {
                            onRequestFriend(profile);
                          }
                        }}
                        className="inline-flex h-7 w-7 items-center justify-center text-[#01767A] transition hover:text-[#24C3BE] disabled:opacity-50"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="h-5 w-5"
                          aria-hidden="true"
                        >
                          <circle cx="10" cy="8" r="3.2" />
                          <path d="M4 19v-1.2a6 6 0 0 1 12 0V19" />
                          <path d="M18.2 9.2v5.2M15.6 11.8h5.2" />
                        </svg>
                      </button>
                    )}

                    {receivedRequest &&
                      openFriendActionsUid === profile.uid && (
                        <div className="absolute right-0 top-7 z-30 flex gap-1 rounded-xl border border-[#93CA87]/50 bg-white p-2 shadow-lg">
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => {
                              setOpenFriendActionsUid("");
                              onAcceptRequest(receivedRequest);
                            }}
                            className="rounded-lg bg-[#01767A] px-3 py-1.5 text-xs font-semibold text-white"
                          >
                            Accept
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => {
                              setOpenFriendActionsUid("");
                              onDeclineRequest(receivedRequest);
                            }}
                            className="rounded-lg px-3 py-1.5 text-xs font-semibold text-[#374049]"
                          >
                            Decline
                          </button>
                        </div>
                      )}
                  </div>
                )}

                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-full border text-sm font-semibold sm:h-12 sm:w-12 sm:text-base ${
                    isCurrentUser
                      ? "border-[#24C3BE] bg-[#24C3BE] text-white"
                      : "border-[#93CA87]/55 bg-[#FDFBF8] text-[#374049]"
                  }`}
                  title={`Rank ${profile.rankingPosition}`}
                >
                  #{profile.rankingPosition}
                </div>

                <div className="flex min-w-0 items-center gap-3">
                  <button
                    type="button"
                    disabled={!canViewProfile}
                    onClick={() => {
                      if (canViewProfile) onRequestProfile(profile);
                    }}
                    className={`shrink-0 rounded-full focus:outline-none focus:ring-2 focus:ring-[#24C3BE]/30 ${
                      canViewProfile ? "cursor-pointer" : "cursor-default"
                    }`}
                    aria-label={
                      canViewProfile
                        ? `Visit ${profileName}'s profile`
                        : `${profileName}'s profile is available after you become friends`
                    }
                    title={
                      canViewProfile
                        ? undefined
                        : "Become friends to view this profile"
                    }
                  >
                    {profile.photoURL ? (
                      <img
                        src={profile.photoURL}
                        alt=""
                        className="h-11 w-11 rounded-full border border-[#93CA87]/45 object-cover sm:h-12 sm:w-12"
                      />
                    ) : (
                      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#374049] text-xs font-semibold text-white sm:h-12 sm:w-12">
                        {initial}
                      </span>
                    )}
                  </button>

                  <div className="min-w-0">
                    <button
                      type="button"
                      disabled={!canViewProfile}
                      onClick={() => {
                        if (canViewProfile) onRequestProfile(profile);
                      }}
                      className={`block max-w-full truncate text-left text-sm font-semibold text-[#374049] focus:outline-none sm:text-base ${
                        canViewProfile
                          ? "transition hover:text-[#01767A] focus:underline"
                          : "cursor-default"
                      }`}
                      title={
                        canViewProfile
                          ? undefined
                          : "Become friends to view this profile"
                      }
                    >
                      {profileName}
                      {profile.country &&
                        getProfileCountry(profile.country) && (
                          <span
                            className="ml-2 inline-block align-middle text-base leading-none"
                            role="img"
                            aria-label={getProfileCountry(profile.country).name}
                            title={getProfileCountry(profile.country).name}
                          >
                            {getProfileCountry(profile.country).flag}
                          </span>
                        )}
                      {isCurrentUser && (
                        <span className="ml-1.5 text-[10px] font-medium uppercase tracking-wide text-[#01767A]">
                          You
                        </span>
                      )}
                    </button>

                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-slate-500">
                      <span>
                        <strong className="font-semibold text-[#374049]">
                          {speciesCount.toLocaleString()}
                        </strong>{" "}
                        species
                      </span>

                      <span aria-hidden="true" className="text-[#93CA87]">
                        •
                      </span>

                      <span>
                        {photoCount.toLocaleString()}{" "}
                        {photoCount === 1 ? "photo" : "photos"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="hidden justify-end sm:col-span-1 sm:flex">
                  {isCurrentUser ? (
                    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#01767A]">
                      Your collection
                    </span>
                  ) : isFriend ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#01767A]">
                      <span aria-hidden="true">✓</span> Friends
                    </span>
                  ) : requestSent ? (
                    <span className="text-xs font-medium text-slate-400">
                      Request sent
                    </span>
                  ) : receivedRequest ? (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onDeclineRequest(receivedRequest)}
                        className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-500 transition hover:bg-slate-100 disabled:opacity-50"
                      >
                        Decline
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onAcceptRequest(receivedRequest)}
                        className="rounded-lg bg-[#01767A] px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-[#025f62] disabled:opacity-50"
                      >
                        {busy ? "Saving..." : "Accept"}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onRequestFriend(profile)}
                      className="rounded-lg border border-[#93CA87]/55 bg-white px-3 py-1.5 text-xs font-semibold text-[#374049] transition hover:border-[#24C3BE] hover:bg-[#24C3BE]/10 disabled:opacity-50"
                    >
                      Add friend
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="py-16 text-center">
          <h3
            className="text-3xl text-[#374049]"
            style={{ fontFamily: "Cormorant Garamond" }}
          >
            No rankings yet
          </h3>
          <p className="mt-2 text-sm text-slate-500">
            {view === "friends"
              ? "Add friends to build your personal ranking circle."
              : "BioIndex rankings will appear here as collections grow."}
          </p>
        </div>
      )}
    </section>
  );
}

function CommunityPhotoPreviewStrip({
  photoRefs,
  uploadedPhotoCount,
  onSelectPhoto,
  onShowAll,
  flushTop = false,
}) {
  const containerRef = useRef(null);
  const measurementImageRefs = useRef([]);
  const measurementCounterRef = useRef(null);

  const [visiblePhotoCount, setVisiblePhotoCount] = useState(() =>
    Math.min(3, photoRefs.length),
  );
  const [measurementVersion, setMeasurementVersion] = useState(0);

  const totalPhotoCount = Math.max(uploadedPhotoCount, photoRefs.length);
  const actualVisiblePhotoCount = Math.min(visiblePhotoCount, photoRefs.length);
  const visiblePhotoRefs = photoRefs.slice(0, actualVisiblePhotoCount);
  const remainingPhotoCount = Math.max(
    0,
    totalPhotoCount - actualVisiblePhotoCount,
  );

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return undefined;

    function updateVisiblePhotoCount() {
      const availableWidth = container.clientWidth;

      if (!availableWidth) return;

      const gap = 6;
      const counterWidth =
        measurementCounterRef.current?.getBoundingClientRect().width || 48;

      const photoWidths = photoRefs.map(
        (_, index) =>
          measurementImageRefs.current[index]?.getBoundingClientRect().width ||
          48,
      );

      let nextVisiblePhotoCount = 0;

      for (
        let candidateCount = photoRefs.length;
        candidateCount >= 0;
        candidateCount -= 1
      ) {
        const hiddenPhotoCount = Math.max(0, totalPhotoCount - candidateCount);

        const photosWidth = photoWidths
          .slice(0, candidateCount)
          .reduce((total, width) => total + width, 0);

        const photosGapWidth = Math.max(0, candidateCount - 1) * gap;

        const counterSpace =
          hiddenPhotoCount > 0
            ? counterWidth + (candidateCount > 0 ? gap : 0)
            : 0;

        if (photosWidth + photosGapWidth + counterSpace <= availableWidth) {
          nextVisiblePhotoCount = candidateCount;
          break;
        }
      }

      setVisiblePhotoCount(nextVisiblePhotoCount);
    }

    const frameId = window.requestAnimationFrame(updateVisiblePhotoCount);

    let resizeObserver;

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateVisiblePhotoCount);
      resizeObserver.observe(container);
    } else {
      window.addEventListener("resize", updateVisiblePhotoCount);
    }

    return () => {
      window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateVisiblePhotoCount);
    };
  }, [measurementVersion, photoRefs, totalPhotoCount]);

  return (
    <div
      ref={containerRef}
      className={`relative min-w-0 overflow-hidden ${flushTop ? "mt-0" : "mt-3"}`}
    >
      <div className="flex flex-nowrap items-center gap-1.5">
        {visiblePhotoRefs.map((photoRef, index) => (
          <button
            key={`${photoRef.speciesId}_${photoRef.formId || index}`}
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onSelectPhoto?.(photoRef);
            }}
            onKeyDown={(event) => event.stopPropagation()}
            aria-label={`View uploaded photo ${index + 1}`}
            className="block shrink-0 border border-[#374049]/15 bg-white transition hover:border-[#24C3BE]/70 focus:outline-none focus:ring-2 focus:ring-[#24C3BE]/30"
          >
            <img
              src={photoRef.formUrl}
              alt=""
              loading="lazy"
              className="block h-auto w-auto max-h-[46px] max-w-[46px] object-contain"
            />
          </button>
        ))}

        {remainingPhotoCount > 0 &&
          (onShowAll ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onShowAll();
              }}
              onKeyDown={(event) => event.stopPropagation()}
              aria-label={`View all ${totalPhotoCount.toLocaleString()} uploaded photos`}
              className="flex h-12 min-w-12 shrink-0 items-center justify-center border border-[#374049]/15 bg-white px-2 text-xs font-semibold text-slate-500 transition hover:border-[#24C3BE]/70 focus:outline-none focus:ring-2 focus:ring-[#24C3BE]/30"
            >
              +{remainingPhotoCount.toLocaleString()}
            </button>
          ) : (
            <div className="flex h-12 min-w-12 shrink-0 items-center justify-center border border-[#374049]/15 bg-white px-2 text-xs font-semibold text-slate-500">
              +{remainingPhotoCount.toLocaleString()}
            </div>
          ))}
      </div>

      <div
        className="pointer-events-none absolute left-0 top-0 invisible flex w-max flex-nowrap items-center gap-1.5"
        aria-hidden="true"
      >
        {photoRefs.map((photoRef, index) => (
          <img
            key={`measure_${photoRef.speciesId}_${photoRef.formId || index}`}
            ref={(element) => {
              measurementImageRefs.current[index] = element;
            }}
            src={photoRef.formUrl}
            alt=""
            className="block h-auto w-auto max-h-[46px] max-w-[46px] shrink-0 border border-[#374049]/15 object-contain"
            onLoad={() => {
              setMeasurementVersion((current) => current + 1);
            }}
          />
        ))}

        <div
          ref={measurementCounterRef}
          className="flex h-12 min-w-12 shrink-0 items-center justify-center border border-[#374049]/15 bg-white px-2 text-xs font-semibold text-slate-500"
        >
          +{totalPhotoCount.toLocaleString()}
        </div>
      </div>
    </div>
  );
}

function getCommunityActivityTime(item) {
  return activityTime(item);
}

function getDatedItemDayKey(item) {
  return activityDayKey(item);
}

async function loadCompleteCommunityGenusGroups(ownerUid, activities) {
  const requestedGroupKeys = new Set();
  const requestedDayKeys = new Set();

  activities.forEach((activity) => {
    const actorKey = String(
      activity.actorUid || activity.actorName || activity.id || "",
    );
    const dayKey = getDatedItemDayKey(activity);

    if (!actorKey || !dayKey) return;

    requestedGroupKeys.add(`${actorKey}_${dayKey}`);
    requestedDayKeys.add(dayKey);
  });

  if (requestedGroupKeys.size === 0) return [];

  const daySnapshots = await Promise.all(
    [...requestedDayKeys].map((dayKey) =>
      getDocs(
        query(
          collection(db, "users", ownerUid, "communityFeed"),
          where("dateKey", "==", dayKey),
        ),
      ),
    ),
  );

  const completedActivities = [];

  daySnapshots.forEach((snapshot) => {
    snapshot.docs.forEach((activityDocument) => {
      const activity = {
        id: activityDocument.id,
        ownerUid,
        ...activityDocument.data(),
      };

      const actorKey = String(
        activity.actorUid || activity.actorName || activity.id || "",
      );
      const dayKey = getDatedItemDayKey(activity);

      if (requestedGroupKeys.has(`${actorKey}_${dayKey}`)) {
        completedActivities.push(activity);
      }
    });
  });

  return completedActivities;
}

/**
 * Revalidates stored Community Feed photo references against the actor's
 * CURRENT collection.
 *
 * This is intentionally defensive. The Cloud Function should keep feed
 * documents synchronized going forward, but this also repairs the display of
 * stale activities created before that backend reconciliation existed.
 */
async function reconcileLoadedCommunityPhotoActivities(activities) {
  const photoActivitiesByActor = new Map();

  activities.forEach((activity) => {
    if (
      activity.type !== "photos_uploaded" ||
      !activity.actorUid ||
      !Array.isArray(activity.photoRefs) ||
      activity.photoRefs.length === 0
    ) {
      return;
    }

    const actorActivities = photoActivitiesByActor.get(activity.actorUid) || [];

    actorActivities.push(activity);
    photoActivitiesByActor.set(activity.actorUid, actorActivities);
  });

  if (photoActivitiesByActor.size === 0) {
    return activities;
  }

  const reconciledActivitiesById = new Map();

  await Promise.all(
    Array.from(photoActivitiesByActor.entries()).map(
      async ([actorUid, actorActivities]) => {
        const referencedSpeciesIds = Array.from(
          new Set(
            actorActivities
              .flatMap((activity) => activity.photoRefs || [])
              .map((photoRef) => String(photoRef?.speciesId || "").trim())
              .filter(Boolean),
          ),
        );

        const speciesById = new Map();

        const referencedSpeciesSnapshots = await Promise.all(
          referencedSpeciesIds.map((speciesId) =>
            getDoc(doc(db, "users", actorUid, "species", speciesId)),
          ),
        );

        referencedSpeciesSnapshots.forEach((speciesSnapshot) => {
          if (!speciesSnapshot.exists()) return;

          speciesById.set(speciesSnapshot.id, {
            id: speciesSnapshot.id,
            data: speciesSnapshot.data() || {},
          });
        });

        function findMatchingFormInSpecies(photoRef) {
          const speciesId = String(photoRef?.speciesId || "").trim();
          const currentSpecies = speciesById.get(speciesId);

          if (!currentSpecies) return null;

          const forms = Array.isArray(currentSpecies.data.forms)
            ? currentSpecies.data.forms
            : [];

          const matchingForm = forms.find(
            (form) =>
              (photoRef.formId && form?.id === photoRef.formId) ||
              (photoRef.formUrl && form?.url === photoRef.formUrl),
          );

          if (!matchingForm) return null;

          return {
            speciesId: currentSpecies.id,
            speciesData: currentSpecies.data,
            form: matchingForm,
          };
        }

        /*
         * Normally the referenced species document still exists, so the cheap
         * direct getDocs above are enough.
         *
         * Only scan the actor's full collection when a stored reference cannot
         * be found. That is the important recovery path for renamed or
         * reclassified species whose document ID changed.
         */
        const hasUnresolvedReference = actorActivities.some((activity) =>
          (activity.photoRefs || []).some(
            (photoRef) => !findMatchingFormInSpecies(photoRef),
          ),
        );

        if (hasUnresolvedReference) {
          const fullSpeciesSnapshot = await getDocs(
            collection(db, "users", actorUid, "species"),
          );

          fullSpeciesSnapshot.docs.forEach((speciesDocument) => {
            speciesById.set(speciesDocument.id, {
              id: speciesDocument.id,
              data: speciesDocument.data() || {},
            });
          });
        }

        const currentFormsByUrl = new Map();
        const currentFormsById = new Map();

        speciesById.forEach((currentSpecies) => {
          const forms = Array.isArray(currentSpecies.data.forms)
            ? currentSpecies.data.forms
            : [];

          forms.forEach((form) => {
            const formUrl = String(form?.url || "").trim();
            const formId = String(form?.id || "").trim();

            const currentReference = {
              speciesId: currentSpecies.id,

              scientificName:
                currentSpecies.data.name ||
                currentSpecies.data.scientificName ||
                currentSpecies.id,

              commonName:
                currentSpecies.data.common ||
                currentSpecies.data.commonName ||
                "",

              formId,
              formUrl,

              locationName: String(form?.location || form?.locationName || "")
                .split(",")[0]
                .trim(),
            };

            if (formUrl) {
              currentFormsByUrl.set(formUrl, currentReference);
            }

            if (formId && !currentFormsById.has(formId)) {
              currentFormsById.set(formId, currentReference);
            }
          });
        });

        actorActivities.forEach((activity) => {
          const nextRefsByKey = new Map();

          activity.photoRefs.forEach((photoRef) => {
            const directMatch = findMatchingFormInSpecies(photoRef);

            let currentReference = null;

            if (directMatch) {
              currentReference = {
                speciesId: directMatch.speciesId,

                scientificName:
                  directMatch.speciesData.name ||
                  directMatch.speciesData.scientificName ||
                  directMatch.speciesId,

                commonName:
                  directMatch.speciesData.common ||
                  directMatch.speciesData.commonName ||
                  "",

                formId: String(directMatch.form?.id || ""),
                formUrl: String(directMatch.form?.url || ""),

                locationName: String(
                  directMatch.form?.location ||
                    directMatch.form?.locationName ||
                    "",
                )
                  .split(",")[0]
                  .trim(),
              };
            } else {
              currentReference =
                (photoRef.formUrl && currentFormsByUrl.get(photoRef.formUrl)) ||
                (photoRef.formId && currentFormsById.get(photoRef.formId)) ||
                null;
            }

            /*
             * No current form means that historical photo has been deleted.
             */
            if (!currentReference?.formUrl) return;

            const referenceKey = `${currentReference.speciesId}_${
              currentReference.formId || currentReference.formUrl
            }`;

            nextRefsByKey.set(referenceKey, currentReference);
          });

          const photoRefs = Array.from(nextRefsByKey.values());

          /*
           * All photos represented by this activity are gone.
           * Hide the stale activity entirely.
           */
          if (photoRefs.length === 0) {
            reconciledActivitiesById.set(activity.id, null);
            return;
          }

          const uploadLocations = Array.from(
            new Set(
              photoRefs
                .map((photoRef) => String(photoRef.locationName || "").trim())
                .filter(Boolean),
            ),
          );

          reconciledActivitiesById.set(activity.id, {
            ...activity,

            photoRefs,

            uploadedPhotoCount: photoRefs.length,

            uploadedSpeciesCount: new Set(
              photoRefs.map((photoRef) => photoRef.speciesId),
            ).size,

            uploadLocations,
          });
        });
      },
    ),
  );

  return activities
    .map((activity) =>
      reconciledActivitiesById.has(activity.id)
        ? reconciledActivitiesById.get(activity.id)
        : activity,
    )
    .filter(Boolean);
}

function groupCommunityFeedItems(activities) {
  // Merge uploads, genus cards and titles from the SAME friend on the SAME
  // day into one presentation item. Original documents (and kudos) remain.
  const groups = new Map();
  activities.forEach((activity) => {
    const actor = String(
      activity.actorUid || activity.actorName || activity.id,
    );
    const dateKey = getDatedItemDayKey(activity);
    const key = `${actor}_${dateKey}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(activity);
  });
  return [...groups.entries()].map(([key, group]) =>
    group.length === 1 &&
    !["photos_uploaded", "genus_unlocked", "title_unlocked"].includes(
      group[0].type,
    )
      ? { kind: "activity", id: `activity_${group[0].id}`, activity: group[0] }
      : {
          kind: "daily_group",
          id: `community_daily_${key}`,
          activities: group,
        },
  );
}

function groupNotificationItems(notifications) {
  const grouped = [];
  const dailyGroups = new Map();
  const rankingGroups = new Map();

  notifications.forEach((notification) => {
    const day = personalActivityDayKey(notification);
    if (day) {
      if (!dailyGroups.has(day)) {
        const group = {
          kind: "personal_day",
          id: `personal_day_${day}`,
          day,
          notifications: [],
        };
        dailyGroups.set(day, group);
        grouped.push(group);
      }
      dailyGroups.get(day).notifications.push(notification);
    } else if (isRankingNotification(notification)) {
      const dayKey = bogotaDayKey(notification.createdAt) || notification.id;
      if (!rankingGroups.has(dayKey)) {
        const group = {
          kind: "ranking_group",
          id: `notification_ranking_${dayKey}`,
          notifications: [],
        };
        rankingGroups.set(dayKey, group);
        grouped.push(group);
      }
      rankingGroups.get(dayKey).notifications.push(notification);
    } else {
      grouped.push({
        kind: "notification",
        id: `notification_${notification.id}`,
        notification,
      });
    }
  });

  // Kudos on an older accomplishment should bring that day's card back to the
  // top of My Activity, even though its original creation date is unchanged.
  const lastInteraction = (item) => {
    const items =
      item.notifications || (item.notification ? [item.notification] : []);
    return Math.max(
      0,
      ...items.map((entry) =>
        Math.max(
          timestampToMillis(entry.kudosUpdatedAt),
          timestampToMillis(entry.updatedAt),
          timestampToMillis(entry.createdAt),
        ),
      ),
    );
  };
  return grouped.sort((a, b) => lastInteraction(b) - lastInteraction(a));
}

function GenusUnlockSummary({
  items,
  leadingText,
  timestampValue = null,
  onSelect = null,
}) {
  const genusCount = items.length;

  return (
    <>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <div className="text-sm leading-relaxed text-slate-600">
          {leadingText}{" "}
          {genusCount === 1 ? (
            <>
              the genus card{" "}
              <span className="font-semibold italic text-[#374049]">
                {items[0]?.genusName || "Unknown genus"}
              </span>
            </>
          ) : (
            <>
              <span className="font-semibold text-[#374049]">
                {genusCount.toLocaleString()} genus cards
              </span>
              :{" "}
              {items.map((item, index) => (
                <React.Fragment key={`${item.id}_name`}>
                  <span className="font-semibold italic text-[#374049]">
                    {item.genusName || "Unknown genus"}
                  </span>

                  {index < genusCount - 2
                    ? ", "
                    : index === genusCount - 2
                      ? genusCount === 2
                        ? " and "
                        : ", and "
                      : ""}
                </React.Fragment>
              ))}
            </>
          )}
          .
        </div>

        {timestampValue && (
          <div className="inline-flex shrink-0 items-center gap-2 text-[10px] text-slate-400">
            <span aria-hidden="true">•</span>
            {formatRelativeTime(timestampValue)}
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {items.map((item) =>
          onSelect ? (
            <button
              key={`${item.id}_image`}
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onSelect(item);
              }}
              onKeyDown={(event) => event.stopPropagation()}
              aria-label={`View ${item.genusName || "genus"} card`}
              className="flex h-14 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md border border-[#374049]/30 bg-white p-0.5 shadow-[0_4px_10px_rgba(55,64,73,0.11)] transition hover:border-[#24C3BE]/60 focus:outline-none focus:ring-2 focus:ring-[#24C3BE]/30 sm:h-16 sm:w-14 sm:p-1"
            >
              <img
                src={getGenusImage(item.genusName)}
                alt={item.genusName || ""}
                onError={(event) => (event.currentTarget.src = defaultImg)}
                className="max-h-full max-w-full object-contain"
              />
            </button>
          ) : (
            <div
              key={`${item.id}_image`}
              className="flex h-14 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md border border-[#374049]/30 bg-white p-0.5 shadow-[0_4px_10px_rgba(55,64,73,0.11)] sm:h-16 sm:w-14 sm:p-1"
            >
              <img
                src={getGenusImage(item.genusName)}
                alt={item.genusName || ""}
                onError={(event) => (event.currentTarget.src = defaultImg)}
                className="max-h-full max-w-full object-contain"
              />
            </div>
          ),
        )}
      </div>
    </>
  );
}

function CommunityKudosButton({
  recipientUid,
  activityKey,
  sourceActivityId,
  activityType,
}) {
  const currentUid = auth.currentUser?.uid || "";
  const [senderUids, setSenderUids] = useState([]);
  const [pendingSelection, setPendingSelection] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [kudosError, setKudosError] = useState("");

  useEffect(() => {
    setSenderUids([]);
    setPendingSelection(null);
    setKudosError("");

    if (!currentUid || !recipientUid || !activityKey) return undefined;

    const aggregateRef = doc(
      db,
      "users",
      recipientUid,
      "communityKudos",
      activityKey,
    );

    const unsubscribe = onSnapshot(
      aggregateRef,
      (snapshot) => {
        const nextSenderUids = snapshot.exists()
          ? Array.from(
              new Set(
                (Array.isArray(snapshot.data().senderUids)
                  ? snapshot.data().senderUids
                  : []
                )
                  .map((senderUid) => String(senderUid || "").trim())
                  .filter(Boolean),
              ),
            )
          : [];

        setSenderUids(nextSenderUids);
        setPendingSelection((currentSelection) => {
          if (currentSelection === null) return null;

          return nextSenderUids.includes(currentUid) === currentSelection
            ? null
            : currentSelection;
        });
        setKudosError("");
      },
      (error) => {
        console.error("Failed to load Community Feed kudos:", error);
        setKudosError("Sending kudos is temporarily unavailable.");
      },
    );

    return () => unsubscribe();
  }, [activityKey, currentUid, recipientUid]);

  if (
    !currentUid ||
    !recipientUid ||
    currentUid === recipientUid ||
    !activityKey ||
    !sourceActivityId
  ) {
    return null;
  }

  const serverHasSentKudos = senderUids.includes(currentUid);
  const hasSentKudos = pendingSelection ?? serverHasSentKudos;

  const displayedSenderCount = Math.max(
    0,
    senderUids.length +
      (pendingSelection === true && !serverHasSentKudos
        ? 1
        : pendingSelection === false && serverHasSentKudos
          ? -1
          : 0),
  );

  async function toggleKudos(event) {
    event.preventDefault();
    event.stopPropagation();

    if (submitting) return;

    const nextSelection = !hasSentKudos;
    const senderRef = doc(
      db,
      "users",
      recipientUid,
      "communityKudos",
      activityKey,
      "senders",
      currentUid,
    );

    setPendingSelection(nextSelection);
    setSubmitting(true);
    setKudosError("");

    try {
      if (nextSelection) {
        await setDoc(senderRef, {
          senderUid: currentUid,
          recipientUid,
          sourceActivityId,
          activityType,
          createdAt: serverTimestamp(),
        });
      } else {
        await deleteDoc(senderRef);
      }
    } catch (error) {
      console.error("Failed to update Community Feed kudos:", error);
      setPendingSelection(null);
      setKudosError("Kudos could not be updated.");
    } finally {
      setSubmitting(false);
    }
  }

  const peopleLabel = `${displayedSenderCount.toLocaleString()} ${
    displayedSenderCount === 1 ? "person has" : "people have"
  } sent kudos`;

  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
      <button
        type="button"
        onClick={toggleKudos}
        onKeyDown={(event) => event.stopPropagation()}
        disabled={submitting}
        aria-pressed={hasSentKudos}
        aria-label={`${hasSentKudos ? "Remove" : "Send"} kudos. ${peopleLabel}.`}
        title={displayedSenderCount > 0 ? peopleLabel : "Send kudos"}
        className={`inline-flex items-center gap-1.5 text-[11px] font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#24C3BE]/30 disabled:cursor-wait disabled:opacity-60 ${
          hasSentKudos
            ? "text-[#374049]"
            : "text-slate-500 hover:text-[#374049]"
        }`}
      >
        <span
          aria-hidden="true"
          className={`text-sm leading-none ${
            hasSentKudos ? "text-[#24C3BE]" : "text-[#374049]/55"
          }`}
        >
          ✦
        </span>

        <span>{hasSentKudos ? "Kudos sent" : "Send kudos"}</span>

        {displayedSenderCount > 0 && (
          <span className="font-medium text-slate-400">
            · {displayedSenderCount.toLocaleString()}
          </span>
        )}
      </button>

      {kudosError && (
        <span className="text-[10px] text-red-600">{kudosError}</span>
      )}
    </div>
  );
}

function KudosNotificationSummary({ message, updatedAt }) {
  if (!message) return null;

  return (
    <div className="mt-3 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-t border-[#93CA87]/20 pt-2.5">
      <div className="inline-flex items-baseline gap-1.5 text-xs font-medium leading-relaxed text-[#374049]">
        <span aria-hidden="true" className="text-sm text-[#24C3BE]">
          ✦
        </span>
        <span>{message}</span>
      </div>

      {updatedAt && (
        <div className="inline-flex shrink-0 items-center gap-2 text-[10px] text-slate-400">
          <span aria-hidden="true">•</span>
          {formatRelativeTime(updatedAt)}
        </div>
      )}
    </div>
  );
}

function CommunityGenusActivityCard({
  activities,
  onOpenGenus,
  onRequestProfile,
}) {
  const primaryActivity = activities[0] || {};
  const actorName = primaryActivity.actorName || "BioIndex User";
  const actorInitial = String(actorName).trim().charAt(0).toUpperCase() || "B";

  function requestProfile() {
    if (!primaryActivity.actorUid || !onRequestProfile) return;
    onRequestProfile(primaryActivity);
  }

  return (
    <article
      onClick={requestProfile}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;

        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          requestProfile();
        }
      }}
      role={primaryActivity.actorUid ? "button" : undefined}
      tabIndex={primaryActivity.actorUid ? 0 : undefined}
      className={`block w-full overflow-hidden rounded-2xl border border-[#93CA87]/35 bg-white p-4 text-left shadow-sm transition hover:-translate-y-[1px] hover:shadow-md sm:p-5 ${
        primaryActivity.actorUid
          ? "cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#01767A]/20"
          : ""
      }`}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            requestProfile();
          }}
          onKeyDown={(event) => event.stopPropagation()}
          disabled={!primaryActivity.actorUid}
          aria-label={`Visit ${actorName}'s profile`}
          className="shrink-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#24C3BE]/35 disabled:cursor-default sm:rounded-2xl"
        >
          {primaryActivity.actorPhotoURL ? (
            <img
              src={primaryActivity.actorPhotoURL}
              alt=""
              className="h-12 w-12 rounded-xl border border-[#93CA87]/40 object-cover shadow-sm transition hover:border-[#24C3BE]/70 sm:h-16 sm:w-16 sm:rounded-2xl"
            />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#374049] text-sm font-semibold text-white shadow-sm sm:h-16 sm:w-16 sm:rounded-2xl">
              {actorInitial}
            </div>
          )}
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                requestProfile();
              }}
              onKeyDown={(event) => event.stopPropagation()}
              disabled={!primaryActivity.actorUid}
              className="min-w-0 text-left text-sm font-semibold leading-snug text-[#374049] transition hover:text-[#01767A] focus:outline-none focus:underline disabled:cursor-default sm:text-base"
            >
              {actorName}
            </button>

            <div className="inline-flex shrink-0 items-center gap-2 text-[10px] text-slate-400">
              <span aria-hidden="true">•</span>
              {formatRelativeTime(
                primaryActivity.createdAt || primaryActivity.updatedAt,
              )}
            </div>
          </div>

          <div className="mt-0.5">
            <GenusUnlockSummary
              items={activities}
              leadingText="Unlocked"
              onSelect={onOpenGenus}
            />

            <CommunityKudosButton
              recipientUid={primaryActivity.actorUid}
              activityKey={`${primaryActivity.actorUid}_genus_${getDatedItemDayKey(
                primaryActivity,
              )}`}
              sourceActivityId={primaryActivity.id}
              activityType="genus_unlocked"
            />
          </div>
        </div>
      </div>
    </article>
  );
}

function CommunityDailyActivityCard({
  activities,
  onOpenUploads,
  onOpenPhoto,
  onOpenGenus,
  onRequestProfile,
}) {
  const latest = activities.reduce(
    (best, entry) =>
      !best || getCommunityActivityTime(entry) > getCommunityActivityTime(best)
        ? entry
        : best,
    null,
  );
  const photos = activities.find((entry) => entry.type === "photos_uploaded");
  const genera = activities.filter((entry) => entry.type === "genus_unlocked");
  const titles = activities.filter((entry) => entry.type === "title_unlocked");
  const actorName = latest?.actorName || "BioIndex User";
  const actorUid = latest?.actorUid;
  const dateKey = getDatedItemDayKey(latest);
  const photoCount = Number(
    photos?.uploadedPhotoCount || photos?.photoRefs?.length || 0,
  );
  const photoRefs = Array.isArray(photos?.photoRefs)
    ? photos.photoRefs.filter((ref) => ref?.formUrl)
    : [];

  // Preserve existing one-per-accomplishment kudos aggregation and its Firestore IDs.
  const kudosTarget = photos || genera[0] || titles[0];
  const kudosKey = photos
    ? photos.id
    : genera.length
      ? activities.length === 1
        ? genera[0].id
        : `${actorUid}_genus_${dateKey}`
      : kudosTarget?.id;

  return (
    <article className="bioindex-m-friends-card w-full overflow-hidden rounded-[1.35rem] border border-[#374049]/15 bg-white px-4 py-5 shadow-[0_6px_24px_rgba(55,64,73,0.045)] sm:px-7 sm:py-7">
      <header className="flex flex-wrap items-center gap-3 border-b border-[#374049]/10 pb-4">
        <button
          type="button"
          onClick={() => onRequestProfile?.(latest)}
          disabled={!actorUid}
          aria-label={`Visit ${actorName}'s profile`}
          className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[#374049] text-sm font-semibold text-white transition hover:ring-2 hover:ring-[#24C3BE]/40 disabled:cursor-default"
        >
          {latest?.actorPhotoURL ? (
            <img
              src={latest.actorPhotoURL}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            actorName.trim().charAt(0).toUpperCase() || "B"
          )}
        </button>
        <div className="min-w-0 flex-1">
          <h3
            className="text-xl font-semibold leading-tight text-[#374049] sm:text-[22px]"
            style={{ fontFamily: "Cormorant Garamond" }}
          >
            <span className="bioindex-m-desktop-card-title">
              {formatAccomplishmentHeading(dateKey)}
            </span>
            <span className="bioindex-m-inline-date">
              {formatCommunityActivityDate(dateKey)}
            </span>
          </h3>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-[#374049]/60">
            <button
              type="button"
              onClick={() => onRequestProfile?.(latest)}
              disabled={!actorUid}
              className="font-semibold text-[#374049]/80 hover:text-[#01767A] hover:underline disabled:cursor-default"
            >
              {actorName}
            </button>
            <span aria-hidden="true">·</span>
            <span>
              {formatRelativeTime(latest?.updatedAt || latest?.createdAt)}
            </span>
          </div>
        </div>
      </header>

      <div className="space-y-4 pt-4">
        {photos && (
          <section
            className="bioindex-m-photo-row bioindex-m-friends-photo-section gap-x-3 gap-y-3"
            style={{
              display: "grid",
              gridTemplateColumns: "max-content minmax(0, 1fr)",
            }}
          >
            <PersonalActivityIcon type="photos" />
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-5 text-[#374049]">
                Photos uploaded
              </p>
              <p className="mt-0.5 text-xs leading-5 text-[#374049]/60">
                {photoCount.toLocaleString()}{" "}
                {photoCount === 1 ? "photo" : "photos"}
                {photos.uploadedSpeciesCount
                  ? ` of ${Number(photos.uploadedSpeciesCount).toLocaleString()} species`
                  : ""}
              </p>
            </div>
            {photoRefs.length > 0 && (
              <div
                className="bioindex-m-friends-photo-strip col-span-2 min-w-0 w-full"
                style={{ gridColumn: "1 / -1", marginLeft: 0, width: "100%" }}
              >
                <CommunityPhotoPreviewStrip
                  photoRefs={photoRefs}
                  uploadedPhotoCount={photoCount}
                  onSelectPhoto={(photoRef) => onOpenPhoto?.(photos, photoRef)}
                  onShowAll={() => onOpenUploads?.(photos)}
                  flushTop
                />
              </div>
            )}
          </section>
        )}

        {genera.length > 0 && (
          <section
            className="bioindex-m-genus-row gap-x-3 gap-y-3"
            style={{
              display: "grid",
              gridTemplateColumns: "max-content minmax(0, 1fr)",
            }}
          >
            <PersonalActivityIcon type="genus" />
            <div className="min-w-0 self-center">
              <p className="text-sm font-semibold leading-5 text-[#374049]">
                {genera.length === 1
                  ? "Genus card unlocked"
                  : `${genera.length} genus cards unlocked`}
              </p>
            </div>
            <div
              className="col-span-2 mt-0 flex flex-wrap items-center gap-3 sm:gap-4"
              style={{ gridColumn: "1 / -1" }}
            >
              {genera.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  onClick={() => onOpenGenus?.(entry)}
                  aria-label={`View ${entry.genusName || "genus"} card`}
                  className="group relative inline-flex h-[76px] w-[68px] shrink-0 items-center justify-center rounded-lg border border-[#374049]/20 bg-white p-1.5 shadow-[0_2px_8px_rgba(55,64,73,0.08)] transition duration-150 hover:-translate-y-0.5 hover:border-[#01767A]/60 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#24C3BE]/50 sm:h-[88px] sm:w-[78px]"
                >
                  <img
                    src={getGenusImage(entry.genusName)}
                    alt=""
                    onError={(event) => {
                      event.currentTarget.src = defaultImg;
                    }}
                    className="max-h-full max-w-full object-contain"
                  />
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 max-w-[180px] -translate-x-1/2 whitespace-nowrap rounded-md bg-[#374049] px-2.5 py-1.5 text-xs font-medium italic text-white opacity-0 shadow-lg transition-opacity duration-100 group-hover:opacity-100 group-focus-visible:opacity-100"
                  >
                    {entry.genusName || "Unknown genus"}
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}

        {titles.length > 0 && (
          <section className="bioindex-m-titles-row flex items-start gap-3">
            <PersonalActivityIcon type="titles" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold leading-5 text-[#374049]">
                {titles.length === 1
                  ? "Title earned"
                  : `${titles.length} titles earned`}
              </p>
              <ul className="mt-1.5 flex flex-wrap items-center gap-x-5 gap-y-1.5">
                {titles.map((entry) => (
                  <li
                    key={entry.id}
                    className="flex min-w-0 items-start gap-1.5 text-xs text-[#374049]/70"
                  >
                    <span
                      className="mt-0.5 text-[#374049]/30"
                      aria-hidden="true"
                    >
                      •
                    </span>
                    <span className="font-medium text-[#374049]/80">
                      {getCurrentTitleDisplay(entry).label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}
      </div>

      {kudosTarget && (
        <footer className="bioindex-m-kudos-footer mt-4 flex items-start gap-3 border-t border-[#374049]/10 pt-3.5">
          <PersonalActivityIcon type="kudos" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold leading-5 text-[#374049]">
              Appreciate this activity
            </p>
            <CommunityKudosButton
              recipientUid={actorUid}
              activityKey={kudosKey}
              sourceActivityId={kudosTarget.id}
              activityType={kudosTarget.type}
            />
          </div>
        </footer>
      )}
    </article>
  );
}

function PersonalDailyActivityCard({
  item,
  onRead,
  onOpenUploads,
  onOpenPhoto,
  onGenus,
  onTitle,
}) {
  const entries = item.notifications;
  const unread = entries.filter((entry) => !entry.read);
  const photos = entries.find((entry) => entry.type === "photos_uploaded");
  const genera = entries.filter((entry) => entry.type === "genus_unlocked");
  const titles = entries.filter((entry) => entry.type === "title_unlocked");
  const kudosEntries = entries.filter(
    (entry) =>
      entry.type === "kudos_received" ||
      entry.kudosMessage ||
      Number(entry.kudosCount || 0) > 0,
  );

  // Keep the existing sender aggregation: one friend appears once per day,
  // even if they sent kudos for multiple accomplishments.
  const senders = new Map();
  kudosEntries.forEach((entry) => {
    (entry.kudosSenderRecords || []).forEach((sender) => {
      if (sender?.uid)
        senders.set(sender.uid, sender.name || "A BioIndex friend");
    });
  });
  const latestKudos = kudosEntries.reduce(
    (latest, entry) =>
      !latest ||
      timestampToMillis(entry.kudosUpdatedAt || entry.createdAt) >
        timestampToMillis(latest.kudosUpdatedAt || latest.createdAt)
        ? entry
        : latest,
    null,
  );
  const legacyKudos =
    latestKudos?.kudosMessage ||
    (latestKudos?.type === "kudos_received" ? latestKudos.message : "");
  const senderNames = [...senders.values()];
  const photoCount = Number(
    photos?.uploadedPhotoCount || photos?.photoRefs?.length || 0,
  );
  const photoRefs = Array.isArray(photos?.photoRefs)
    ? photos.photoRefs.filter((ref) => ref?.formUrl)
    : [];
  const hasAchievements = Boolean(photos || genera.length || titles.length);
  const readCard = () => {
    if (unread.length) onRead(unread);
  };
  const heading = formatAccomplishmentHeading(
    item.day,
    hasAchievements ? "Accomplishments" : "Kudos",
  );

  return (
    <article
      className={`bioindex-m-personal-card w-full overflow-hidden rounded-[1.35rem] border bg-white px-4 py-5 shadow-[0_6px_24px_rgba(55,64,73,0.045)] sm:px-7 sm:py-7 ${
        unread.length ? "border-[#374049]/25" : "border-[#374049]/15"
      }`}
    >
      <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 border-b border-[#374049]/10 pb-3">
        <div className="flex min-w-0 items-center gap-2.5">
          {unread.length > 0 && (
            <span
              className="h-2 w-2 shrink-0 rounded-full bg-[#01767A]"
              role="img"
              aria-label="Unread activity"
              title="Unread activity"
            />
          )}
          <h3
            className="bioindex-m-desktop-card-title text-xl font-semibold leading-tight text-[#374049] sm:text-[22px]"
            style={{ fontFamily: "Cormorant Garamond" }}
          >
            {heading}
          </h3>
          <h3 className="bioindex-m-date-heading">
            <span className="bioindex-m-calendar" aria-hidden="true">
              ▦
            </span>
            {formatCommunityActivityDate(item.day)}
          </h3>
        </div>
        {unread.length > 0 && (
          <button
            type="button"
            onClick={readCard}
            className="rounded-md px-1 py-1 text-xs font-medium text-[#374049]/60 transition hover:text-[#374049] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#24C3BE]/40"
          >
            <span className="bioindex-m-desktop-label">Mark as read</span>
            <span className="bioindex-m-short-label">Read</span>
          </button>
        )}
      </header>

      {hasAchievements && (
        <div className="space-y-4 pt-4">
          {photos && (
            <section
              className="bioindex-m-personal-photos items-start gap-x-3 gap-y-3"
              style={{
                display: "grid",
                gridTemplateColumns: "max-content minmax(0, 1fr)",
              }}
            >
              <PersonalActivityIcon type="photos" />
              <div className="min-w-0">
                <p className="text-sm font-semibold leading-5 text-[#374049]">
                  Photos uploaded
                </p>
                <p className="mt-0.5 text-xs leading-5 text-[#374049]/60">
                  {photoCount.toLocaleString()}{" "}
                  {photoCount === 1 ? "photo" : "photos"}
                  {photos.uploadedSpeciesCount
                    ? ` of ${Number(photos.uploadedSpeciesCount).toLocaleString()} species`
                    : ""}
                </p>
              </div>
              {photoRefs.length > 0 && (
                <div
                  className="col-span-2 min-w-0 w-full"
                  style={{ gridColumn: "1 / -1", marginLeft: 0, width: "100%" }}
                >
                  <CommunityPhotoPreviewStrip
                    photoRefs={photoRefs}
                    uploadedPhotoCount={photoCount}
                    onSelectPhoto={(photoRef) => {
                      readCard();
                      onOpenPhoto(photos, photoRef);
                    }}
                    onShowAll={() => {
                      readCard();
                      onOpenUploads(photos);
                    }}
                    flushTop
                  />
                </div>
              )}
            </section>
          )}

          {genera.length > 0 && (
            <section
              className="bioindex-m-genus-row items-start gap-x-3 gap-y-3"
              style={{
                display: "grid",
                gridTemplateColumns: "max-content minmax(0, 1fr)",
              }}
            >
              <PersonalActivityIcon type="genus" />
              <div className="min-w-0 self-center">
                <p className="text-sm font-semibold leading-5 text-[#374049]">
                  {genera.length === 1
                    ? "Genus card unlocked"
                    : `${genera.length} genus cards unlocked`}
                </p>
              </div>
              <div
                className="col-span-2 min-w-0 flex flex-wrap items-center gap-3 sm:gap-4"
                style={{ gridColumn: "1 / -1" }}
              >
                {genera.map((entry) => (
                  <button
                    type="button"
                    key={entry.id}
                    onClick={() => {
                      readCard();
                      onGenus(entry);
                    }}
                    className="group relative inline-flex h-[76px] w-[68px] shrink-0 items-center justify-center rounded-lg border border-[#374049]/20 bg-white p-1.5 shadow-[0_2px_8px_rgba(55,64,73,0.08)] transition duration-150 hover:-translate-y-0.5 hover:border-[#01767A]/60 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#24C3BE]/50 sm:h-[88px] sm:w-[78px]"
                    aria-label={`View ${entry.genusName || "genus"} card`}
                  >
                    <img
                      src={getGenusImage(entry.genusName)}
                      alt=""
                      onError={(event) => {
                        event.currentTarget.src = defaultImg;
                      }}
                      className="max-h-full max-w-full object-contain"
                    />
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 max-w-[180px] -translate-x-1/2 whitespace-nowrap rounded-md bg-[#374049] px-2.5 py-1.5 text-xs font-medium italic text-white opacity-0 shadow-lg transition-opacity duration-100 group-hover:opacity-100 group-focus-visible:opacity-100"
                    >
                      {entry.genusName || "Unknown genus"}
                    </span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {titles.length > 0 && (
            <section className="bioindex-m-titles-row flex items-start gap-3">
              <PersonalActivityIcon type="titles" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold leading-5 text-[#374049]">
                  {titles.length === 1
                    ? "Title earned"
                    : `${titles.length} titles earned`}
                </p>
                <ul className="mt-1.5 flex flex-wrap items-center gap-x-5 gap-y-1.5">
                  {titles.map((entry) => (
                    <li
                      key={entry.id}
                      className="flex min-w-0 items-start gap-1.5 text-xs text-[#374049]/70"
                    >
                      <span
                        className="mt-0.5 text-[#374049]/30"
                        aria-hidden="true"
                      >
                        •
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          readCard();
                          onTitle(entry);
                        }}
                        className="text-left font-medium text-[#374049]/80 underline-offset-4 transition hover:text-[#01767A] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#24C3BE]/40"
                      >
                        {getCurrentTitleDisplay(entry).label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}
        </div>
      )}

      {latestKudos && (
        <footer className="bioindex-m-kudos-footer mt-4 flex items-start gap-3 border-t border-[#374049]/10 pt-3.5">
          <PersonalActivityIcon type="kudos" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold leading-5 text-[#374049]">
              {senderNames.length > 0
                ? `Kudos from ${senderNames.slice(0, 2).join(senderNames.length === 2 ? " and " : ", ")}${senderNames.length > 2 ? ` and ${senderNames.length - 2} other${senderNames.length === 3 ? "" : "s"}` : ""}`
                : "Kudos received"}
            </p>
            <p className="mt-0.5 text-xs leading-5 text-[#374049]/60">
              {senderNames.length > 0
                ? `${senderNames.length} ${senderNames.length === 1 ? "person" : "people"} appreciated your accomplishments`
                : legacyKudos || "Someone appreciated your accomplishments"}
              {latestKudos.kudosUpdatedAt
                ? ` · ${formatRelativeTime(latestKudos.kudosUpdatedAt)}`
                : ""}
            </p>
          </div>
        </footer>
      )}
    </article>
  );
}

function CombinedPersonalNotification({ item, onRead, onRankings }) {
  const unread = item.notifications.filter(
    (notification) => !notification.read,
  );
  return (
    <div
      className={`rounded-2xl border bg-white p-4 shadow-[0_2px_12px_rgba(55,64,73,0.045)] sm:p-5 ${
        unread.length ? "border-[#374049]/25" : "border-[#374049]/15"
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-[#374049]">
            {unread.length > 0 && (
              <span
                className="h-2 w-2 rounded-full bg-[#01767A]"
                aria-label="Unread ranking changes"
              />
            )}
            Ranking updates
          </h3>
          <p className="mt-1 text-xs text-slate-500">
            {item.notifications.length} ranking{" "}
            {item.notifications.length === 1 ? "change" : "changes"} on this day
          </p>
        </div>
        {unread.length > 0 && (
          <button
            type="button"
            onClick={() => onRead(item.notifications)}
            className="rounded-md px-1 py-1 text-xs font-medium text-[#374049]/60 hover:text-[#374049]"
          >
            Mark as read
          </button>
        )}
      </div>
      <div className="mt-3 space-y-1.5 text-xs text-[#374049]">
        {item.notifications.map((entry) => (
          <p key={entry.id}>{entry.message}</p>
        ))}
        <button
          type="button"
          onClick={() => {
            onRead(item.notifications);
            onRankings();
          }}
          className="mt-2 font-semibold text-[#01767A] hover:underline"
        >
          View rankings
        </button>
      </div>
    </div>
  );
}

function GenusNotificationGroup({
  notifications,
  className,
  onOpenNotification,
}) {
  const hasUnreadNotification = notifications.some(
    (notification) => !notification.read,
  );

  const kudosNotification = notifications.find(
    (notification) => notification.kudosMessage,
  );

  return (
    <div className={className}>
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <GenusUnlockSummary
            items={notifications}
            leadingText="You unlocked"
            timestampValue={
              notifications[0]?.updatedAt || notifications[0]?.createdAt
            }
            onSelect={onOpenNotification}
          />

          <KudosNotificationSummary
            message={kudosNotification?.kudosMessage}
            updatedAt={kudosNotification?.kudosUpdatedAt}
          />
        </div>

        {hasUnreadNotification && (
          <div
            className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full bg-[#01767A] shadow-[0_0_0_3px_rgba(1,118,122,0.10)]"
            aria-label="Unread"
          />
        )}
      </div>
    </div>
  );
}

function CommunityActivityCard({
  activity,
  onOpenUploads,
  onOpenPhoto,
  onOpenGenus,
  onRequestProfile,
}) {
  const actorName = activity.actorName || "BioIndex User";

  const actorInitial = String(actorName).trim().charAt(0).toUpperCase() || "B";

  const isGenusUnlock = activity.type === "genus_unlocked";
  const isTitleUnlock = activity.type === "title_unlocked";
  const isDailyUploadSummary = activity.type === "photos_uploaded";
  const currentTitleDisplay = getCurrentTitleDisplay(activity);

  const uploadedPhotoCount = Number(activity.uploadedPhotoCount || 0);
  const uploadedSpeciesCount = Number(activity.uploadedSpeciesCount || 0);
  const photoRefs = Array.isArray(activity.photoRefs)
    ? activity.photoRefs.filter((photoRef) => photoRef?.formUrl)
    : [];

  const uploadLocations = Array.from(
    new Set(
      [
        ...(Array.isArray(activity.uploadLocations)
          ? activity.uploadLocations
          : []),
        ...photoRefs.map((photoRef) => photoRef.locationName),
      ]
        .map((locationName) => String(locationName || "").trim())
        .filter(Boolean),
    ),
  );

  function requestProfile() {
    if (!activity.actorUid || !onRequestProfile) return;
    onRequestProfile(activity);
  }

  function openActivity() {
    if (isDailyUploadSummary && photoRefs.length > 0 && onOpenUploads) {
      onOpenUploads(activity);
      return;
    }

    requestProfile();
  }

  const activityIsClickable =
    (isDailyUploadSummary && photoRefs.length > 0 && onOpenUploads) ||
    (activity.actorUid && onRequestProfile);

  return (
    <article
      onClick={openActivity}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;

        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openActivity();
        }
      }}
      role={activityIsClickable ? "button" : undefined}
      tabIndex={activityIsClickable ? 0 : undefined}
      className={`block w-full overflow-hidden rounded-2xl border border-[#93CA87]/35 bg-white p-4 text-left shadow-sm transition hover:-translate-y-[1px] hover:shadow-md sm:p-5 ${
        activityIsClickable
          ? "cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#01767A]/20"
          : ""
      }`}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            requestProfile();
          }}
          onKeyDown={(event) => event.stopPropagation()}
          disabled={!activity.actorUid}
          aria-label={`Visit ${actorName}'s profile`}
          className="shrink-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#24C3BE]/35 disabled:cursor-default sm:rounded-2xl"
        >
          {activity.actorPhotoURL ? (
            <img
              src={activity.actorPhotoURL}
              alt=""
              className="h-12 w-12 rounded-xl border border-[#93CA87]/40 object-cover shadow-sm transition hover:border-[#24C3BE]/70 sm:h-16 sm:w-16 sm:rounded-2xl"
            />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#374049] text-sm font-semibold text-white shadow-sm sm:h-16 sm:w-16 sm:rounded-2xl">
              {actorInitial}
            </div>
          )}
        </button>

        <div className="min-w-0 flex-1">
          <div className="min-w-0">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  requestProfile();
                }}
                onKeyDown={(event) => event.stopPropagation()}
                disabled={!activity.actorUid}
                className="min-w-0 text-left text-sm font-semibold leading-snug text-[#374049] transition hover:text-[#01767A] focus:outline-none focus:underline disabled:cursor-default sm:text-base"
              >
                {actorName}
              </button>

              <div className="inline-flex shrink-0 items-center gap-2 text-[10px] text-slate-400">
                <span aria-hidden="true">•</span>
                {formatRelativeTime(activity.createdAt || activity.updatedAt)}
              </div>
            </div>

            <div className="mt-0.5 text-sm leading-relaxed text-slate-600">
              {isGenusUnlock && (
                <>
                  Unlocked the genus card{" "}
                  <span className="font-semibold italic text-[#374049]">
                    {activity.genusName}
                  </span>
                  .
                </>
              )}

              {isTitleUnlock && (
                <>
                  Unlocked the title{" "}
                  <span className="font-semibold text-[#374049]">
                    “{currentTitleDisplay.label}”
                  </span>
                  {currentTitleDisplay.description && (
                    <>
                      {" "}
                      by{" "}
                      {formatTitleUnlockCondition(
                        currentTitleDisplay.description,
                      )}
                    </>
                  )}
                  .
                </>
              )}

              {isDailyUploadSummary && (
                <>
                  Uploaded{" "}
                  <span className="font-semibold text-[#374049]">
                    {uploadedPhotoCount.toLocaleString()}{" "}
                    {uploadedPhotoCount === 1 ? "photo" : "photos"}
                  </span>
                  {uploadedSpeciesCount > 0 && (
                    <>
                      {" "}
                      of{" "}
                      <span className="font-semibold text-[#374049]">
                        {uploadedSpeciesCount.toLocaleString()} species
                      </span>
                    </>
                  )}
                  {uploadLocations.length === 1 && (
                    <>
                      {" "}
                      from{" "}
                      <span className="font-semibold text-[#374049]">
                        {uploadLocations[0]}
                      </span>
                    </>
                  )}
                  {uploadLocations.length > 1 && (
                    <>
                      {" "}
                      from{" "}
                      <span className="font-semibold text-[#374049]">
                        {uploadLocations.length.toLocaleString()} locations
                      </span>
                    </>
                  )}
                  .
                </>
              )}
            </div>
          </div>

          {isDailyUploadSummary && photoRefs.length > 0 && (
            <CommunityPhotoPreviewStrip
              photoRefs={photoRefs}
              uploadedPhotoCount={uploadedPhotoCount}
              onSelectPhoto={(photoRef) => onOpenPhoto?.(activity, photoRef)}
            />
          )}

          <CommunityKudosButton
            recipientUid={activity.actorUid}
            activityKey={activity.id}
            sourceActivityId={activity.id}
            activityType={activity.type}
          />
        </div>

        {isGenusUnlock ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onOpenGenus?.(activity);
            }}
            onKeyDown={(event) => event.stopPropagation()}
            aria-label={`View ${activity.genusName || "genus"} card`}
            className="flex h-14 w-12 shrink-0 self-center items-center justify-center overflow-hidden rounded-md border border-[#374049]/30 bg-white p-0.5 shadow-[0_4px_10px_rgba(55,64,73,0.11)] transition hover:border-[#24C3BE]/70 focus:outline-none focus:ring-2 focus:ring-[#24C3BE]/30 sm:h-16 sm:w-14 sm:p-1"
          >
            {" "}
            <img
              src={getGenusImage(activity.genusName)}
              alt={activity.genusName || ""}
              onError={(event) => (event.currentTarget.src = defaultImg)}
              className="max-h-full max-w-full object-contain"
            />
          </button>
        ) : isTitleUnlock ? (
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#93CA87]/50 bg-white text-xl shadow-sm">
            <span aria-hidden="true">👑</span>
          </div>
        ) : !isDailyUploadSummary || photoRefs.length === 0 ? (
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#93CA87]/50 bg-white text-lg text-[#24C3BE] shadow-sm">
            <span aria-hidden="true">▧</span>
          </div>
        ) : null}
      </div>
    </article>
  );
}

function InlineForumDiscussion({
  topic,
  currentUid,
  reportedContentKeys,
  onReportComment,
  blockedRelationshipUserIdSet,
}) {
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [commentsError, setCommentsError] = useState("");
  const [commentBody, setCommentBody] = useState("");
  const [commentSaving, setCommentSaving] = useState(false);
  const [commentError, setCommentError] = useState("");
  const [deletingCommentId, setDeletingCommentId] = useState("");

  const visibleComments = useMemo(
    () =>
      comments.filter(
        (comment) =>
          !comment.authorUid ||
          !blockedRelationshipUserIdSet.has(comment.authorUid),
      ),
    [comments, blockedRelationshipUserIdSet],
  );

  useEffect(() => {
    if (!topic?.id) return undefined;

    setCommentsLoading(true);
    setCommentsError("");

    const commentsQuery = query(
      collection(db, "forumTopics", topic.id, "comments"),
      orderBy("createdAt", "asc"),
    );

    const unsubscribe = onSnapshot(
      commentsQuery,
      (snapshot) => {
        setComments(
          snapshot.docs.map((commentDocument) => ({
            id: commentDocument.id,
            ...commentDocument.data(),
          })),
        );

        setCommentsLoading(false);
        setCommentsError("");
      },
      (error) => {
        console.error("Failed to load inline forum comments:", error);
        setCommentsLoading(false);
        setCommentsError("The discussion could not be loaded.");
      },
    );

    return () => unsubscribe();
  }, [topic?.id]);

  async function submitComment() {
    if (commentSaving || !topic?.id) return;

    const user = auth.currentUser;
    const body = commentBody.trim();

    setCommentError("");

    if (!user) {
      setCommentError("You must be logged in to comment.");
      return;
    }

    if (!body) {
      setCommentError("Please write a comment.");
      return;
    }

    if (topic.authorUid && blockedRelationshipUserIdSet.has(topic.authorUid)) {
      setCommentError("Interaction with this user is unavailable.");
      return;
    }

    if (body.length > 5000) {
      setCommentError("Comments cannot exceed 5,000 characters.");
      return;
    }

    setCommentSaving(true);

    try {
      const profileSnapshot = await getDoc(doc(db, "profile", user.uid));
      const profileData = profileSnapshot.exists()
        ? profileSnapshot.data()
        : {};

      const authorName =
        String(
          profileData.name ||
            user.displayName ||
            user.email?.split("@")[0] ||
            "BioIndex User",
        ).trim() || "BioIndex User";

      const authorPhotoURL = String(profileData.photoURL || "").trim();

      const topicRef = doc(db, "forumTopics", topic.id);
      const commentRef = doc(
        collection(db, "forumTopics", topic.id, "comments"),
      );

      await runTransaction(db, async (transaction) => {
        const topicSnapshot = await transaction.get(topicRef);

        if (!topicSnapshot.exists()) {
          throw new Error("Topic not found.");
        }

        const topicData = topicSnapshot.data();

        if (topicData.deleted || topicData.hidden || topicData.locked) {
          throw new Error("Topic unavailable.");
        }

        transaction.set(commentRef, {
          body,

          authorUid: user.uid,
          authorName,
          authorPhotoURL,

          parentCommentId: null,

          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          editedAt: null,

          hidden: false,
          deleted: false,
        });

        transaction.update(topicRef, {
          commentCount: Number(topicData.commentCount || 0) + 1,
          lastActivityAt: serverTimestamp(),
        });
      });

      if (topic.authorUid && topic.authorUid !== user.uid) {
        try {
          await addDoc(
            collection(db, "users", topic.authorUid, "notifications"),
            {
              type: "topic_comment",

              message: `${authorName} commented on your topic`,
              detail: topic.title || "Forum discussion",
              targetRoute: `/forum/topic/${topic.id}`,
              read: false,

              actorUid: user.uid,
              actorName: authorName,
              actorPhotoURL: authorPhotoURL,

              topicId: topic.id,
              commentId: commentRef.id,
              parentCommentId: "",

              titleId: "",
              genusName: "",

              createdAt: serverTimestamp(),
              dedupeKey: `topic_comment_${topic.id}_${commentRef.id}`,
            },
          );
        } catch (notificationError) {
          console.error(
            "Comment published, but its notification could not be created:",
            notificationError,
          );
        }
      }

      setCommentBody("");
      setCommentError("");
    } catch (error) {
      console.error("Failed to publish inline forum comment:", error);
      setCommentError("Your comment could not be published.");
    } finally {
      setCommentSaving(false);
    }
  }

  async function deleteOwnComment(comment) {
    if (
      !comment?.id ||
      deletingCommentId ||
      !currentUid ||
      currentUid !== comment.authorUid
    ) {
      return;
    }

    const confirmed = window.confirm(
      comment.parentCommentId
        ? "Delete this reply? Its place in the discussion will be preserved."
        : "Delete this comment? Any replies beneath it will remain visible.",
    );

    if (!confirmed) return;

    setDeletingCommentId(comment.id);

    try {
      await updateDoc(
        doc(db, "forumTopics", topic.id, "comments", comment.id),
        {
          deleted: true,
          updatedAt: serverTimestamp(),
        },
      );
    } catch (error) {
      console.error("Failed to delete inline forum comment:", error);
      window.alert("The comment could not be deleted.");
    } finally {
      setDeletingCommentId("");
    }
  }

  return (
    <section className="border-t border-[#93CA87]/35 bg-white">
      <div className="border-b border-[#93CA87]/30 px-4 py-4 sm:px-5">
        <h4
          className="text-2xl leading-none text-[#374049]"
          style={{ fontFamily: "Cormorant Garamond" }}
        >
          Discussion
        </h4>

        <p className="mt-1 text-xs text-slate-500">
          {visibleComments.length}{" "}
          {visibleComments.length === 1 ? "reply" : "replies"}
        </p>
      </div>

      {commentsError && (
        <div className="mx-4 mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 sm:mx-5">
          {commentsError}
        </div>
      )}

      {commentsLoading ? (
        <div className="flex min-h-28 items-center justify-center">
          <div className="h-7 w-7 animate-spin rounded-full border-4 border-[#93CA87]/40 border-t-[#01767A]" />
        </div>
      ) : visibleComments.length > 0 ? (
        <div className="divide-y divide-[#93CA87]/25">
          {visibleComments.map((comment) => {
            const authorInitial =
              String(comment.authorName || "B")
                .trim()
                .charAt(0)
                .toUpperCase() || "B";

            return (
              <article
                key={comment.id}
                className={`px-4 py-4 sm:px-5 ${
                  comment.parentCommentId
                    ? "ml-5 border-l-2 border-[#93CA87]/40 bg-[#FDFBF8] sm:ml-10"
                    : "bg-white"
                }`}
              >
                <div className="flex items-start gap-3">
                  {comment.authorPhotoURL ? (
                    <img
                      src={comment.authorPhotoURL}
                      alt=""
                      className="h-8 w-8 shrink-0 rounded-full border border-[#93CA87]/40 object-cover"
                    />
                  ) : (
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#374049] text-[10px] font-semibold text-white">
                      {authorInitial}
                    </div>
                  )}

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="text-xs font-semibold text-[#374049]">
                        {comment.authorName || "BioIndex User"}
                      </span>

                      <span className="text-[10px] text-slate-400">
                        {formatRelativeTime(comment.createdAt)}
                      </span>
                    </div>

                    {comment.deleted ? (
                      <p className="mt-1 text-sm italic text-slate-400">
                        This comment was deleted.
                      </p>
                    ) : comment.hidden ? (
                      <p className="mt-1 text-sm italic text-slate-400">
                        This comment is unavailable.
                      </p>
                    ) : (
                      <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-600">
                        {comment.body}
                      </p>
                    )}

                    {!comment.deleted && !comment.hidden && currentUid && (
                      <div className="mt-2 flex flex-wrap items-center gap-3">
                        {currentUid === comment.authorUid ? (
                          <button
                            type="button"
                            onClick={() => deleteOwnComment(comment)}
                            disabled={deletingCommentId === comment.id}
                            className="text-[10px] font-semibold text-red-600 transition hover:text-red-700 disabled:cursor-wait disabled:opacity-60"
                          >
                            {deletingCommentId === comment.id
                              ? "Deleting..."
                              : "Delete"}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              onReportComment(
                                comment,
                                !!comment.parentCommentId,
                              )
                            }
                            disabled={
                              !!reportedContentKeys[`comment_${comment.id}`]
                            }
                            className="text-[10px] font-semibold text-[#374049]/55 transition hover:text-[#01767A] disabled:cursor-default disabled:text-[#01767A]"
                          >
                            {reportedContentKeys[`comment_${comment.id}`]
                              ? "Reported"
                              : "Report"}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="px-4 py-8 text-center sm:px-5">
          <p className="text-sm text-slate-500">
            No replies yet. Be the first person to join the discussion.
          </p>
        </div>
      )}

      {!topic.locked ? (
        <div className="border-t border-[#93CA87]/30 bg-[#FDFBF8] p-4 sm:p-5">
          <div className="mb-1 flex items-center justify-between gap-3">
            <label
              htmlFor={`inline-comment-${topic.id}`}
              className="text-xs font-semibold text-[#374049]"
            >
              Add a Comment
            </label>

            <span className="text-[10px] text-slate-400">
              {commentBody.length}/5000
            </span>
          </div>

          {commentError && (
            <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {commentError}
            </div>
          )}

          <textarea
            id={`inline-comment-${topic.id}`}
            value={commentBody}
            onChange={(event) => {
              setCommentBody(event.target.value.slice(0, 5000));
              setCommentError("");
            }}
            rows={4}
            maxLength={5000}
            disabled={commentSaving}
            placeholder="Write your comment..."
            className="w-full resize-y rounded-xl border border-[#93CA87]/40 bg-white px-4 py-3 text-sm leading-relaxed text-[#374049] outline-none transition placeholder:text-slate-400 focus:border-[#01767A]/70 focus:ring-2 focus:ring-[#01767A]/15 disabled:opacity-60"
          />

          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={submitComment}
              disabled={commentSaving}
              className={`rounded-xl px-5 py-2.5 text-sm font-semibold text-white transition ${
                commentSaving
                  ? "cursor-not-allowed bg-slate-400"
                  : "bg-[#01767A] hover:bg-[#374049]"
              }`}
            >
              {commentSaving ? "Publishing..." : "Publish Comment"}
            </button>
          </div>
        </div>
      ) : (
        <div className="border-t border-[#93CA87]/30 bg-[#FDFBF8] px-4 py-5 text-center text-sm text-slate-500 sm:px-5">
          This discussion is locked.
        </div>
      )}
    </section>
  );
}

function ForumTopicCard({
  topic,
  currentUid,
  blockedRelationshipUserIdSet,
  onTopicDeleted,
  onRequestProfile,
}) {
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  useEffect(() => {
    if (!photoModalOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setPhotoModalOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [photoModalOpen]);
  const [discussionOpen, setDiscussionOpen] = useState(false);
  const [topicDeleting, setTopicDeleting] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [reportedContentKeys, setReportedContentKeys] = useState({});

  const authorInitial =
    String(topic.authorName || "B")
      .trim()
      .charAt(0)
      .toUpperCase() || "B";

  function requestAuthorProfile() {
    if (!topic.authorUid || !onRequestProfile) return;

    onRequestProfile({
      uid: topic.authorUid,
      name: topic.authorName || "BioIndex User",
    });
  }

  function openTopicReport() {
    setReportTarget({
      contentType: "topic",
      topicId: topic.id,
      commentId: "",
      reportedUserUid: topic.authorUid || "",
      reportedUserName: topic.authorName || "BioIndex User",
      contentTitle: topic.title || "",
      contentBody: topic.body || "",
      targetLabel: "this topic",
    });
  }

  function openCommentReport(comment, isReply) {
    setReportTarget({
      contentType: "comment",
      topicId: topic.id,
      commentId: comment.id,
      reportedUserUid: comment.authorUid || "",
      reportedUserName: comment.authorName || "BioIndex User",
      contentTitle: topic.title || "",
      contentBody: comment.body || "",
      targetLabel: isReply ? "this reply" : "this comment",
    });
  }

  function markContentReported(reportKey) {
    setReportedContentKeys((currentKeys) => ({
      ...currentKeys,
      [reportKey]: true,
    }));
  }

  async function deleteOwnTopic() {
    if (
      topicDeleting ||
      !topic?.id ||
      !currentUid ||
      currentUid !== topic.authorUid
    ) {
      return;
    }

    const confirmed = window.confirm(
      "Permanently delete this topic and its entire discussion? All comments, replies, votes, notifications, and the uploaded topic image will also be deleted. This cannot be undone.",
    );

    if (!confirmed) return;

    setTopicDeleting(true);

    try {
      const commentsSnapshot = await getDocs(
        query(collection(db, "forumTopics", topic.id, "comments")),
      );
      const votesSnapshot = await getDocs(
        query(collection(db, "forumTopics", topic.id, "votes")),
      );

      const notificationRecipientUids = new Set(
        [topic.authorUid].filter(Boolean),
      );

      commentsSnapshot.forEach((commentDocument) => {
        const authorUid = commentDocument.data()?.authorUid;
        if (authorUid) notificationRecipientUids.add(authorUid);
      });

      const [commentVoteSnapshots, notificationSnapshots] = await Promise.all([
        Promise.all(
          commentsSnapshot.docs.map((commentDocument) =>
            getDocs(
              collection(
                db,
                "forumTopics",
                topic.id,
                "comments",
                commentDocument.id,
                "votes",
              ),
            ),
          ),
        ),
        Promise.all(
          [...notificationRecipientUids].map((recipientUid) =>
            getDocs(
              query(
                collection(db, "users", recipientUid, "notifications"),
                where("topicId", "==", topic.id),
              ),
            ),
          ),
        ),
      ]);

      const relatedDocumentDeletions = [];

      commentVoteSnapshots.forEach((voteSnapshot) => {
        voteSnapshot.forEach((voteDocument) => {
          relatedDocumentDeletions.push(deleteDoc(voteDocument.ref));
        });
      });

      commentsSnapshot.forEach((commentDocument) => {
        relatedDocumentDeletions.push(deleteDoc(commentDocument.ref));
      });

      votesSnapshot.forEach((voteDocument) => {
        relatedDocumentDeletions.push(deleteDoc(voteDocument.ref));
      });

      notificationSnapshots.forEach((notificationSnapshot) => {
        notificationSnapshot.forEach((notificationDocument) => {
          relatedDocumentDeletions.push(deleteDoc(notificationDocument.ref));
        });
      });

      await Promise.all(relatedDocumentDeletions);

      if (topic.imageStoragePath) {
        try {
          await deleteObject(ref(storage, topic.imageStoragePath));
        } catch (storageError) {
          console.error(
            "The discussion image could not be removed:",
            storageError,
          );
        }
      }

      await deleteDoc(doc(db, "forumTopics", topic.id));
      onTopicDeleted?.(topic.id);
    } catch (error) {
      console.error("Failed to permanently delete forum topic:", error);
      window.alert(
        "The topic and its associated discussion could not be completely deleted.",
      );
    } finally {
      setTopicDeleting(false);
    }
  }

  return (
    <article className="bioindex-m-topic-card h-full overflow-hidden rounded-2xl border border-[#374049]/10 bg-white shadow-[0_4px_18px_rgba(55,64,73,0.045)] transition duration-200 hover:-translate-y-0.5 hover:border-[#24C3BE]/55 hover:shadow-[0_10px_28px_rgba(1,118,122,0.09)]">
      <div className="p-4 sm:p-5">
        <div className="flex flex-col gap-4">
          <div className="min-w-0">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#374049]">
                {topic.category}
              </span>

              {topic.pinned && (
                <span className="rounded-full border border-[#93CA87]/50 bg-white px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[#374049]">
                  Pinned
                </span>
              )}
            </div>

            <Link
              to={`/forum/topic/${topic.id}`}
              className="bioindex-m-topic-title-link inline-block text-[1.75rem] leading-tight text-[#374049] transition hover:text-[#01767A] sm:text-3xl"
              style={{ fontFamily: "Cormorant Garamond" }}
            >
              {topic.title}
            </Link>

            <div className="mt-2 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-400">
              <button
                type="button"
                onClick={requestAuthorProfile}
                disabled={!topic.authorUid}
                className="flex min-w-0 items-center gap-2 rounded-full text-left transition hover:text-[#01767A] focus:outline-none focus:ring-2 focus:ring-[#24C3BE]/25 disabled:cursor-default"
                aria-label={`Visit ${topic.authorName || "BioIndex User"}'s profile`}
              >
                {topic.authorPhotoURL ? (
                  <img
                    src={topic.authorPhotoURL}
                    alt=""
                    className="h-6 w-6 shrink-0 rounded-full border border-[#93CA87]/40 object-cover"
                  />
                ) : (
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#374049] text-[9px] font-semibold text-white">
                    {authorInitial}
                  </span>
                )}

                <span className="max-w-48 truncate font-medium text-[#374049] transition hover:text-[#01767A]">
                  {topic.authorName || "BioIndex User"}
                </span>
              </button>

              <span aria-hidden="true">•</span>

              <span>
                Active{" "}
                {formatRelativeTime(topic.lastActivityAt || topic.createdAt)}
              </span>
            </div>
          </div>

          {topic.imageURL ? (
            topic.imageSource === "collection" && topic.speciesPhoto ? (
              <div className="space-y-4">
                <ForumSpeciesPhotoCard
                  imageURL={topic.imageURL}
                  speciesPhoto={topic.speciesPhoto}
                  speciesPhotoRef={topic.speciesPhotoRef}
                  compact
                />

                <div className="min-w-0">
                  <p
                    className="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-600"
                    style={{
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 7,
                      overflow: "hidden",
                    }}
                  >
                    <ForumTopicLinks text={topic.body} />
                  </p>

                  {String(topic.body || "").length > 500 && (
                    <Link
                      to={`/forum/topic/${topic.id}`}
                      className="mt-2 inline-flex text-xs font-semibold text-[#01767A] transition hover:text-[#374049]"
                    >
                      Continue reading
                    </Link>
                  )}
                </div>
              </div>
            ) : (
              <div className="grid min-w-0 grid-cols-1 overflow-hidden rounded-xl border border-[#93CA87]/40 bg-white shadow-sm sm:grid-cols-[220px_minmax(0,1fr)]">
                <button
                  type="button"
                  onClick={() => setPhotoModalOpen(true)}
                  aria-label={`View full photo for ${topic.title}`}
                  className="group relative block h-[220px] w-full overflow-hidden bg-[#FDFBF8] text-left"
                >
                  <img
                    src={topic.imageURL}
                    alt=""
                    className="h-full w-full object-cover transition duration-200 group-hover:scale-[1.02]"
                  />

                  <span className="absolute inset-x-0 bottom-0 bg-[#374049]/70 px-3 py-2 text-[10px] font-medium text-white opacity-0 transition group-hover:opacity-100">
                    View full photo
                  </span>
                </button>

                <div className="flex min-w-0 flex-col border-t border-[#93CA87]/30 bg-white px-4 py-4 sm:min-h-[220px] sm:border-l sm:border-t-0 sm:px-5">
                  <p
                    className="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-600"
                    style={{
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 6,
                      overflow: "hidden",
                    }}
                  >
                    <ForumTopicLinks text={topic.body} />
                  </p>

                  {String(topic.body || "").length > 500 && (
                    <Link
                      to={`/forum/topic/${topic.id}`}
                      className="mt-3 inline-flex self-start text-xs font-semibold text-[#01767A] transition hover:text-[#374049]"
                    >
                      Continue reading
                    </Link>
                  )}
                </div>
              </div>
            )
          ) : (
            <div>
              <p
                className="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-600"
                style={{
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 8,
                  overflow: "hidden",
                }}
              >
                <ForumTopicLinks text={topic.body} />
              </p>

              {String(topic.body || "").length > 500 && (
                <Link
                  to={`/forum/topic/${topic.id}`}
                  className="mt-2 inline-flex text-xs font-semibold text-[#01767A] transition hover:text-[#24C3BE]"
                >
                  Continue reading
                </Link>
              )}
            </div>
          )}

          <div className="flex flex-col gap-3 border-t border-[#93CA87]/25 pt-3 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span>
                {topic.commentCount || 0}{" "}
                {(topic.commentCount || 0) === 1 ? "reply" : "replies"}
              </span>

              <span>
                {topic.approvalPercentage == null
                  ? "No ratings yet"
                  : `${topic.approvalPercentage}% helpful`}
              </span>
            </div>

            <div className="bioindex-m-topic-actions flex flex-wrap items-center gap-3 self-start sm:self-auto">
              {currentUid && currentUid === topic.authorUid ? (
                <>
                  <Link
                    to={`/forum/topic/${topic.id}`}
                    className="bioindex-m-topic-action text-[10px] font-semibold text-[#01767A] transition hover:text-[#374049]"
                  >
                    Edit Topic
                  </Link>

                  <button
                    type="button"
                    onClick={deleteOwnTopic}
                    disabled={topicDeleting}
                    className="bioindex-m-topic-action text-[10px] font-semibold text-red-600 transition hover:text-red-700 disabled:cursor-wait disabled:opacity-60"
                  >
                    {topicDeleting ? "Deleting..." : "Delete Topic"}
                  </button>
                </>
              ) : currentUid ? (
                <button
                  type="button"
                  onClick={openTopicReport}
                  disabled={!!reportedContentKeys.topic}
                  className="bioindex-m-topic-action text-[10px] font-semibold text-[#374049]/55 transition hover:text-[#01767A] disabled:cursor-default disabled:text-[#01767A]"
                >
                  {reportedContentKeys.topic ? "Reported" : "Report Topic"}
                </button>
              ) : null}

              <button
                type="button"
                onClick={() => setDiscussionOpen((current) => !current)}
                aria-expanded={discussionOpen}
                className="bioindex-m-topic-action text-xs font-semibold text-[#01767A] transition hover:text-[#374049]"
              >
                {discussionOpen ? "Hide Discussion" : "View Discussion →"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {discussionOpen && (
        <InlineForumDiscussion
          topic={topic}
          currentUid={currentUid}
          reportedContentKeys={reportedContentKeys}
          onReportComment={openCommentReport}
          blockedRelationshipUserIdSet={blockedRelationshipUserIdSet}
        />
      )}

      {photoModalOpen && topic.imageURL && createPortal(
        <div
          className="fixed inset-0 z-[150] flex items-center justify-center bg-[#374049]/85 p-4 sm:p-8"
          onClick={() => setPhotoModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={`Full photo for ${topic.title}`}
        >
          <div
            className="relative inline-flex max-h-full max-w-full items-center justify-center"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={topic.imageURL}
              alt=""
              className="block max-h-[calc(100vh-2rem)] max-w-[calc(100vw-2rem)] object-contain shadow-2xl sm:max-h-[calc(100vh-4rem)] sm:max-w-[calc(100vw-4rem)]"
            />

            <button
              type="button"
              onClick={() => setPhotoModalOpen(false)}
              aria-label="Close full photo"
              className="absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-full border border-white/70 bg-[#374049]/85 p-0 text-xl font-medium leading-none text-white shadow-lg backdrop-blur-sm transition hover:bg-[#01767A] focus:outline-none focus:ring-2 focus:ring-white/70"
            >
              <span className="flex h-full w-full items-center justify-center pb-[2px]">
                ×
              </span>
            </button>
          </div>
        </div>,
        document.body,
      )}

      <ContentReportModal
        target={reportTarget}
        onClose={() => setReportTarget(null)}
        onSubmitted={markContentReported}
      />
    </article>
  );
}

// Mobile-only navigation. The desktop V7 navigation is kept unchanged.
function BioIndexMobileTabIcon({ section }) {
  const iconProps = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.8",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
    className: "bioindex-m-tab-icon",
  };
  if (section === "topics")
    return (
      <svg {...iconProps}>
        <path d="M20 11.2a7.8 7.8 0 0 1-8.2 7.3 8.8 8.8 0 0 1-3.2-.6L4 20l1.2-4.2a7.4 7.4 0 0 1-2-5A8 8 0 0 1 11.5 3C16.2 3 20 6.7 20 11.2Z" />
        <path d="M8 11h.01M12 11h.01M16 11h.01" />
      </svg>
    );
  if (section === "notifications")
    return (
      <svg {...iconProps}>
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
        <path d="M10 21h4" />
      </svg>
    );
  if (section === "community")
    return (
      <svg {...iconProps}>
        <circle cx="9" cy="8" r="3" />
        <path d="M2.5 20v-2.3a6.5 6.5 0 0 1 13 0V20Z" />
        <path d="M17 5.3a3 3 0 0 1 0 5.7M17.5 14.2a5 5 0 0 1 4 5" />
      </svg>
    );
  return (
    <svg {...iconProps}>
      <path d="M8 3h8v5a4 4 0 0 1-8 0V3Z" />
      <path d="M8 5H4v2a4 4 0 0 0 4 4M16 5h4v2a4 4 0 0 1-4 4M12 12v5M8 21h8M10 17h4v4h-4Z" />
    </svg>
  );
}

function BioIndexMobilePrimaryNav({ onSearch, onVoiceSearch }) {
  const [exploreOpen, setExploreOpen] = useState(false);
  const exploreMenuRef = useRef(null);

  useEffect(() => {
    if (!exploreOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!exploreMenuRef.current?.contains(event.target)) {
        setExploreOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setExploreOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [exploreOpen]);

  const openSearch = () => {
    setExploreOpen(false);
    onSearch?.();
  };
  const openVoiceSearch = () => {
    setExploreOpen(false);
    onVoiceSearch?.();
  };
  return (
    <nav className="bioindex-m-bottom-bar" aria-label="Primary navigation">
      <div className="bioindex-m-bottom-grid">
        <Link className="bioindex-m-bottom-item" to="/" aria-label="Home">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 10.8 12 3l9 7.8V21h-6v-7H9v7H3Z" />
          </svg>
          <span>Home</span>
        </Link>
        <div className="bioindex-m-explore-wrap" ref={exploreMenuRef}>
          <button
            type="button"
            className={`bioindex-m-bottom-item bioindex-m-explore-trigger${exploreOpen ? " bioindex-m-explore-active" : ""}`}
            onClick={() => setExploreOpen((open) => !open)}
            aria-expanded={exploreOpen}
            aria-controls="bioindex-m-explore-menu"
            aria-label="Explore or Search"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="m15.9 8.1-2.2 5.5-5.5 2.2 2.2-5.5Z" />
            </svg>
            <span>Explore</span>
          </button>

          {exploreOpen && (
            <div
              className="bioindex-m-explore-menu"
              id="bioindex-m-explore-menu"
              role="group"
              aria-label="Explore options"
            >
              <Link
                to="/explore"
                className="bioindex-m-explore-choice"
                onClick={() => setExploreOpen(false)}
              >
                <span
                  className="bioindex-m-explore-choice-icon"
                  aria-hidden="true"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="8.6" />
                    <path d="m15.6 8.4-2.2 5-5 2.2 2.2-5Z" />
                  </svg>
                </span>
                <span>Explore taxonomy</span>
              </Link>
              <button
                type="button"
                className="bioindex-m-explore-choice"
                onClick={openSearch}
              >
                <span
                  className="bioindex-m-explore-choice-icon"
                  aria-hidden="true"
                >
                  <img src="/Search.png" alt="" />
                </span>
                <span>Search BioIndex</span>
              </button>
              <button
                type="button"
                className="bioindex-m-explore-choice"
                onClick={openVoiceSearch}
              >
                <span
                  className="bioindex-m-explore-choice-icon"
                  aria-hidden="true"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <rect x="9" y="2" width="6" height="12" rx="3" />
                    <path d="M5 10a7 7 0 0 0 14 0M12 17v5m-4 0h8" />
                  </svg>
                </span>
                <span>Voice Search</span>
              </button>
            </div>
          )}
        </div>

        <Link
          className="bioindex-m-bottom-item bioindex-m-add"
          to="/upload"
          aria-label="Add Photos"
        >
          <span className="bioindex-m-add-circle" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 7h3l2-2h6l2 2h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2Z" />
              <circle cx="12" cy="13" r="3.4" />
            </svg>
          </span>
          <span>Add Photos</span>
        </Link>
        <Link
          className="bioindex-m-bottom-item"
          to="/profile"
          aria-label="Profile"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="7.5" r="4" />
            <path d="M4.5 21v-2.7a7.5 7.5 0 0 1 15 0V21" />
          </svg>
          <span>Profile</span>
        </Link>
        <div
          className="bioindex-m-bottom-item bioindex-m-current"
          aria-current="page"
        >
          <span className="bioindex-m-current-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <circle cx="9" cy="8" r="3" />
              <path d="M2.5 20v-2a6.5 6.5 0 0 1 13 0v2M17 5.5a3 3 0 0 1 0 5.5M17 14a5.5 5.5 0 0 1 4 5.5" />
            </svg>
            <CommunityNavBadge />
          </span>
          <span>Community</span>
        </div>
      </div>
    </nav>
  );
}

export default function ForumPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [currentUid, setCurrentUid] = useState(
    () => auth.currentUser?.uid || "",
  );
  const [searchOpen, setSearchOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [activeView, setActiveView] = useState("topics");
  const [topicSearch, setTopicSearch] = useState("");
  const [forumSearchOpen, setForumSearchOpen] = useState(false);
  const [forumFiltersOpen, setForumFiltersOpen] = useState(false);
  const [topicCategoryFilter, setTopicCategoryFilter] = useState("all");
  const [topicSort, setTopicSort] = useState("recent");
  const [topicParticipationFilter, setTopicParticipationFilter] =
    useState("all");

  const [newTopicOpen, setNewTopicOpen] = useState(false);
  const [newTopicTitle, setNewTopicTitle] = useState("");
  const [newTopicCategory, setNewTopicCategory] = useState(
    DEFAULT_FORUM_CATEGORY,
  );
  const [newTopicBody, setNewTopicBody] = useState("");
  const newTopicBodyRef = useRef(null);
  const [newTopicImage, setNewTopicImage] = useState(null);
  const [newTopicImagePreview, setNewTopicImagePreview] = useState("");
  const [topicImageDragging, setTopicImageDragging] = useState(false);

  const [topicPhotoPickerOpen, setTopicPhotoPickerOpen] = useState(false);
  const [topicPhotoPickerLoading, setTopicPhotoPickerLoading] = useState(false);
  const [topicPhotoPickerError, setTopicPhotoPickerError] = useState("");
  const [topicSpeciesWithPhotos, setTopicSpeciesWithPhotos] = useState([]);
  const [topicPickerSelectedSpecies, setTopicPickerSelectedSpecies] =
    useState(null);
  const [selectedExistingTopicPhoto, setSelectedExistingTopicPhoto] =
    useState(null);

  const [creatingTopic, setCreatingTopic] = useState(false);
  const [newTopicError, setNewTopicError] = useState("");
  const [topicCreatedMessage, setTopicCreatedMessage] = useState("");

  const [topics, setTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(true);
  const [topicsError, setTopicsError] = useState("");
  const [loadingMoreTopics, setLoadingMoreTopics] = useState(false);
  const [hasMoreTopics, setHasMoreTopics] = useState(false);

  const [blockedUserIds, setBlockedUserIds] = useState([]);
  const [blockedByUserIds, setBlockedByUserIds] = useState([]);
  const [blockRelationshipsLoading, setBlockRelationshipsLoading] =
    useState(true);

  const [, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [notificationsError, setNotificationsError] = useState("");
  const [loadingMoreNotifications, setLoadingMoreNotifications] =
    useState(false);
  const [hasMoreNotifications, setHasMoreNotifications] = useState(false);
  const [markingNotificationsRead, setMarkingNotificationsRead] =
    useState(false);
  const [respondingToFriendRequestId, setRespondingToFriendRequestId] =
    useState("");
  const [openedGenusNotification, setOpenedGenusNotification] = useState(null);
  const [openedTitleNotification, setOpenedTitleNotification] = useState(null);
  const [currentProfileTitle, setCurrentProfileTitle] = useState("Beginner");
  const [equippingNotificationTitle, setEquippingNotificationTitle] =
    useState(false);
  const [equipNotificationTitleError, setEquipNotificationTitleError] =
    useState("");

  const [communityActivities, setCommunityActivities] = useState([]);
  const [communityActivitiesLoading, setCommunityActivitiesLoading] =
    useState(true);
  const [communityActivitiesError, setCommunityActivitiesError] = useState("");
  const [loadingMoreCommunityActivities, setLoadingMoreCommunityActivities] =
    useState(false);
  const [hasMoreCommunityActivities, setHasMoreCommunityActivities] =
    useState(false);
  const [openedCommunityUploadActivity, setOpenedCommunityUploadActivity] =
    useState(null);
  const [communityUploadSpecies, setCommunityUploadSpecies] = useState([]);
  const [communityUploadLoading, setCommunityUploadLoading] = useState(false);
  const [communityUploadError, setCommunityUploadError] = useState("");
  const [communityUploadLoadedImages, setCommunityUploadLoadedImages] =
    useState({});
  const [communityUploadPreviewBySpecies, setCommunityUploadPreviewBySpecies] =
    useState({});
  const [selectedCommunitySpecies, setSelectedCommunitySpecies] =
    useState(null);
  const [communityUploadPhotoIndex, setCommunityUploadPhotoIndex] = useState(0);
  const [communityUploadSlides, setCommunityUploadSlides] = useState([]);
  const [communityUploadSlideIndex, setCommunityUploadSlideIndex] = useState(0);
  const [communityUploadPhotoBounds, setCommunityUploadPhotoBounds] =
    useState(null);
  const [profileNavigationTarget, setProfileNavigationTarget] = useState(null);

  const [rankingProfiles, setRankingProfiles] = useState([]);
  const [rankingCommunityTotals, setRankingCommunityTotals] = useState(null);
  const [rankingFriends, setRankingFriends] = useState([]);
  const [rankingSentRequests, setRankingSentRequests] = useState([]);
  const [rankingReceivedRequests, setRankingReceivedRequests] = useState([]);
  const [rankingsLoading, setRankingsLoading] = useState(false);
  const [rankingsLoaded, setRankingsLoaded] = useState(false);
  const [rankingsError, setRankingsError] = useState("");
  const [rankingView, setRankingView] = useState("all");
  const [rankingTaxon, setRankingTaxon] = useState("all");
  const [taxonomicRankingStats, setTaxonomicRankingStats] = useState(null);
  const [taxonomicRankingLoading, setTaxonomicRankingLoading] = useState(false);
  const [taxonomicRankingError, setTaxonomicRankingError] = useState("");
  const taxonomicRankingRequestRef = useRef(false);
  const [rankingActionUid, setRankingActionUid] = useState("");
  const [friendRequestTarget, setFriendRequestTarget] = useState(null);

  const topicCursorRef = useRef(null);
  const hasLoadedAdditionalPagesRef = useRef(false);

  const notificationsCursorRef = useRef(null);
  const hasLoadedAdditionalNotificationsRef = useRef(false);
  const notificationsLoadLockRef = useRef(false);
  const notificationsLoadMoreSentinelRef = useRef(null);
  const loadMoreNotificationsRef = useRef(null);

  const communityActivitiesCursorRef = useRef(null);
  const hasLoadedAdditionalCommunityActivitiesRef = useRef(false);
  const communityActivitiesLoadLockRef = useRef(false);
  const communityActivitiesLoadMoreSentinelRef = useRef(null);
  const loadMoreCommunityActivitiesRef = useRef(null);

  const openedTitleDisplay = getCurrentTitleDisplay(openedTitleNotification);

  useEffect(
    () =>
      onAuthStateChanged(auth, (user) => {
        setCurrentUid(user?.uid || "");
      }),
    [],
  );

  useEffect(() => {
    const requestedView = new URLSearchParams(location.search).get("view");

    if (
      requestedView === "topics" ||
      requestedView === "notifications" ||
      requestedView === "community" ||
      requestedView === "rankings"
    ) {
      setActiveView(requestedView);
      if (requestedView === "community") markFeedSeen();
      if (requestedView === "rankings") markRankingsSeen();
    }
    // Only react to URL changes, not snapshot updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  async function hydrateTopicDocuments(topicDocuments) {
    const currentUid = auth.currentUser?.uid || "";

    return Promise.all(
      topicDocuments.map(async (topicDocument) => {
        const topicData = topicDocument.data();

        try {
          const [votesSnap, commentsSnap] = await Promise.all([
            getDocs(collection(db, "forumTopics", topicDocument.id, "votes")),
            getDocs(
              collection(db, "forumTopics", topicDocument.id, "comments"),
            ),
          ]);

          let positiveVotes = 0;
          let negativeVotes = 0;

          votesSnap.forEach((voteDocument) => {
            const value = Number(voteDocument.data()?.value || 0);

            if (value === 1) positiveVotes++;
            if (value === -1) negativeVotes++;
          });

          const totalVotes = positiveVotes + negativeVotes;

          const currentUserParticipated =
            !!currentUid &&
            (topicData.authorUid === currentUid ||
              commentsSnap.docs.some(
                (commentDocument) =>
                  commentDocument.data()?.authorUid === currentUid,
              ));

          return {
            id: topicDocument.id,
            ...topicData,
            positiveVotes,
            negativeVotes,
            currentUserParticipated,
            approvalPercentage:
              totalVotes > 0
                ? Math.round((positiveVotes / totalVotes) * 100)
                : null,
          };
        } catch (error) {
          console.error(`Failed to prepare topic ${topicDocument.id}:`, error);

          return {
            id: topicDocument.id,
            ...topicData,
            positiveVotes: 0,
            negativeVotes: 0,
            currentUserParticipated:
              !!currentUid && topicData.authorUid === currentUid,
            approvalPercentage: null,
          };
        }
      }),
    );
  }

  useEffect(() => {
    const currentUid = auth.currentUser?.uid;

    if (!currentUid) {
      setBlockedUserIds([]);
      setBlockedByUserIds([]);
      setBlockRelationshipsLoading(false);
      return undefined;
    }

    let outgoingBlocksLoaded = false;
    let incomingBlocksLoaded = false;

    function finishBlockRelationshipLoading() {
      if (outgoingBlocksLoaded && incomingBlocksLoaded) {
        setBlockRelationshipsLoading(false);
      }
    }

    setBlockRelationshipsLoading(true);

    const outgoingBlocksQuery = query(
      collection(db, "userBlocks"),
      where("blockerUid", "==", currentUid),
    );

    const incomingBlocksQuery = query(
      collection(db, "userBlocks"),
      where("blockedUid", "==", currentUid),
    );

    const unsubscribeOutgoingBlocks = onSnapshot(
      outgoingBlocksQuery,
      (snapshot) => {
        setBlockedUserIds(
          snapshot.docs
            .map((blockDocument) => blockDocument.data().blockedUid)
            .filter(Boolean),
        );

        outgoingBlocksLoaded = true;
        finishBlockRelationshipLoading();
      },
      (error) => {
        console.error("Failed to load blocked users:", error);
        outgoingBlocksLoaded = true;
        finishBlockRelationshipLoading();
      },
    );

    const unsubscribeIncomingBlocks = onSnapshot(
      incomingBlocksQuery,
      (snapshot) => {
        setBlockedByUserIds(
          snapshot.docs
            .map((blockDocument) => blockDocument.data().blockerUid)
            .filter(Boolean),
        );

        incomingBlocksLoaded = true;
        finishBlockRelationshipLoading();
      },
      (error) => {
        console.error("Failed to load incoming blocks:", error);
        incomingBlocksLoaded = true;
        finishBlockRelationshipLoading();
      },
    );

    return () => {
      unsubscribeOutgoingBlocks();
      unsubscribeIncomingBlocks();
    };
  }, []);

  useEffect(() => {
    if (!currentUid) {
      setRankingFriends([]);
      return undefined;
    }

    return onSnapshot(
      collection(db, "users", currentUid, "friends"),
      (snapshot) => {
        setRankingFriends(snapshot.docs.map((friend) => friend.id));
      },
      (error) => {
        console.error("Failed to load accepted friends:", error);
        setRankingFriends([]);
      },
    );
  }, [currentUid]);

  useEffect(() => {
    if (activeView !== "rankings" || !currentUid || rankingsLoaded) return;

    let cancelled = false;

    async function loadRankings() {
      setRankingsLoading(true);
      setRankingsError("");

      try {
        const functions = getFunctions(undefined, "southamerica-east1");
        const getCommunityRankings = httpsCallable(
          functions,
          "getCommunityRankings",
        );

        const [
          rankingsResult,
          friendsSnapshot,
          sentSnapshot,
          receivedSnapshot,
        ] = await Promise.all([
          getCommunityRankings(),
          getDocs(collection(db, "users", currentUid, "friends")),
          getDocs(
            query(
              collection(db, "friendRequests"),
              where("fromUid", "==", currentUid),
              where("status", "==", "pending"),
            ),
          ),
          getDocs(
            query(
              collection(db, "friendRequests"),
              where("toUid", "==", currentUid),
              where("status", "==", "pending"),
            ),
          ),
        ]);

        if (cancelled) return;

        setRankingProfiles(
          Array.isArray(rankingsResult.data?.profiles)
            ? rankingsResult.data.profiles
            : [],
        );

        const receivedCommunityTotals = rankingsResult.data?.communityTotals;

        if (
          Number.isFinite(Number(receivedCommunityTotals?.uniqueSpecies)) &&
          Number.isFinite(Number(receivedCommunityTotals?.totalPhotos))
        ) {
          setRankingCommunityTotals({
            uniqueSpecies: Math.max(
              0,
              Math.floor(Number(receivedCommunityTotals.uniqueSpecies)),
            ),
            totalPhotos: Math.max(
              0,
              Math.floor(Number(receivedCommunityTotals.totalPhotos)),
            ),
          });
        } else {
          setRankingCommunityTotals(null);
        }

        setRankingFriends(friendsSnapshot.docs.map((friend) => friend.id));

        setRankingSentRequests(
          sentSnapshot.docs.map((requestDocument) => ({
            id: requestDocument.id,
            ...requestDocument.data(),
          })),
        );

        setRankingReceivedRequests(
          receivedSnapshot.docs.map((requestDocument) => ({
            id: requestDocument.id,
            ...requestDocument.data(),
          })),
        );

        setRankingsLoaded(true);
      } catch (error) {
        console.error("Failed to load BioIndex rankings:", error);

        if (!cancelled) {
          setRankingsError("The rankings could not be loaded right now.");
        }
      } finally {
        if (!cancelled) setRankingsLoading(false);
      }
    }

    loadRankings();

    return () => {
      cancelled = true;
    };
  }, [activeView, currentUid, rankingsLoaded]);

  useEffect(() => {
    if (
      activeView !== "rankings" ||
      !currentUid ||
      rankingTaxon === "all" ||
      taxonomicRankingStats ||
      taxonomicRankingRequestRef.current
    )
      return;

    taxonomicRankingRequestRef.current = true;
    setTaxonomicRankingLoading(true);
    setTaxonomicRankingError("");

    const loadTaxonomicRankings = httpsCallable(
      getFunctions(undefined, "southamerica-east1"),
      "getTaxonomicCommunityRankings",
    );

    loadTaxonomicRankings()
      .then((result) => {
        if (auth.currentUser?.uid === currentUid) {
          setTaxonomicRankingStats(result.data || null);
        }
      })
      .catch((error) => {
        console.error("Failed to load taxonomic rankings:", error);
        setTaxonomicRankingError(
          "Could not load taxonomic rankings. Confirm the new Cloud Function has been deployed.",
        );
      })
      .finally(() => {
        taxonomicRankingRequestRef.current = false;
        setTaxonomicRankingLoading(false);
      });
  }, [activeView, currentUid, rankingTaxon, taxonomicRankingStats]);

  useEffect(() => {
    setTopicsLoading(true);
    setTopicsError("");

    const firstPageQuery = query(
      collection(db, "forumTopics"),
      orderBy("lastActivityAt", "desc"),
      limit(TOPICS_PER_PAGE),
    );

    const unsubscribe = onSnapshot(
      firstPageQuery,
      async (snapshot) => {
        try {
          const hydratedTopics = await hydrateTopicDocuments(snapshot.docs);
          const firstPageIds = new Set(hydratedTopics.map((topic) => topic.id));

          setTopics((previousTopics) => {
            const additionalTopics = previousTopics.filter(
              (topic) => !firstPageIds.has(topic.id),
            );

            return sortForumTopics([...hydratedTopics, ...additionalTopics]);
          });

          if (!hasLoadedAdditionalPagesRef.current) {
            topicCursorRef.current =
              snapshot.docs[snapshot.docs.length - 1] || null;
          }

          setHasMoreTopics(snapshot.docs.length === TOPICS_PER_PAGE);
          setTopicsError("");
        } catch (error) {
          console.error("Failed to prepare forum topics:", error);
          setTopicsError("Forum topics could not be loaded.");
        } finally {
          setTopicsLoading(false);
        }
      },
      (error) => {
        console.error("Failed to load forum topics:", error);
        setTopicsError("Forum topics could not be loaded.");
        setTopicsLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const currentUid = auth.currentUser?.uid;

    if (!currentUid) {
      setNotifications([]);
      setNotificationsLoading(false);
      return;
    }

    getDoc(doc(db, "profile", currentUid))
      .then((profileSnapshot) => {
        if (!profileSnapshot.exists()) {
          setCurrentProfileTitle("Beginner");
          return;
        }

        const profileData = profileSnapshot.data();

        setCurrentProfileTitle(profileData.title || "Beginner");

        if (Number(profileData.genusUnlockReconciliationVersion || 0) < 2) {
          const functions = getFunctions(undefined, "southamerica-east1");
          const reconcileMyGenusUnlocks = httpsCallable(
            functions,
            "reconcileMyGenusUnlocks",
          );

          reconcileMyGenusUnlocks().catch((error) => {
            console.error(
              "Failed to reconcile legacy genus achievements:",
              error,
            );
          });
        }
      })
      .catch((error) => {
        console.error("Failed to load current profile data:", error);
        setCurrentProfileTitle("Beginner");
      });

    setNotificationsLoading(true);
    setNotificationsError("");
    setLoadingMoreNotifications(false);
    setHasMoreNotifications(false);

    notificationsCursorRef.current = null;
    hasLoadedAdditionalNotificationsRef.current = false;
    notificationsLoadLockRef.current = false;

    const notificationsQuery = query(
      collection(db, "users", currentUid, "notifications"),
      orderBy("createdAt", "desc"),
      limit(NOTIFICATIONS_PER_PAGE),
    );

    const unsubscribe = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        const loadedNotifications = snapshot.docs.map(
          (notificationDocument) => ({
            id: notificationDocument.id,
            ...notificationDocument.data(),
          }),
        );

        setNotifications((previousNotifications) => {
          if (!hasLoadedAdditionalNotificationsRef.current) {
            return loadedNotifications;
          }

          const firstPageIds = new Set(
            loadedNotifications.map((notification) => notification.id),
          );

          const additionalNotifications = previousNotifications.filter(
            (notification) => !firstPageIds.has(notification.id),
          );

          return [...loadedNotifications, ...additionalNotifications].sort(
            (a, b) =>
              timestampToMillis(b.createdAt) - timestampToMillis(a.createdAt),
          );
        });

        if (!hasLoadedAdditionalNotificationsRef.current) {
          notificationsCursorRef.current =
            snapshot.docs[snapshot.docs.length - 1] || null;
        }

        setHasMoreNotifications(
          snapshot.docs.length === NOTIFICATIONS_PER_PAGE,
        );
        setNotificationsError("");
        setNotificationsLoading(false);
      },
      (error) => {
        console.error("Failed to load notifications:", error);
        setHasMoreNotifications(false);
        setNotificationsError("Notifications could not be loaded.");
        setNotificationsLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const currentUid = auth.currentUser?.uid;

    if (!currentUid) {
      setCommunityActivities([]);
      setCommunityActivitiesLoading(false);
      return;
    }

    let cancelled = false;
    let completionRequestId = 0;

    setCommunityActivitiesLoading(true);
    setCommunityActivitiesError("");
    setLoadingMoreCommunityActivities(false);
    setHasMoreCommunityActivities(false);

    communityActivitiesCursorRef.current = null;
    hasLoadedAdditionalCommunityActivitiesRef.current = false;
    communityActivitiesLoadLockRef.current = false;

    const communityFeedQuery = query(
      collection(db, "users", currentUid, "communityFeed"),
      orderBy("createdAt", "desc"),
      limit(COMMUNITY_ACTIVITIES_PER_PAGE),
    );

    const unsubscribe = onSnapshot(
      communityFeedQuery,
      async (snapshot) => {
        const currentCompletionRequestId = ++completionRequestId;

        const loadedActivities = snapshot.docs.map((activityDocument) => ({
          id: activityDocument.id,
          ownerUid: currentUid,
          ...activityDocument.data(),
        }));

        let reconciledActivities = loadedActivities;

        try {
          reconciledActivities =
            await reconcileLoadedCommunityPhotoActivities(loadedActivities);
        } catch (error) {
          console.error(
            "Failed to reconcile Community Feed photo activities:",
            error,
          );
        }

        let completedGenusActivities = [];

        try {
          completedGenusActivities = await loadCompleteCommunityGenusGroups(
            currentUid,
            reconciledActivities,
          );
        } catch (error) {
          console.error(
            "Failed to complete daily Community Feed genus groups:",
            error,
          );
        }

        if (cancelled || currentCompletionRequestId !== completionRequestId) {
          return;
        }

        const firstPageActivitiesById = new Map();

        [...reconciledActivities, ...completedGenusActivities].forEach(
          (activity) => {
            firstPageActivitiesById.set(activity.id, activity);
          },
        );

        const firstPageActivities = [...firstPageActivitiesById.values()].sort(
          (a, b) => getCommunityActivityTime(b) - getCommunityActivityTime(a),
        );

        setCommunityActivities((previousActivities) => {
          if (!hasLoadedAdditionalCommunityActivitiesRef.current) {
            return firstPageActivities;
          }

          const firstPageIds = new Set(
            firstPageActivities.map((activity) => activity.id),
          );

          const additionalActivities = previousActivities.filter(
            (activity) => !firstPageIds.has(activity.id),
          );

          return [...firstPageActivities, ...additionalActivities].sort(
            (a, b) => getCommunityActivityTime(b) - getCommunityActivityTime(a),
          );
        });

        if (!hasLoadedAdditionalCommunityActivitiesRef.current) {
          communityActivitiesCursorRef.current =
            snapshot.docs[snapshot.docs.length - 1] || null;
        }

        setHasMoreCommunityActivities(
          snapshot.docs.length === COMMUNITY_ACTIVITIES_PER_PAGE,
        );
        setCommunityActivitiesError("");
        setCommunityActivitiesLoading(false);
      },
      (error) => {
        console.error("Failed to load community activity:", error);

        setCommunityActivities([]);
        setHasMoreCommunityActivities(false);
        setCommunityActivitiesError("Community activity could not be loaded.");
        setCommunityActivitiesLoading(false);
      },
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    loadMoreNotificationsRef.current = loadMoreNotifications;
    loadMoreCommunityActivitiesRef.current = loadMoreCommunityActivities;
  });

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;

    const sentinel =
      activeView === "notifications"
        ? notificationsLoadMoreSentinelRef.current
        : activeView === "community"
          ? communityActivitiesLoadMoreSentinelRef.current
          : null;

    const canLoadMore =
      activeView === "notifications"
        ? hasMoreNotifications && !loadingMoreNotifications
        : activeView === "community"
          ? hasMoreCommunityActivities && !loadingMoreCommunityActivities
          : false;

    if (!sentinel || !canLoadMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;

        if (activeView === "notifications") {
          loadMoreNotificationsRef.current?.();
        }

        if (activeView === "community") {
          loadMoreCommunityActivitiesRef.current?.();
        }
      },
      {
        rootMargin: "80px 0px",
      },
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [
    activeView,
    hasMoreNotifications,
    hasMoreCommunityActivities,
    loadingMoreNotifications,
    loadingMoreCommunityActivities,
  ]);

  useEffect(() => {
    if (!newTopicOpen) return;
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [newTopicOpen]);

  useEffect(() => {
    if (!selectedCommunitySpecies || communityUploadSlides.length <= 1) {
      return undefined;
    }

    function handleCommunityUploadKeys(event) {
      let direction = 0;

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        direction = -1;
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        direction = 1;
      }

      if (!direction) return;

      const nextIndex = communityUploadSlideIndex + direction;
      const nextSlide = communityUploadSlides[nextIndex];

      if (!nextSlide) return;

      setCommunityUploadSlideIndex(nextIndex);
      setSelectedCommunitySpecies(nextSlide.species);
      setCommunityUploadPhotoIndex(0);
      setOpenedCommunityUploadActivity(null);
    }

    window.addEventListener("keydown", handleCommunityUploadKeys);

    return () => {
      window.removeEventListener("keydown", handleCommunityUploadKeys);
    };
  }, [
    selectedCommunitySpecies,
    communityUploadSlideIndex,
    communityUploadSlides,
  ]);

  // Position the Friends' Updates arrows beside the displayed image.
  // SpeciesPhotoModal can change its wrapper/classes without breaking this:
  // locate the photo by the slide's own URL rather than by a CSS selector.
  useEffect(() => {
    if (!selectedCommunitySpecies || communityUploadSlides.length <= 1) {
      setCommunityUploadPhotoBounds(null);
      return undefined;
    }

    const activePhotoUrl =
      communityUploadSlides[communityUploadSlideIndex]?.form?.url || "";
    let observedImage = null;
    let frameId = null;

    const sizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => scheduleMeasurement())
        : null;

    function measurePhoto() {
      frameId = null;

      const candidates = Array.from(document.querySelectorAll("img"))
        .filter(
          (image) =>
            activePhotoUrl &&
            (image.getAttribute("src") === activePhotoUrl ||
              image.currentSrc === activePhotoUrl ||
              image.src === activePhotoUrl),
        )
        .map((image) => ({
          image,
          rect: image.getBoundingClientRect(),
        }))
        .filter(({ rect }) => rect.width * rect.height > 4096)
        .sort(
          (a, b) => b.rect.width * b.rect.height - a.rect.width * a.rect.height,
        );

      // The modal image is larger than the thumbnail of the same photo.
      const image = candidates[0]?.image || null;

      if (image !== observedImage) {
        if (observedImage) {
          observedImage.removeEventListener("load", scheduleMeasurement);
          sizeObserver?.unobserve(observedImage);
        }
        observedImage = image;
        if (observedImage) {
          observedImage.addEventListener("load", scheduleMeasurement);
          sizeObserver?.observe(observedImage);
        }
      }

      if (!image) {
        // Controls retain their centered fallback position while the modal
        // image mounts or when a differently sourced preview is displayed.
        setCommunityUploadPhotoBounds(null);
        return;
      }

      const rect = image.getBoundingClientRect();
      const edgePadding = window.innerWidth < 640 ? 22 : 26;
      const left = Math.max(edgePadding, rect.left);
      const right = Math.min(window.innerWidth - edgePadding, rect.right);
      const top = Math.max(
        edgePadding,
        Math.min(window.innerHeight - edgePadding, rect.top + rect.height / 2),
      );
      const nextBounds = {
        left,
        right: Math.max(left, right),
        top,
      };

      setCommunityUploadPhotoBounds((previous) =>
        previous &&
        Math.abs(previous.left - nextBounds.left) < 0.5 &&
        Math.abs(previous.right - nextBounds.right) < 0.5 &&
        Math.abs(previous.top - nextBounds.top) < 0.5
          ? previous
          : nextBounds,
      );
    }

    function scheduleMeasurement() {
      if (frameId !== null) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(measurePhoto);
    }

    // Observe the document because SpeciesPhotoModal can be portaled to body.
    const contentObserver =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(scheduleMeasurement)
        : null;

    contentObserver?.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["src"],
    });

    window.addEventListener("resize", scheduleMeasurement);
    window.addEventListener("scroll", scheduleMeasurement, true);
    scheduleMeasurement();

    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      window.removeEventListener("resize", scheduleMeasurement);
      window.removeEventListener("scroll", scheduleMeasurement, true);
      contentObserver?.disconnect();
      sizeObserver?.disconnect();
      observedImage?.removeEventListener("load", scheduleMeasurement);
    };
  }, [
    selectedCommunitySpecies,
    communityUploadSlideIndex,
    communityUploadSlides,
  ]);

  useEffect(() => {
    return () => {
      if (newTopicImage && newTopicImagePreview) {
        URL.revokeObjectURL(newTopicImagePreview);
      }
    };
  }, [newTopicImage, newTopicImagePreview]);

  const blockedRelationshipUserIdSet = useMemo(
    () => new Set([...blockedUserIds, ...blockedByUserIds]),
    [blockedUserIds, blockedByUserIds],
  );

  const { notificationRecords: allNotificationRecords } =
    useNotificationBadges();

  const visibleNotifications = useMemo(
    () =>
      allNotificationRecords.filter(
        (notification) =>
          !notification.actorUid ||
          !blockedRelationshipUserIdSet.has(notification.actorUid),
      ),
    [allNotificationRecords, blockedRelationshipUserIdSet],
  );

  const visibleCommunityActivities = useMemo(
    () =>
      communityActivities.filter(
        (activity) =>
          !activity.actorUid ||
          !blockedRelationshipUserIdSet.has(activity.actorUid),
      ),
    [communityActivities, blockedRelationshipUserIdSet],
  );

  const notificationItems = useMemo(
    () => groupNotificationItems(visibleNotifications),
    [visibleNotifications],
  );

  const communityFeedItems = useMemo(
    () => groupCommunityFeedItems(visibleCommunityActivities),
    [visibleCommunityActivities],
  );

  const visibleRankingProfiles = useMemo(
    () =>
      rankingProfiles.filter(
        (profile) => !blockedRelationshipUserIdSet.has(profile.uid),
      ),
    [rankingProfiles, blockedRelationshipUserIdSet],
  );

  const {
    personal: unreadNotificationCount,
    community: unreadCommunityActivityCount,
    rankings: unreadRankingCount,
    markFeedSeen,
    markRankingsSeen,
  } = useNotificationBadges();

  useEffect(() => {
    if (activeView === "rankings" && unreadRankingCount > 0) {
      markRankingsSeen();
    }
    // Respond to newly loaded ranking notifications while rankings is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, unreadRankingCount]);

  useEffect(() => {
    const openedActorUid = openedCommunityUploadActivity?.actorUid;

    if (!openedActorUid || !blockedRelationshipUserIdSet.has(openedActorUid)) {
      return;
    }

    setOpenedCommunityUploadActivity(null);
    setCommunityUploadSpecies([]);
    setCommunityUploadError("");
    setCommunityUploadLoadedImages({});
    setCommunityUploadPreviewBySpecies({});
    setSelectedCommunitySpecies(null);
    setCommunityUploadPhotoIndex(0);
    setCommunityUploadSlides([]);
    setCommunityUploadSlideIndex(0);
  }, [openedCommunityUploadActivity?.actorUid, blockedRelationshipUserIdSet]);

  const tabs = [
    {
      key: "topics",
      label: "Forum",
      icon: "/ForumIcon.png",
    },
    {
      key: "notifications",
      label: "My Activity",
      icon: "/NotificationsIcon.png",
      badge: unreadNotificationCount,
    },
    {
      key: "community",
      label: "Friends’ Updates",
      icon: "/CommunityIcon.png",
      badge: unreadCommunityActivityCount,
    },
    {
      key: "rankings",
      label: "Rankings",
      icon: "/RankingsIcon.png",
      badge: unreadRankingCount,
    },
  ];

  async function markCommunityFeedSeen() {
    await markFeedSeen();
  }

  async function markAchievementNotificationsRead(items) {
    const pending = items.filter((item) => !item.read);
    if (!currentUid || pending.length === 0) return;
    try {
      for (let i = 0; i < pending.length; i += 400) {
        const batch = writeBatch(db);
        pending
          .slice(i, i + 400)
          .forEach((item) =>
            batch.update(
              doc(db, "users", currentUid, "notifications", item.id),
              { read: true },
            ),
          );
        await batch.commit();
      }
      setNotifications((previous) =>
        previous.map((item) =>
          pending.some((readItem) => readItem.id === item.id)
            ? { ...item, read: true }
            : item,
        ),
      );
    } catch (error) {
      console.error("Could not mark grouped notifications as read:", error);
      setNotificationsError("Could not mark these notifications as read.");
    }
  }

  function showRankingUpdates() {
    setActiveView("rankings");
    markRankingsSeen();
  }

  function requestCommunityProfile(person) {
    const actorUid = String(
      person?.uid || person?.actorUid || person?.authorUid || "",
    ).trim();

    const canViewProfile =
      actorUid === currentUid || rankingFriends.includes(actorUid);

    if (
      !actorUid ||
      !canViewProfile ||
      blockedRelationshipUserIdSet.has(actorUid)
    ) {
      return;
    }

    setProfileNavigationTarget({
      uid: actorUid,
      name: String(
        person?.name ||
          person?.actorName ||
          person?.authorName ||
          "BioIndex User",
      ).trim(),
    });
  }

  function confirmCommunityProfileNavigation() {
    if (!profileNavigationTarget?.uid) return;

    const canViewProfile =
      profileNavigationTarget.uid === currentUid ||
      rankingFriends.includes(profileNavigationTarget.uid);

    if (!canViewProfile) {
      setProfileNavigationTarget(null);
      return;
    }

    const targetRoute =
      profileNavigationTarget.uid === currentUid
        ? "/profile"
        : `/profile/${profileNavigationTarget.uid}`;

    setProfileNavigationTarget(null);
    navigate(targetRoute);
  }

  async function removeFriendRequestNotifications(requestId) {
    if (!currentUid || !requestId) return;

    const notificationSnapshot = await getDocs(
      query(
        collection(db, "users", currentUid, "notifications"),
        where("friendRequestId", "==", requestId),
      ),
    );

    await Promise.all(
      notificationSnapshot.docs.map((notificationDocument) =>
        deleteDoc(notificationDocument.ref),
      ),
    );
  }

  async function sendRankingFriendRequest() {
    const target = friendRequestTarget;
    const user = auth.currentUser;

    if (
      !user ||
      !target?.uid ||
      target.uid === user.uid ||
      rankingActionUid ||
      blockedRelationshipUserIdSet.has(target.uid)
    ) {
      return;
    }

    setRankingActionUid(target.uid);
    setRankingsError("");

    try {
      const [outgoingSnapshot, incomingSnapshot] = await Promise.all([
        getDocs(
          query(
            collection(db, "friendRequests"),
            where("fromUid", "==", user.uid),
            where("toUid", "==", target.uid),
            where("status", "==", "pending"),
            limit(1),
          ),
        ),
        getDocs(
          query(
            collection(db, "friendRequests"),
            where("fromUid", "==", target.uid),
            where("toUid", "==", user.uid),
            where("status", "==", "pending"),
            limit(1),
          ),
        ),
      ]);

      if (!incomingSnapshot.empty) {
        const requestDocument = incomingSnapshot.docs[0];

        setRankingReceivedRequests((requests) => [
          ...requests.filter((request) => request.id !== requestDocument.id),
          { id: requestDocument.id, ...requestDocument.data() },
        ]);

        setFriendRequestTarget(null);
        return;
      }

      if (!outgoingSnapshot.empty) {
        const requestDocument = outgoingSnapshot.docs[0];

        setRankingSentRequests((requests) => [
          ...requests.filter((request) => request.id !== requestDocument.id),
          { id: requestDocument.id, ...requestDocument.data() },
        ]);

        setFriendRequestTarget(null);
        return;
      }

      const requestRef = await addDoc(collection(db, "friendRequests"), {
        fromUid: user.uid,
        toUid: target.uid,
        status: "pending",
        createdAt: serverTimestamp(),
      });

      const currentProfile =
        rankingProfiles.find((profile) => profile.uid === user.uid) || {};

      const actorName =
        String(
          currentProfile.name ||
            user.displayName ||
            user.email?.split("@")[0] ||
            "BioIndex User",
        ).trim() || "BioIndex User";

      try {
        await addDoc(collection(db, "users", target.uid, "notifications"), {
          type: "friend_request",
          message: `${actorName} sent you a friend request`,
          detail: "View the request in My Notifications or Rankings.",
          targetRoute: "/community?view=rankings",
          read: false,
          createdAt: serverTimestamp(),
          actorUid: user.uid,
          actorName,
          actorPhotoURL: String(currentProfile.photoURL || "").trim(),
          topicId: "",
          commentId: "",
          parentCommentId: "",
          titleId: "",
          genusName: "",
          friendRequestId: requestRef.id,
          dedupeKey: `friend_request_${requestRef.id}`,
        });
      } catch (notificationError) {
        console.error(
          "Friend request sent, but its notification could not be created:",
          notificationError,
        );
      }

      setRankingSentRequests((requests) => [
        ...requests,
        {
          id: requestRef.id,
          fromUid: user.uid,
          toUid: target.uid,
          status: "pending",
        },
      ]);

      setFriendRequestTarget(null);
    } catch (error) {
      console.error("Failed to send ranking friend request:", error);
      setRankingsError("The friend request could not be sent.");
    } finally {
      setRankingActionUid("");
    }
  }

  async function respondToRankingFriendRequest(request, accepted) {
    const user = auth.currentUser;

    if (
      !user ||
      !request?.id ||
      !request.fromUid ||
      rankingActionUid ||
      blockedRelationshipUserIdSet.has(request.fromUid)
    ) {
      return;
    }

    setRankingActionUid(request.fromUid);
    setRankingsError("");

    try {
      const requestRef = doc(db, "friendRequests", request.id);
      const requestSnapshot = await getDoc(requestRef);

      if (!requestSnapshot.exists()) {
        setRankingReceivedRequests((requests) =>
          requests.filter((candidate) => candidate.id !== request.id),
        );
        throw new Error("Friend request no longer exists.");
      }

      const requestData = requestSnapshot.data();

      if (
        requestData.fromUid !== request.fromUid ||
        requestData.toUid !== user.uid ||
        requestData.status !== "pending"
      ) {
        throw new Error("Friend request data does not match.");
      }

      if (accepted) {
        await Promise.all([
          setDoc(doc(db, "users", user.uid, "friends", request.fromUid), {
            uid: request.fromUid,
            requestId: request.id,
            createdAt: serverTimestamp(),
          }),
          setDoc(doc(db, "users", request.fromUid, "friends", user.uid), {
            uid: user.uid,
            requestId: request.id,
            createdAt: serverTimestamp(),
          }),
        ]);
      }

      await deleteDoc(requestRef);

      try {
        await removeFriendRequestNotifications(request.id);
      } catch (notificationCleanupError) {
        console.error(
          "Friend request response saved, but notification cleanup failed:",
          notificationCleanupError,
        );
      }

      if (accepted) {
        const currentProfile =
          rankingProfiles.find((profile) => profile.uid === user.uid) || {};
        const actorName =
          String(
            currentProfile.name ||
              user.displayName ||
              user.email?.split("@")[0] ||
              "BioIndex User",
          ).trim() || "BioIndex User";

        try {
          await addDoc(
            collection(db, "users", request.fromUid, "notifications"),
            {
              type: "friend_accepted",
              message: `${actorName} accepted your friend request`,
              detail: "You are now friends on BioIndex.",
              targetRoute: `/profile/${user.uid}`,
              read: false,
              createdAt: serverTimestamp(),
              actorUid: user.uid,
              actorName,
              actorPhotoURL: String(currentProfile.photoURL || "").trim(),
              topicId: "",
              commentId: "",
              parentCommentId: "",
              titleId: "",
              genusName: "",
              friendRequestId: request.id,
              dedupeKey: `friend_accepted_${request.id}`,
            },
          );
        } catch (notificationError) {
          console.error(
            "Friendship created, but its notification could not be created:",
            notificationError,
          );
        }

        setRankingFriends((friends) =>
          friends.includes(request.fromUid)
            ? friends
            : [...friends, request.fromUid],
        );
      }

      setRankingReceivedRequests((requests) =>
        requests.filter((candidate) => candidate.id !== request.id),
      );
    } catch (error) {
      console.error("Failed to respond to ranking friend request:", error);
      setRankingsError(
        accepted
          ? "The friend request could not be accepted."
          : "The friend request could not be declined.",
      );
    } finally {
      setRankingActionUid("");
    }
  }

  async function openCommunityUploadActivity(
    activity,
    selectedPhotoRef = null,
  ) {
    if (!activity?.actorUid || !Array.isArray(activity.photoRefs)) return;

    setOpenedCommunityUploadActivity(activity);
    setCommunityUploadSpecies([]);
    setCommunityUploadLoadedImages({});
    setCommunityUploadPreviewBySpecies({});
    setCommunityUploadSlides([]);
    setCommunityUploadSlideIndex(0);
    setCommunityUploadError("");
    setCommunityUploadLoading(true);

    try {
      const refsBySpecies = new Map();

      activity.photoRefs.forEach((photoRef, index) => {
        if (!photoRef?.formUrl) return;

        const speciesId =
          String(photoRef.speciesId || "").trim() || `legacy_photo_${index}`;

        const currentRefs = refsBySpecies.get(speciesId) || [];

        currentRefs.push({
          ...photoRef,
          speciesId,
        });

        refsBySpecies.set(speciesId, currentRefs);
      });

      const speciesIds = Array.from(refsBySpecies.keys());

      const speciesDocuments = await Promise.all(
        speciesIds.map(async (speciesId) => {
          if (speciesId.startsWith("legacy_photo_")) {
            return {
              id: speciesId,
              exists: () => false,
              data: () => ({}),
            };
          }

          return getDoc(
            doc(db, "users", activity.actorUid, "species", speciesId),
          );
        }),
      );

      const loadedSpecies = speciesDocuments
        .map((speciesSnapshot) => {
          const referencedForms = refsBySpecies.get(speciesSnapshot.id) || [];

          if (referencedForms.length === 0) return null;

          const speciesData = speciesSnapshot.exists()
            ? speciesSnapshot.data()
            : {};

          const currentForms = Array.isArray(speciesData.forms)
            ? speciesData.forms
            : [];

          const forms = referencedForms
            .map((photoRef) => {
              const matchingForm = currentForms.find(
                (form) =>
                  (photoRef.formId && form?.id === photoRef.formId) ||
                  (photoRef.formUrl && form?.url === photoRef.formUrl),
              );

              if (matchingForm) return matchingForm;

              /*
               * A normal referenced species exists, but this particular form
               * no longer does. The photo was deleted and must not be rebuilt
               * from the historical Community Feed snapshot.
               */
              if (speciesSnapshot.exists()) {
                return null;
              }

              /*
               * Preserve the fallback only for genuinely old pre-speciesId
               * activities that cannot be resolved against a live species
               * document.
               */
              if (
                !speciesSnapshot.id.startsWith("legacy_photo_") ||
                !photoRef.formUrl
              ) {
                return null;
              }

              return {
                id:
                  photoRef.formId ||
                  `legacy_${String(photoRef.formUrl).slice(-24)}`,

                url: photoRef.formUrl,

                label: photoRef.label || "",
                date: photoRef.date || null,

                location: photoRef.location || "",
                locationName: photoRef.locationName || "",
                locationLat: photoRef.locationLat ?? null,
                locationLng: photoRef.locationLng ?? null,
                locationCountry: photoRef.locationCountry || "",
                locationAdminArea: photoRef.locationAdminArea || "",
                locationCity: photoRef.locationCity || "",
                locationPlaceId: photoRef.locationPlaceId || "",

                variant: photoRef.variant || "",
                notes: photoRef.notes || "",

                collections: Array.isArray(photoRef.collections)
                  ? photoRef.collections
                  : [],
              };
            })
            .filter(Boolean);

          if (forms.length === 0) return null;

          const firstReference = referencedForms[0] || {};

          return {
            id: speciesSnapshot.id,
            ...speciesData,

            name:
              speciesData.name ||
              firstReference.scientificName ||
              firstReference.speciesName ||
              firstReference.name ||
              "Uploaded photo",

            common:
              speciesData.common ||
              firstReference.commonName ||
              firstReference.common ||
              "",

            ownerUid: activity.actorUid,
            forms,
          };
        })
        .filter(Boolean);

      setCommunityUploadSpecies(loadedSpecies);

      const uploadSlides = loadedSpecies.flatMap((species) =>
        (species.forms || []).map((form) => ({
          key: `${species.id}_${form.id || form.url}`,
          species: {
            ...species,
            forms: [form],
          },
          form,
        })),
      );

      setCommunityUploadSlides(uploadSlides);

      if (loadedSpecies.length === 0) {
        setCommunityUploadError(
          "No associated uploaded photos could be recovered.",
        );
      } else if (selectedPhotoRef) {
        const selectedSlideIndex = uploadSlides.findIndex(
          (slide) =>
            (selectedPhotoRef.formId &&
              slide.form?.id === selectedPhotoRef.formId) ||
            (selectedPhotoRef.formUrl &&
              slide.form?.url === selectedPhotoRef.formUrl),
        );

        if (selectedSlideIndex >= 0) {
          setCommunityUploadSlideIndex(selectedSlideIndex);
          setSelectedCommunitySpecies(uploadSlides[selectedSlideIndex].species);
          setCommunityUploadPhotoIndex(0);
          setOpenedCommunityUploadActivity(null);
        }
      }
    } catch (error) {
      console.error("Failed to load Community Feed uploads:", error);
      setCommunityUploadError("These uploaded photos could not be loaded.");
    } finally {
      setCommunityUploadLoading(false);
    }
  }

  function closeCommunityUploadActivity() {
    setOpenedCommunityUploadActivity(null);
    setCommunityUploadSpecies([]);
    setCommunityUploadError("");
    setCommunityUploadLoadedImages({});
    setCommunityUploadPreviewBySpecies({});
    setCommunityUploadSlides([]);
    setCommunityUploadSlideIndex(0);
  }

  function openCommunityUploadSlide(slideIndex) {
    const slide = communityUploadSlides[slideIndex];

    if (!slide) return;

    setCommunityUploadSlideIndex(slideIndex);
    setSelectedCommunitySpecies(slide.species);
    setCommunityUploadPhotoIndex(0);
    setOpenedCommunityUploadActivity(null);
  }

  function moveCommunityUploadSlide(direction) {
    const nextIndex = communityUploadSlideIndex + direction;

    if (nextIndex < 0 || nextIndex >= communityUploadSlides.length) return;

    openCommunityUploadSlide(nextIndex);
  }

  async function openPhotoKudosNotification(notification) {
    const currentUid = auth.currentUser?.uid;

    if (!currentUid || !notification) return;

    function timestampValueToDateKey(value) {
      const millis = timestampToMillis(value);

      if (!millis) return "";

      const date = new Date(millis);

      return [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0"),
      ].join("-");
    }

    function storedDateToDateKey(value) {
      if (!value) return "";

      if (typeof value === "string") {
        const directMatch = value.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);

        if (directMatch) {
          return [
            directMatch[1],
            String(directMatch[2]).padStart(2, "0"),
            String(directMatch[3]).padStart(2, "0"),
          ].join("-");
        }

        return "";
      }

      if (typeof value === "object" && value.year && value.month && value.day) {
        return [
          String(value.year),
          String(value.month).padStart(2, "0"),
          String(value.day).padStart(2, "0"),
        ].join("-");
      }

      return timestampValueToDateKey(value);
    }

    function formDateKey(form) {
      return (
        storedDateToDateKey(form?.date) ||
        timestampValueToDateKey(form?.uploadedAt) ||
        timestampValueToDateKey(form?.createdAt) ||
        timestampValueToDateKey(form?.addedAt)
      );
    }

    const activityKey = String(
      notification.activityKey ||
        notification.sourceActivityId ||
        notification.activityId ||
        "",
    ).trim();

    let sourceActivity =
      notification.activityData && typeof notification.activityData === "object"
        ? notification.activityData
        : notification.activity && typeof notification.activity === "object"
          ? notification.activity
          : {};

    let photoRefs = Array.isArray(notification.photoRefs)
      ? notification.photoRefs.filter((photoRef) => photoRef?.formUrl)
      : Array.isArray(sourceActivity.photoRefs)
        ? sourceActivity.photoRefs.filter((photoRef) => photoRef?.formUrl)
        : [];

    const activityDateMatch = activityKey.match(/_photos_(\d{4}-\d{2}-\d{2})$/);

    let uploadDateKey =
      String(
        notification.dateKey ||
          sourceActivity.dateKey ||
          activityDateMatch?.[1] ||
          "",
      ).trim() || timestampValueToDateKey(notification.createdAt);

    /*
     * First recovery path:
     * Search all activity identifiers and all plausible Community Feed owners.
     * The recipient's own Community Feed is checked first because copied friend
     * activities are normally stored under the recipient.
     */
    if (photoRefs.length === 0) {
      const possibleActivityIds = Array.from(
        new Set(
          [
            activityKey,
            notification.sourceActivityId,
            notification.activityId,
            notification.communityActivityId,
            sourceActivity.id,
          ]
            .map((value) => String(value || "").trim())
            .filter(Boolean),
        ),
      );

      const possibleFeedOwnerUids = Array.from(
        new Set(
          [
            currentUid,
            notification.recipientUid,
            notification.ownerUid,
            notification.actorUid,
            notification.senderUid,
            notification.fromUid,
          ]
            .map((value) => String(value || "").trim())
            .filter(Boolean),
        ),
      );

      try {
        let recoveredActivity = null;

        for (const feedOwnerUid of possibleFeedOwnerUids) {
          for (const possibleActivityId of possibleActivityIds) {
            const sourceActivitySnapshot = await getDoc(
              doc(
                db,
                "users",
                feedOwnerUid,
                "communityFeed",
                possibleActivityId,
              ),
            );

            if (!sourceActivitySnapshot.exists()) continue;

            const candidateActivity = {
              id: sourceActivitySnapshot.id,
              ...sourceActivitySnapshot.data(),
            };

            const candidatePhotoRefs = Array.isArray(
              candidateActivity.photoRefs,
            )
              ? candidateActivity.photoRefs.filter(
                  (photoRef) => photoRef?.formUrl,
                )
              : [];

            if (candidatePhotoRefs.length === 0) continue;

            recoveredActivity = candidateActivity;
            photoRefs = candidatePhotoRefs;
            break;
          }

          if (recoveredActivity) break;
        }

        if (recoveredActivity) {
          sourceActivity = recoveredActivity;

          uploadDateKey =
            String(recoveredActivity.dateKey || uploadDateKey || "").trim() ||
            uploadDateKey;
        }
      } catch (error) {
        console.error("Failed to recover the linked photo activity:", error);
      }
    }

    /*
     * Second recovery path:
     * Older kudos notifications may not have stored photoRefs at all. Rebuild
     * the photo list from every species form uploaded by the current user on
     * the notification's upload date.
     */
    if (photoRefs.length === 0 && uploadDateKey) {
      try {
        const speciesSnapshot = await getDocs(
          collection(db, "users", currentUid, "species"),
        );

        const reconstructedPhotoRefs = [];

        speciesSnapshot.docs.forEach((speciesDocument) => {
          const speciesData = speciesDocument.data();

          const forms = Array.isArray(speciesData.forms)
            ? speciesData.forms
            : [];

          forms.forEach((form, formIndex) => {
            if (!form?.url) return;
            if (formDateKey(form) !== uploadDateKey) return;

            reconstructedPhotoRefs.push({
              speciesId: speciesDocument.id,

              scientificName:
                speciesData.name ||
                speciesData.scientificName ||
                speciesDocument.id,

              commonName: speciesData.common || speciesData.commonName || "",

              formId: form.id || `${speciesDocument.id}_${formIndex}`,

              formUrl: form.url,

              label: form.label || "",
              date: form.date || null,

              location: form.location || "",
              locationName: form.locationName || "",
              locationLat: form.locationLat ?? null,
              locationLng: form.locationLng ?? null,
              locationCountry: form.locationCountry || "",
              locationAdminArea: form.locationAdminArea || "",
              locationCity: form.locationCity || "",
              locationPlaceId: form.locationPlaceId || "",

              variant: form.variant || "",
              notes: form.notes || "",

              collections: Array.isArray(form.collections)
                ? form.collections
                : [],
            });
          });
        });

        photoRefs = reconstructedPhotoRefs;
      } catch (error) {
        console.error(
          "Failed to reconstruct legacy kudos photos from the collection:",
          error,
        );
      }
    }

    await markNotificationRead(notification);

    await openCommunityUploadActivity({
      id:
        activityKey ||
        notification.sourceActivityId ||
        notification.activityId ||
        notification.id,

      type: "photos_uploaded",

      actorUid: currentUid,
      actorName: "",
      isOwnUpload: true,

      dateKey: uploadDateKey,

      uploadedPhotoCount: Number(
        notification.uploadedPhotoCount ??
          sourceActivity.uploadedPhotoCount ??
          photoRefs.length,
      ),

      uploadedSpeciesCount: Number(
        notification.uploadedSpeciesCount ??
          sourceActivity.uploadedSpeciesCount ??
          new Set(
            photoRefs.map((photoRef) => photoRef?.speciesId).filter(Boolean),
          ).size,
      ),

      photoRefs,
    });
  }

  async function markNotificationRead(notification) {
    if (!currentUid || !notification?.id || notification.read) return;

    // Taxonomic revisions are handled by their dedicated review and
    // acknowledgment flows. Opening a notification must not bypass those.
    if (["taxonomic_split", "taxonomic_change"].includes(notification.type))
      return;

    try {
      await updateDoc(
        doc(db, "users", currentUid, "notifications", notification.id),
        {
          read: true,
        },
      );

      setNotifications((previousNotifications) =>
        previousNotifications.map((currentNotification) =>
          currentNotification.id === notification.id
            ? { ...currentNotification, read: true }
            : currentNotification,
        ),
      );
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  }

  async function respondToFriendRequest(notification, response) {
    const user = auth.currentUser;

    if (
      !user ||
      !notification?.id ||
      !notification.friendRequestId ||
      !notification.actorUid ||
      blockedRelationshipUserIdSet.has(notification.actorUid) ||
      respondingToFriendRequestId
    ) {
      return;
    }

    const accepted = response === "accept";

    setRespondingToFriendRequestId(notification.id);
    setNotificationsError("");

    try {
      const requestRef = doc(
        db,
        "friendRequests",
        notification.friendRequestId,
      );

      const requestSnap = await getDoc(requestRef);

      if (!requestSnap.exists()) {
        await deleteDoc(
          doc(db, "users", user.uid, "notifications", notification.id),
        );

        setNotificationsError("This friend request is no longer available.");
        return;
      }

      const requestData = requestSnap.data();

      if (
        requestData.toUid !== user.uid ||
        requestData.fromUid !== notification.actorUid ||
        requestData.status !== "pending"
      ) {
        throw new Error("Friend request data does not match.");
      }

      if (accepted) {
        await Promise.all([
          setDoc(doc(db, "users", user.uid, "friends", notification.actorUid), {
            uid: notification.actorUid,
            requestId: notification.friendRequestId,
            createdAt: serverTimestamp(),
          }),
          setDoc(doc(db, "users", notification.actorUid, "friends", user.uid), {
            uid: user.uid,
            requestId: notification.friendRequestId,
            createdAt: serverTimestamp(),
          }),
        ]);
      }

      await deleteDoc(requestRef);

      if (accepted) {
        try {
          const profileSnap = await getDoc(doc(db, "profile", user.uid));

          const profileData = profileSnap.exists() ? profileSnap.data() : {};

          const actorName =
            String(
              profileData.name ||
                user.displayName ||
                user.email?.split("@")[0] ||
                "BioIndex User",
            ).trim() || "BioIndex User";

          const actorPhotoURL = String(profileData.photoURL || "").trim();

          await addDoc(
            collection(db, "users", notification.actorUid, "notifications"),
            {
              type: "friend_accepted",

              message: `${actorName} accepted your friend request`,
              detail: "You are now friends on BioIndex.",
              targetRoute: `/profile/${user.uid}`,
              read: false,

              createdAt: serverTimestamp(),

              actorUid: user.uid,
              actorName,
              actorPhotoURL,

              topicId: "",
              commentId: "",
              parentCommentId: "",

              titleId: "",
              genusName: "",

              friendRequestId: notification.friendRequestId,

              dedupeKey: `friend_accepted_${notification.friendRequestId}`,
            },
          );
        } catch (notificationError) {
          console.error(
            "Friendship created, but acceptance notification failed:",
            notificationError,
          );
        }
      }

      await deleteDoc(
        doc(db, "users", user.uid, "notifications", notification.id),
      );
    } catch (error) {
      console.error("Failed to respond to friend request:", error);

      setNotificationsError(
        accepted
          ? "The friend request could not be accepted."
          : "The friend request could not be denied.",
      );
    } finally {
      setRespondingToFriendRequestId("");
    }
  }

  async function equipTitleFromNotification() {
    const currentUid = auth.currentUser?.uid;
    const nextTitle = openedTitleDisplay.label;

    if (
      !currentUid ||
      !openedTitleNotification ||
      !nextTitle ||
      equippingNotificationTitle ||
      nextTitle === currentProfileTitle
    ) {
      return;
    }

    setEquippingNotificationTitle(true);
    setEquipNotificationTitleError("");

    try {
      const profileRef = doc(db, "profile", currentUid);
      const profileSnapshot = await getDoc(profileRef);
      const profileData = profileSnapshot.exists()
        ? profileSnapshot.data()
        : {};

      const now = Date.now();

      await setDoc(
        profileRef,
        {
          title: nextTitle,
          sectionUpdatedAt: {
            ...(profileData.sectionUpdatedAt || {}),
            profile: now,
          },
        },
        { merge: true },
      );

      setCurrentProfileTitle(nextTitle);
      setOpenedTitleNotification(null);
    } catch (error) {
      console.error("Failed to equip unlocked title:", error);
      setEquipNotificationTitleError(
        "The title could not be changed. Please try again.",
      );
    } finally {
      setEquippingNotificationTitle(false);
    }
  }

  async function markAllNotificationsRead() {
    if (!currentUid || markingNotificationsRead) return;

    setMarkingNotificationsRead(true);
    setNotificationsError("");

    try {
      const snapshot = await getDocs(
        collection(db, "users", currentUid, "notifications"),
      );

      const unreadDocuments = snapshot.docs.filter(
        (notificationDocument) =>
          !notificationDocument.data().read &&
          !["taxonomic_change", "taxonomic_split"].includes(
            notificationDocument.data().type,
          ),
      );

      for (let index = 0; index < unreadDocuments.length; index += 400) {
        const batch = writeBatch(db);
        const batchDocuments = unreadDocuments.slice(index, index + 400);

        batchDocuments.forEach((notificationDocument) => {
          batch.update(notificationDocument.ref, {
            read: true,
          });
        });

        await batch.commit();
      }

      setNotifications((previousNotifications) =>
        previousNotifications.map((notification) =>
          ["taxonomic_change", "taxonomic_split"].includes(notification.type)
            ? notification
            : { ...notification, read: true },
        ),
      );
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
      setNotificationsError("Notifications could not all be marked as read.");
    } finally {
      setMarkingNotificationsRead(false);
    }
  }

  const visibleTopics = useMemo(
    () =>
      sortForumTopics(
        topics.filter(
          (topic) =>
            !topic.deleted &&
            !topic.hidden &&
            (!topic.authorUid ||
              !blockedRelationshipUserIdSet.has(topic.authorUid)),
        ),
      ),
    [topics, blockedRelationshipUserIdSet],
  );

  const filteredTopics = useMemo(() => {
    const currentUid = auth.currentUser?.uid || "";
    const searchValue = topicSearch.trim().toLowerCase();

    const matchingTopics = visibleTopics.filter((topic) => {
      if (
        topicCategoryFilter !== "all" &&
        topic.category !== topicCategoryFilter
      ) {
        return false;
      }

      if (
        topicParticipationFilter === "started" &&
        topic.authorUid !== currentUid
      ) {
        return false;
      }

      if (
        topicParticipationFilter === "participated" &&
        !topic.currentUserParticipated
      ) {
        return false;
      }

      if (!searchValue) return true;

      const searchableValues = [
        topic.title,
        topic.body,
        topic.authorName,
        topic.category,
        topic.searchTitle,
        topic.searchBody,
        topic.searchAuthor,
        topic.searchCategory,
      ];

      return searchableValues.some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(searchValue),
      );
    });

    return [...matchingTopics].sort((a, b) => {
      if (!!a.pinned !== !!b.pinned) {
        return a.pinned ? -1 : 1;
      }

      if (topicSort === "oldest") {
        return timestampToMillis(a.createdAt) - timestampToMillis(b.createdAt);
      }

      if (topicSort === "most-replies") {
        const replyDifference =
          Number(b.commentCount || 0) - Number(a.commentCount || 0);

        if (replyDifference !== 0) return replyDifference;
      }

      if (topicSort === "newest") {
        return timestampToMillis(b.createdAt) - timestampToMillis(a.createdAt);
      }

      return (
        timestampToMillis(b.lastActivityAt || b.createdAt) -
        timestampToMillis(a.lastActivityAt || a.createdAt)
      );
    });
  }, [
    visibleTopics,
    topicSearch,
    topicCategoryFilter,
    topicParticipationFilter,
    topicSort,
  ]);

  const topicFiltersActive =
    !!topicSearch.trim() ||
    topicCategoryFilter !== "all" ||
    topicParticipationFilter !== "all" ||
    topicSort !== "recent";

  function clearTopicFilters() {
    setTopicSearch("");
    setTopicCategoryFilter("all");
    setTopicParticipationFilter("all");
    setTopicSort("recent");
  }

  function handleTopicDeleted(topicId) {
    setTopics((previousTopics) =>
      previousTopics.filter((topic) => topic.id !== topicId),
    );
    setTopicCreatedMessage("Your topic was deleted successfully.");
  }

  async function loadMoreTopics() {
    if (loadingMoreTopics || !hasMoreTopics || !topicCursorRef.current) {
      return;
    }

    setLoadingMoreTopics(true);
    setTopicsError("");

    try {
      const nextPageQuery = query(
        collection(db, "forumTopics"),
        orderBy("lastActivityAt", "desc"),
        startAfter(topicCursorRef.current),
        limit(TOPICS_PER_PAGE),
      );

      const snapshot = await getDocs(nextPageQuery);
      const hydratedTopics = await hydrateTopicDocuments(snapshot.docs);

      setTopics((previousTopics) => {
        const topicsById = new Map(
          previousTopics.map((topic) => [topic.id, topic]),
        );

        hydratedTopics.forEach((topic) => {
          topicsById.set(topic.id, topic);
        });

        return sortForumTopics([...topicsById.values()]);
      });

      hasLoadedAdditionalPagesRef.current = true;
      topicCursorRef.current =
        snapshot.docs[snapshot.docs.length - 1] || topicCursorRef.current;

      setHasMoreTopics(snapshot.docs.length === TOPICS_PER_PAGE);
    } catch (error) {
      console.error("Failed to load more forum topics:", error);
      setTopicsError("More forum topics could not be loaded.");
    } finally {
      setLoadingMoreTopics(false);
    }
  }

  async function loadMoreNotifications() {
    const currentUid = auth.currentUser?.uid;

    if (
      !currentUid ||
      notificationsLoadLockRef.current ||
      loadingMoreNotifications ||
      !hasMoreNotifications ||
      !notificationsCursorRef.current
    ) {
      return;
    }

    notificationsLoadLockRef.current = true;
    setLoadingMoreNotifications(true);
    setNotificationsError("");

    try {
      const nextPageQuery = query(
        collection(db, "users", currentUid, "notifications"),
        orderBy("createdAt", "desc"),
        startAfter(notificationsCursorRef.current),
        limit(NOTIFICATIONS_PER_PAGE),
      );

      const snapshot = await getDocs(nextPageQuery);

      const loadedNotifications = snapshot.docs.map((notificationDocument) => ({
        id: notificationDocument.id,
        ...notificationDocument.data(),
      }));

      setNotifications((previousNotifications) => {
        const notificationsById = new Map(
          previousNotifications.map((notification) => [
            notification.id,
            notification,
          ]),
        );

        loadedNotifications.forEach((notification) => {
          notificationsById.set(notification.id, notification);
        });

        return [...notificationsById.values()].sort(
          (a, b) =>
            timestampToMillis(b.createdAt) - timestampToMillis(a.createdAt),
        );
      });

      hasLoadedAdditionalNotificationsRef.current = true;
      notificationsCursorRef.current =
        snapshot.docs[snapshot.docs.length - 1] ||
        notificationsCursorRef.current;

      setHasMoreNotifications(snapshot.docs.length === NOTIFICATIONS_PER_PAGE);
    } catch (error) {
      console.error("Failed to load more notifications:", error);
      setNotificationsError("More notifications could not be loaded.");
    } finally {
      notificationsLoadLockRef.current = false;
      setLoadingMoreNotifications(false);
    }
  }

  async function loadMoreCommunityActivities() {
    const currentUid = auth.currentUser?.uid;

    if (
      !currentUid ||
      communityActivitiesLoadLockRef.current ||
      loadingMoreCommunityActivities ||
      !hasMoreCommunityActivities ||
      !communityActivitiesCursorRef.current
    ) {
      return;
    }

    communityActivitiesLoadLockRef.current = true;
    setLoadingMoreCommunityActivities(true);
    setCommunityActivitiesError("");

    try {
      const nextPageQuery = query(
        collection(db, "users", currentUid, "communityFeed"),
        orderBy("createdAt", "desc"),
        startAfter(communityActivitiesCursorRef.current),
        limit(COMMUNITY_ACTIVITIES_PER_PAGE),
      );

      const snapshot = await getDocs(nextPageQuery);

      const loadedActivities = snapshot.docs.map((activityDocument) => ({
        id: activityDocument.id,
        ownerUid: currentUid,
        ...activityDocument.data(),
      }));

      let reconciledActivities = loadedActivities;

      try {
        reconciledActivities =
          await reconcileLoadedCommunityPhotoActivities(loadedActivities);
      } catch (error) {
        console.error(
          "Failed to reconcile paginated Community Feed photo activities:",
          error,
        );
      }

      let completedGenusActivities = [];

      try {
        completedGenusActivities = await loadCompleteCommunityGenusGroups(
          currentUid,
          reconciledActivities,
        );
      } catch (error) {
        console.error(
          "Failed to complete paginated Community Feed genus groups:",
          error,
        );
      }

      const completedPageActivities = [
        ...reconciledActivities,
        ...completedGenusActivities,
      ];

      setCommunityActivities((previousActivities) => {
        const activitiesById = new Map(
          previousActivities.map((activity) => [activity.id, activity]),
        );

        completedPageActivities.forEach((activity) => {
          activitiesById.set(activity.id, activity);
        });

        return [...activitiesById.values()].sort(
          (a, b) => getCommunityActivityTime(b) - getCommunityActivityTime(a),
        );
      });

      hasLoadedAdditionalCommunityActivitiesRef.current = true;
      communityActivitiesCursorRef.current =
        snapshot.docs[snapshot.docs.length - 1] ||
        communityActivitiesCursorRef.current;

      setHasMoreCommunityActivities(
        snapshot.docs.length === COMMUNITY_ACTIVITIES_PER_PAGE,
      );
    } catch (error) {
      console.error("Failed to load more community activity:", error);
      setCommunityActivitiesError(
        "More community activity could not be loaded.",
      );
    } finally {
      communityActivitiesLoadLockRef.current = false;
      setLoadingMoreCommunityActivities(false);
    }
  }

  function resetNewTopicForm() {
    if (newTopicImage && newTopicImagePreview) {
      URL.revokeObjectURL(newTopicImagePreview);
    }

    setNewTopicTitle("");
    setNewTopicCategory(DEFAULT_FORUM_CATEGORY);
    setNewTopicBody("");
    setNewTopicImage(null);
    setNewTopicImagePreview("");
    setTopicImageDragging(false);
    setTopicPhotoPickerOpen(false);
    setTopicPhotoPickerLoading(false);
    setTopicPhotoPickerError("");
    setTopicPickerSelectedSpecies(null);
    setSelectedExistingTopicPhoto(null);
    setNewTopicError("");
  }

  function openNewTopicModal() {
    setTopicCreatedMessage("");
    setNewTopicError("");
    setNewTopicOpen(true);
  }

  async function openTopicPhotoPicker() {
    const currentUid = auth.currentUser?.uid;

    if (!currentUid || topicPhotoPickerLoading) return;

    setTopicPhotoPickerLoading(true);
    setTopicPhotoPickerError("");
    setTopicPickerSelectedSpecies(null);

    try {
      const speciesSnapshot = await getDocs(
        collection(db, "users", currentUid, "species"),
      );

      const uploadedSpecies = speciesSnapshot.docs
        .map((speciesDocument) => {
          const data = speciesDocument.data();
          const forms = Array.isArray(data.forms)
            ? data.forms.filter((form) => form?.url)
            : [];

          return {
            id: speciesDocument.id,
            ...data,
            forms,
          };
        })
        .filter((species) => species.forms.length > 0);

      setTopicSpeciesWithPhotos(uploadedSpecies);
      setTopicPhotoPickerOpen(true);
    } catch (error) {
      console.error("Failed to load uploaded topic photos:", error);

      setTopicPhotoPickerError(
        "Your uploaded photos could not be loaded. Please try again.",
      );
    } finally {
      setTopicPhotoPickerLoading(false);
    }
  }

  function selectExistingTopicPhoto(photoUrl) {
    if (!photoUrl) return;

    let selectedPhoto = null;

    for (const species of topicSpeciesWithPhotos) {
      const matchingForm = (species.forms || []).find(
        (form) => form.url === photoUrl,
      );

      if (!matchingForm) continue;

      selectedPhoto = {
        url: photoUrl,

        ownerUid: auth.currentUser?.uid || "",
        speciesId: species.id || "",
        formId: matchingForm.id || "",
        formUrl: matchingForm.url || photoUrl,

        scientificName: species.name || "",
        commonName: species.common || "",

        form: {
          id: matchingForm.id || "",
          label: matchingForm.label || "",
          date: matchingForm.date || null,

          location: matchingForm.location || "",
          locationName: matchingForm.locationName || "",
          locationLat: matchingForm.locationLat ?? null,
          locationLng: matchingForm.locationLng ?? null,
          locationCountry: matchingForm.locationCountry || "",
          locationAdminArea: matchingForm.locationAdminArea || "",
          locationCity: matchingForm.locationCity || "",
          locationPlaceId: matchingForm.locationPlaceId || "",

          variant: matchingForm.variant || "",
          notes: matchingForm.notes || "",
          collections: Array.isArray(matchingForm.collections)
            ? matchingForm.collections
            : [],
        },
      };

      break;
    }

    if (newTopicImage && newTopicImagePreview) {
      URL.revokeObjectURL(newTopicImagePreview);
    }

    setNewTopicImage(null);
    setNewTopicImagePreview(photoUrl);
    setSelectedExistingTopicPhoto(
      selectedPhoto || {
        url: photoUrl,
        ownerUid: auth.currentUser?.uid || "",
        speciesId: "",
        formId: "",
        formUrl: photoUrl,
        scientificName: "",
        commonName: "",
        form: null,
      },
    );
    setTopicImageDragging(false);
    setTopicPickerSelectedSpecies(null);
    setTopicPhotoPickerOpen(false);
    setNewTopicError("");
  }

  function closeTopicPhotoPicker() {
    setTopicPhotoPickerOpen(false);
    setTopicPickerSelectedSpecies(null);
  }

  function closeNewTopicModal() {
    if (creatingTopic) return;

    resetNewTopicForm();
    setNewTopicOpen(false);
  }

  function selectTopicImage(file) {
    setNewTopicError("");

    if (newTopicImagePreview) {
      URL.revokeObjectURL(newTopicImagePreview);
    }

    if (!file) {
      setNewTopicImage(null);
      setNewTopicImagePreview("");
      return;
    }

    if (!file.type.startsWith("image/")) {
      setNewTopicImage(null);
      setNewTopicImagePreview("");
      setNewTopicError("Please select a valid image file.");
      return;
    }

    if (file.size > MAX_TOPIC_IMAGE_SIZE) {
      setNewTopicImage(null);
      setNewTopicImagePreview("");
      setNewTopicError("Topic images must be 8 MB or smaller.");
      return;
    }

    setSelectedExistingTopicPhoto(null);
    setNewTopicImage(file);
    setNewTopicImagePreview(URL.createObjectURL(file));
  }

  function handleTopicImageChange(event) {
    const file = event.target.files?.[0] || null;

    selectTopicImage(file);

    event.target.value = "";
  }

  function handleTopicImageDragEnter(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!creatingTopic) {
      setTopicImageDragging(true);
    }
  }

  function handleTopicImageDragOver(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!creatingTopic) {
      event.dataTransfer.dropEffect = "copy";
      setTopicImageDragging(true);
    }
  }

  function handleTopicImageDragLeave(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!event.currentTarget.contains(event.relatedTarget)) {
      setTopicImageDragging(false);
    }
  }

  function handleTopicImageDrop(event) {
    event.preventDefault();
    event.stopPropagation();

    setTopicImageDragging(false);

    if (creatingTopic) return;

    const file = event.dataTransfer.files?.[0] || null;

    selectTopicImage(file);
  }

  function removeTopicImage() {
    if (newTopicImage && newTopicImagePreview) {
      URL.revokeObjectURL(newTopicImagePreview);
    }

    setNewTopicImage(null);
    setNewTopicImagePreview("");
    setSelectedExistingTopicPhoto(null);
    setTopicImageDragging(false);
    setNewTopicError("");
  }

  async function createTopic() {
    if (creatingTopic) return;

    const user = auth.currentUser;
    const title = newTopicTitle.trim();
    const body = newTopicBody.trim();
    const category = newTopicCategory.trim();

    setNewTopicError("");
    setTopicCreatedMessage("");

    if (!user) {
      setNewTopicError("You must be logged in to create a topic.");
      return;
    }

    if (title.length < 3) {
      setNewTopicError("The topic title must contain at least 3 characters.");
      return;
    }

    if (title.length > MAX_TOPIC_TITLE_LENGTH) {
      setNewTopicError(
        `The topic title cannot exceed ${MAX_TOPIC_TITLE_LENGTH} characters.`,
      );
      return;
    }

    if (!body) {
      setNewTopicError("Please add a message to your topic.");
      return;
    }

    if (body.length > MAX_TOPIC_BODY_LENGTH) {
      setNewTopicError(
        `The topic message cannot exceed ${MAX_TOPIC_BODY_LENGTH.toLocaleString()} characters.`,
      );
      return;
    }

    if (!isValidForumCategory(category)) {
      setNewTopicError("Please select a valid topic category.");
      return;
    }

    if (
      newTopicImage &&
      (!newTopicImage.type.startsWith("image/") ||
        newTopicImage.size > MAX_TOPIC_IMAGE_SIZE)
    ) {
      setNewTopicError("Please select a valid image that is 8 MB or smaller.");
      return;
    }

    setCreatingTopic(true);

    let uploadedImageStoragePath = "";

    try {
      const profileSnap = await getDoc(doc(db, "profile", user.uid));
      const profileData = profileSnap.exists() ? profileSnap.data() : {};

      const authorName =
        String(
          profileData.name ||
            user.displayName ||
            user.email?.split("@")[0] ||
            "BioIndex User",
        ).trim() || "BioIndex User";

      const authorPhotoURL = String(profileData.photoURL || "").trim();

      const topicRef = doc(collection(db, "forumTopics"));

      let imageURL = selectedExistingTopicPhoto?.url || "";

      if (newTopicImage) {
        const safeFileName = newTopicImage.name.replace(/[^\w.\- ()]/g, "_");

        uploadedImageStoragePath = `forumTopics/${topicRef.id}/${Date.now()}_${safeFileName}`;

        const imageRef = ref(storage, uploadedImageStoragePath);

        await uploadBytes(imageRef, newTopicImage, {
          contentType: newTopicImage.type,
          customMetadata: {
            ownerUid: user.uid,
          },
        });

        imageURL = await getDownloadURL(imageRef);
      }

      await setDoc(topicRef, {
        title,
        body,
        category,

        authorUid: user.uid,
        authorName,
        authorPhotoURL,

        imageURL,
        imageStoragePath: uploadedImageStoragePath,
        imageSource: selectedExistingTopicPhoto ? "collection" : "upload",

        speciesPhotoRef: selectedExistingTopicPhoto
          ? {
              ownerUid: selectedExistingTopicPhoto.ownerUid || user.uid,
              speciesId: selectedExistingTopicPhoto.speciesId || "",
              formId: selectedExistingTopicPhoto.formId || "",
              formUrl:
                selectedExistingTopicPhoto.formUrl ||
                selectedExistingTopicPhoto.url ||
                "",
            }
          : null,

        speciesPhoto: selectedExistingTopicPhoto
          ? {
              speciesId: selectedExistingTopicPhoto.speciesId || "",
              scientificName: selectedExistingTopicPhoto.scientificName || "",
              commonName: selectedExistingTopicPhoto.commonName || "",
              form: selectedExistingTopicPhoto.form || null,
            }
          : null,

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        editedAt: null,
        lastActivityAt: serverTimestamp(),

        commentCount: 0,
        participantCount: 1,

        pinned: false,
        locked: false,
        hidden: false,
        deleted: false,

        searchTitle: title.toLowerCase(),
        searchBody: body.toLowerCase(),
        searchAuthor: authorName.toLowerCase(),
        searchCategory: category.toLowerCase(),
      });

      resetNewTopicForm();
      setNewTopicOpen(false);
      setActiveView("topics");
      setTopicCreatedMessage("Your topic was published successfully.");
    } catch (error) {
      console.error("Failed to create forum topic:", error);

      if (uploadedImageStoragePath) {
        try {
          await deleteObject(ref(storage, uploadedImageStoragePath));
        } catch (cleanupError) {
          console.error("Failed to remove orphaned forum image:", cleanupError);
        }
      }

      setNewTopicError("The topic could not be published. Please try again.");
    } finally {
      setCreatingTopic(false);
    }
  }

  return (
    <div className="bioindex-community-root min-h-screen bg-[#374049] px-2 pb-8 sm:px-5 lg:px-7">
      {/* PAGE NAVIGATION: full-width, responsive, and shared palette */}
      <div className="bioindex-v7-desktop-nav relative z-40 mx-auto w-full max-w-[1640px] py-3">
        <div className="flex w-full items-center gap-3 rounded-[1.6rem] border border-[#93CA87]/35 bg-[#FCFFF6] px-3 py-3 shadow-[0_16px_40px_rgba(15,36,40,0.18),inset_0_1px_0_rgba(255,255,255,0.72)] sm:px-4 lg:px-6">
          <Link
            to="/"
            aria-label="BioIndex Home"
            className="hidden shrink-0 items-center gap-3 rounded-2xl px-2.5 py-2 text-[#374049] transition hover:bg-[#93CA87]/10 lg:flex"
          >
            <img
              src="/favicon.ico"
              alt=""
              className="h-11 w-11 shrink-0 object-contain drop-shadow-[0_4px_10px_rgba(1,118,122,0.16)]"
            />
            <span className="flex flex-col leading-none">
              <span
                className="text-[29px] text-[#374049]"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                BioIndex
              </span>
              <span className="mt-1 text-[10px] font-semibold uppercase tracking-[0.28em] text-[#01767A]/70">
                Community
              </span>
            </span>
          </Link>
          <span
            className="hidden h-7 w-px bg-[#374049]/10 lg:block"
            aria-hidden="true"
          />
          <nav
            aria-label="BioIndex navigation"
            className="grid min-w-0 flex-1 grid-cols-6 items-center gap-1 rounded-2xl bg-[#374049]/[0.03] p-1.5 sm:flex sm:justify-end sm:bg-transparent sm:p-0"
          >
            <Link
              to="/"
              aria-label="Home"
              className="group flex min-w-0 items-center justify-center rounded-2xl px-1 py-2.5 text-sm font-medium text-[#374049]/80 transition-all duration-200 hover:bg-[#24C3BE]/10 hover:text-[#01767A] hover:shadow-[inset_0_0_0_1px_rgba(36,195,190,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#24C3BE]/60 sm:px-3 lg:px-4"
            >
              <img
                src="/HomeIcon.png"
                alt=""
                aria-hidden="true"
                className="h-7 w-7 object-contain sm:hidden"
              />

              <span className="hidden sm:inline">Home</span>
            </Link>

            <Link
              to="/explore"
              aria-label="Explore Taxonomy"
              className="group flex min-w-0 items-center justify-center rounded-2xl px-1 py-2.5 text-sm font-medium text-[#374049]/80 transition-all duration-200 hover:bg-[#24C3BE]/10 hover:text-[#01767A] hover:shadow-[inset_0_0_0_1px_rgba(36,195,190,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#24C3BE]/60 sm:px-3 lg:px-4"
            >
              <img
                src="/ExploreIcon.png"
                alt=""
                aria-hidden="true"
                className="h-7 w-7 object-contain sm:hidden"
              />

              <span className="hidden sm:inline">Explore Taxonomy</span>
            </Link>

            <Link
              to="/upload"
              aria-label="Add Photos"
              className="group flex min-w-0 items-center justify-center rounded-2xl px-1 py-2.5 text-sm font-medium text-[#374049]/80 transition-all duration-200 hover:bg-[#24C3BE]/10 hover:text-[#01767A] hover:shadow-[inset_0_0_0_1px_rgba(36,195,190,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#24C3BE]/60 sm:px-3 lg:px-4"
            >
              <img
                src="/PhotosIcon.png"
                alt=""
                aria-hidden="true"
                className="h-7 w-7 object-contain sm:hidden"
              />

              <span className="hidden sm:inline">Add Photos</span>
            </Link>

            <Link
              to="/profile"
              aria-label="Profile"
              className="group flex min-w-0 items-center justify-center rounded-2xl px-1 py-2.5 text-sm font-medium text-[#374049]/80 transition-all duration-200 hover:bg-[#24C3BE]/10 hover:text-[#01767A] hover:shadow-[inset_0_0_0_1px_rgba(36,195,190,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#24C3BE]/60 sm:px-3 lg:px-4"
            >
              <img
                src="/ProfileIcon.png"
                alt=""
                aria-hidden="true"
                className="h-7 w-7 object-contain sm:hidden"
              />

              <span className="hidden sm:inline">Profile</span>
            </Link>

            <div
              aria-label="Community"
              aria-current="page"
              className="relative flex min-w-0 cursor-default items-center justify-center rounded-2xl bg-[#24C3BE]/15 px-2 py-2.5 text-sm font-semibold text-[#01767A] ring-1 ring-[#24C3BE]/25 sm:bg-[#24C3BE] sm:px-5 sm:text-white sm:shadow-[0_8px_18px_rgba(1,118,122,0.18)] sm:ring-0"
            >
              <img
                src="/CommIcon.png"
                alt=""
                aria-hidden="true"
                className="h-7 w-7 object-contain sm:hidden"
              />

              <span className="hidden sm:inline">Community</span>
              <CommunityNavBadge />
            </div>

            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              className="group flex min-w-0 items-center justify-center rounded-2xl px-1 py-2.5 text-sm font-medium text-[#374049]/80 transition-all duration-200 hover:bg-[#24C3BE]/10 hover:text-[#01767A] hover:shadow-[inset_0_0_0_1px_rgba(36,195,190,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#24C3BE]/60 sm:px-3 lg:px-4"
            >
              <img
                src="/Search.png"
                alt=""
                aria-hidden="true"
                className="h-7 w-7 object-contain sm:hidden"
              />

              <span className="hidden sm:inline">Search</span>
            </button>
          </nav>
        </div>
      </div>

      <div className="bioindex-community-container mx-auto w-full max-w-[1640px]">
        <div className="w-full">
          {/* MAIN COMMUNITY SHELL */}
          <div className="bioindex-community-shell overflow-hidden rounded-[1.6rem] border border-[#93CA87]/35 bg-[#FCFFF6] shadow-[0_20px_65px_rgba(10,31,30,0.19)] sm:rounded-[2rem]">
            <header className="bioindex-community-header relative z-10 overflow-hidden border-b border-[#93CA87]/35 bg-[#FCFFF6] px-4 pb-7 pt-8 text-center sm:px-7 sm:pb-0 sm:pt-0 sm:text-left">
              <nav
                className="bioindex-m-section-tabs"
                aria-label="Community sections (mobile)"
              >
                {tabs.map((tab) => (
                  <button
                    key={`mobile_${tab.key}`}
                    type="button"
                    className={`bioindex-m-section-tab ${activeView === tab.key ? "bioindex-m-selected" : ""}`}
                    aria-label={tab.label}
                    aria-current={activeView === tab.key ? "page" : undefined}
                    onClick={() => {
                      setActiveView(tab.key);
                      if (tab.key === "community") markCommunityFeedSeen();
                      if (tab.key === "rankings") markRankingsSeen();
                    }}
                  >
                    <span className="bioindex-m-tab-icon-holder">
                      <BioIndexMobileTabIcon section={tab.key} />
                      {tab.badge > 0 && (
                        <span className="bioindex-m-tab-badge">
                          {tab.badge > 99 ? "99+" : tab.badge}
                        </span>
                      )}
                    </span>
                    <span className="bioindex-m-tab-label">{tab.label}</span>
                  </button>
                ))}
              </nav>
              <nav
                className="bioindex-v7-desktop-tabs relative flex w-full items-center justify-start gap-5 overflow-x-auto bg-transparent"
                aria-label="Community sections"
              >
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    aria-label={tab.label}
                    onClick={() => {
                      setActiveView(tab.key);

                      if (tab.key === "community") {
                        markCommunityFeedSeen();
                      }
                      if (tab.key === "rankings") {
                        markRankingsSeen();
                      }
                    }}
                    className={`group relative flex shrink-0 items-center justify-center gap-1 border-b-2 px-1 py-3 text-xs font-semibold uppercase tracking-[0.07em] transition ${
                      activeView === tab.key
                        ? "border-[#01767A] text-[#01767A]"
                        : "border-transparent text-[#374049]/65 hover:border-[#93CA87] hover:text-[#01767A]"
                    }`}
                  >
                    {tab.key === "rankings" ? (
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                        className="h-7 w-7 text-[#24C3BE] sm:hidden"
                      >
                        <path d="M8 4h8v4a4 4 0 0 1-8 0V4Z" />
                        <path d="M8 6H5v1a4 4 0 0 0 4 4" />
                        <path d="M16 6h3v1a4 4 0 0 1-4 4" />
                        <path d="M12 12v4M9 20h6M10 16h4v4h-4Z" />
                      </svg>
                    ) : (
                      <img
                        src={tab.icon}
                        alt=""
                        aria-hidden="true"
                        className={`h-8 w-8 object-contain sm:hidden ${
                          activeView === tab.key ? "brightness-0 invert" : ""
                        }`}
                      />
                    )}

                    <span className="hidden truncate sm:inline">
                      {tab.label}
                    </span>

                    {tab.badge > 0 && (
                      <span
                        className={`absolute right-2 top-1.5 inline-flex h-6 items-center justify-center rounded-full border-2 border-white bg-[#C5537F] text-[10px] font-bold leading-none tracking-normal text-white shadow-[0_3px_10px_rgba(197,83,127,0.28)] sm:static sm:ml-1 sm:shrink-0 ${tab.badge > 99 ? "min-w-6 px-1.5" : "w-6"}`}
                      >
                        {tab.badge > 99 ? "99+" : tab.badge}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </header>

            <main className="bioindex-community-main min-h-[560px] bg-[#FCFFF6] px-3 py-6 sm:px-7 sm:py-5 lg:px-10 xl:px-12">
              {/* TOPICS VIEW */}
              {activeView === "topics" && (
                <div className="bioindex-m-forum-section">
                  <div className="mb-4 flex flex-wrap items-end justify-between gap-4 border-b border-[#93CA87]/35 pb-4">
                    <div>
                      <h2
                        className="text-3xl leading-none text-[#374049]"
                        style={{ fontFamily: "Cormorant Garamond" }}
                      >
                        Forum
                      </h2>
                    </div>
                    <button
                      type="button"
                      onClick={openNewTopicModal}
                      className="hidden items-center gap-2 rounded-xl bg-[#01767A] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#05676A] hover:shadow-md sm:inline-flex"
                    >
                      <span aria-hidden="true" className="text-lg leading-none">
                        +
                      </span>
                      Start a New Topic
                    </button>
                  </div>

                  <div
                    className="bioindex-m-forum-toolbar sm:hidden"
                    role="group"
                    aria-label="Forum actions"
                  >
                    <button
                      type="button"
                      onClick={() => setForumSearchOpen((open) => !open)}
                      aria-expanded={forumSearchOpen}
                      aria-controls="bioindex-forum-search"
                      className={`bioindex-m-forum-tool ${forumSearchOpen || topicSearch.trim() ? "is-active" : ""}`}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        aria-hidden="true"
                      >
                        <circle cx="10.8" cy="10.8" r="6.5" />
                        <path d="m16 16 5 5" />
                      </svg>
                      <span>Search</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setForumFiltersOpen((open) => !open)}
                      aria-expanded={forumFiltersOpen}
                      aria-controls="bioindex-forum-filters"
                      aria-label="Sort and filter forum topics"
                      className={`bioindex-m-forum-tool ${forumFiltersOpen || topicCategoryFilter !== "all" || topicParticipationFilter !== "all" || topicSort !== "recent" ? "is-active" : ""}`}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        aria-hidden="true"
                      >
                        <path d="M4 7h16M7 12h10M10 17h4" />
                      </svg>
                      <span>Sort &amp; Filter</span>
                    </button>
                    <button
                      type="button"
                      onClick={openNewTopicModal}
                      className="bioindex-m-forum-tool is-primary"
                      aria-label="Start a new topic"
                    >
                      <span
                        className="bioindex-m-forum-plus"
                        aria-hidden="true"
                      >
                        +
                      </span>
                      <span>New Topic</span>
                    </button>
                  </div>
                  {topicFiltersActive && (
                    <div className="bioindex-m-forum-clear sm:hidden">
                      <button type="button" onClick={clearTopicFilters}>
                        Clear filters
                      </button>
                    </div>
                  )}

                  {topicCreatedMessage && (
                    <div className="mb-5 rounded-2xl border border-[#93CA87]/50 bg-[#FDFBF8] px-4 py-3 text-sm text-[#374049]">
                      {topicCreatedMessage}
                    </div>
                  )}

                  <div className="mb-5">
                    <div className="hidden flex-wrap items-center gap-2 sm:flex">
                      <button
                        type="button"
                        onClick={() => setForumSearchOpen((open) => !open)}
                        aria-expanded={forumSearchOpen}
                        aria-controls="bioindex-forum-search"
                        className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition ${
                          forumSearchOpen || topicSearch.trim()
                            ? "border-[#01767A]/40 bg-[#01767A]/5 text-[#01767A]"
                            : "border-[#374049]/15 bg-white text-[#374049]/75 hover:border-[#24C3BE]/60"
                        }`}
                      >
                        <svg
                          className="h-4 w-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <circle cx="10.8" cy="10.8" r="6.5" />
                          <path d="m16 16 5 5" />
                        </svg>
                        Search
                      </button>
                      <button
                        type="button"
                        onClick={() => setForumFiltersOpen((open) => !open)}
                        aria-expanded={forumFiltersOpen}
                        aria-controls="bioindex-forum-filters"
                        className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition ${
                          forumFiltersOpen ||
                          topicCategoryFilter !== "all" ||
                          topicParticipationFilter !== "all" ||
                          topicSort !== "recent"
                            ? "border-[#01767A]/40 bg-[#01767A]/5 text-[#01767A]"
                            : "border-[#374049]/15 bg-white text-[#374049]/75 hover:border-[#24C3BE]/60"
                        }`}
                      >
                        <svg
                          className="h-4 w-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          aria-hidden="true"
                        >
                          <path d="M4 7h16M7 12h10M10 17h4" />
                        </svg>
                        Sort & filter
                      </button>
                      {topicFiltersActive && (
                        <button
                          type="button"
                          onClick={clearTopicFilters}
                          className="rounded-lg px-2 py-2 text-xs font-medium text-[#01767A] hover:underline"
                        >
                          Clear filters
                        </button>
                      )}
                    </div>

                    {forumSearchOpen && (
                      <div
                        id="bioindex-forum-search"
                        className="mt-3 rounded-xl border border-[#374049]/10 bg-white p-3"
                      >
                        <label
                          htmlFor="forum-topic-search"
                          className="mb-1.5 block text-xs font-semibold text-[#374049]"
                        >
                          Search discussions
                        </label>
                        <input
                          id="forum-topic-search"
                          type="search"
                          autoFocus
                          value={topicSearch}
                          onChange={(event) =>
                            setTopicSearch(event.target.value)
                          }
                          placeholder="Title, author, or keyword"
                          className="h-10 w-full max-w-xl rounded-lg border border-[#93CA87]/50 bg-white px-3 text-sm text-[#374049] outline-none placeholder:text-slate-400 focus:border-[#24C3BE] focus:ring-2 focus:ring-[#24C3BE]/20"
                        />
                      </div>
                    )}

                    {forumFiltersOpen && (
                      <div
                        id="bioindex-forum-filters"
                        className="mt-3 grid gap-3 rounded-xl border border-[#374049]/10 bg-white p-3 sm:grid-cols-3"
                      >
                        <div>
                          <label
                            htmlFor="forum-topic-sort"
                            className="mb-1 block text-xs font-semibold text-[#374049]"
                          >
                            Sort by
                          </label>
                          <select
                            id="forum-topic-sort"
                            value={topicSort}
                            onChange={(event) =>
                              setTopicSort(event.target.value)
                            }
                            className="h-10 w-full rounded-lg border border-[#93CA87]/50 bg-white px-3 text-sm text-[#374049] focus:border-[#24C3BE]"
                          >
                            <option value="recent">Recently active</option>
                            <option value="newest">Newest topics</option>
                            <option value="oldest">Oldest topics</option>
                            <option value="most-replies">Most replies</option>
                          </select>
                        </div>
                        <div>
                          <label
                            htmlFor="forum-category-filter"
                            className="mb-1 block text-xs font-semibold text-[#374049]"
                          >
                            Category
                          </label>
                          <select
                            id="forum-category-filter"
                            value={topicCategoryFilter}
                            onChange={(event) =>
                              setTopicCategoryFilter(event.target.value)
                            }
                            className="h-10 w-full rounded-lg border border-[#93CA87]/50 bg-white px-3 text-sm text-[#374049] focus:border-[#24C3BE]"
                          >
                            <option value="all">All categories</option>
                            {FORUM_CATEGORIES.map((category) => (
                              <option key={category} value={category}>
                                {category}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label
                            htmlFor="forum-participation-filter"
                            className="mb-1 block text-xs font-semibold text-[#374049]"
                          >
                            Participation
                          </label>
                          <select
                            id="forum-participation-filter"
                            value={topicParticipationFilter}
                            onChange={(event) =>
                              setTopicParticipationFilter(event.target.value)
                            }
                            className="h-10 w-full rounded-lg border border-[#93CA87]/50 bg-white px-3 text-sm text-[#374049] focus:border-[#24C3BE]"
                          >
                            <option value="all">All topics</option>
                            <option value="started">Started by me</option>
                            <option value="participated">
                              Participated in
                            </option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>

                  {topicsError && (
                    <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {topicsError}
                    </div>
                  )}

                  {topicsLoading || blockRelationshipsLoading ? (
                    <div className="flex min-h-64 flex-col items-center justify-center text-center">
                      <div className="h-9 w-9 animate-spin rounded-full border-4 border-[#93CA87]/40 border-t-[#24C3BE]" />

                      <div className="mt-3 text-sm text-slate-500">
                        Loading discussions...
                      </div>
                    </div>
                  ) : filteredTopics.length > 0 ? (
                    <>
                      <div className="flex flex-col gap-4">
                        {filteredTopics.map((topic) => (
                          <ForumTopicCard
                            key={topic.id}
                            topic={topic}
                            currentUid={currentUid}
                            onTopicDeleted={handleTopicDeleted}
                            onRequestProfile={requestCommunityProfile}
                            blockedRelationshipUserIdSet={
                              blockedRelationshipUserIdSet
                            }
                          />
                        ))}
                      </div>

                      {hasMoreTopics && !topicFiltersActive && (
                        <div className="mt-6 flex justify-center">
                          <button
                            type="button"
                            onClick={loadMoreTopics}
                            disabled={loadingMoreTopics}
                            className={`rounded-xl border px-5 py-2.5 text-sm font-medium transition ${
                              loadingMoreTopics
                                ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                                : "border-[#93CA87]/40 bg-white text-[#374049] hover:border-[#24C3BE]/60 hover:bg-[#24C3BE]/10"
                            }`}
                          >
                            {loadingMoreTopics
                              ? "Loading..."
                              : "Load More Topics"}
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="rounded-3xl border border-dashed border-[#93CA87]/60 bg-[#FDFBF8] px-5 py-16 text-center">
                      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-[#93CA87]/50 bg-white text-2xl text-[#24C3BE] shadow-sm">
                        ✦
                      </div>

                      <h3
                        className="mt-4 text-3xl text-[#374049]"
                        style={{ fontFamily: "Cormorant Garamond" }}
                      >
                        {topicFiltersActive
                          ? "No matching topics"
                          : "No topics yet"}
                      </h3>

                      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-500">
                        {topicFiltersActive
                          ? "No discussions match the selected search and filters."
                          : "Start the first discussion and help shape the BioIndex community."}
                      </p>

                      {topicFiltersActive ? (
                        <button
                          type="button"
                          onClick={clearTopicFilters}
                          className="mt-5 rounded-xl border border-[#93CA87]/50 bg-white px-5 py-2.5 text-sm font-medium text-[#374049] transition hover:border-[#24C3BE]/60 hover:bg-[#24C3BE]/10"
                        >
                          Clear Filters
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={openNewTopicModal}
                          className="self-start rounded-xl bg-[#01767A] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#24C3BE] hover:shadow-md sm:self-auto"
                        >
                          Start a New Topic
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* NOTIFICATIONS VIEW */}
              {activeView === "notifications" && (
                <div className="bioindex-m-personal-section mx-auto w-full max-w-[1350px]">
                  <div className="mb-4 flex flex-col gap-4 border-b border-[#93CA87]/30 pb-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h2
                        className="text-3xl text-[#374049]"
                        style={{ fontFamily: "Cormorant Garamond" }}
                      >
                        My Activity
                      </h2>
                    </div>

                    {visibleNotifications.some(
                      (notification) =>
                        !notification.read &&
                        !["taxonomic_change", "taxonomic_split"].includes(
                          notification.type,
                        ),
                    ) && (
                      <button
                        type="button"
                        onClick={markAllNotificationsRead}
                        disabled={markingNotificationsRead}
                        className={`rounded-xl border px-4 py-2.5 text-xs font-medium transition ${
                          markingNotificationsRead
                            ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                            : "border-[#374049]/15 bg-white text-[#374049]/75 hover:border-[#374049]/30 hover:bg-[#374049]/5 hover:text-[#374049]"
                        }`}
                      >
                        {markingNotificationsRead ? (
                          "Marking as read..."
                        ) : (
                          <>
                            <span className="bioindex-m-desktop-label">
                              Mark all as read
                            </span>
                            <span className="bioindex-m-short-label">
                              Read all
                            </span>
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {notificationsError && (
                    <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {notificationsError}
                    </div>
                  )}

                  {notificationsLoading || blockRelationshipsLoading ? (
                    <div className="flex min-h-64 flex-col items-center justify-center text-center">
                      <div className="h-9 w-9 animate-spin rounded-full border-4 border-[#93CA87]/40 border-t-[#24C3BE]" />

                      <div className="mt-3 text-sm text-slate-500">
                        Loading your activity...
                      </div>
                    </div>
                  ) : visibleNotifications.length > 0 ? (
                    <div className="space-y-3">
                      {notificationItems.map((item, index) => {
                        if (item.kind === "personal_day") {
                          return (
                            <PersonalDailyActivityCard
                              key={item.id}
                              item={item}
                              onRead={markAchievementNotificationsRead}
                              onOpenUploads={openCommunityUploadActivity}
                              onOpenPhoto={(activity, photoRef) =>
                                openCommunityUploadActivity(activity, photoRef)
                              }
                              onGenus={(entry) =>
                                setOpenedGenusNotification(entry)
                              }
                              onTitle={(entry) =>
                                setOpenedTitleNotification(entry)
                              }
                            />
                          );
                        }
                        if (item.kind === "ranking_group") {
                          return (
                            <CombinedPersonalNotification
                              key={item.id}
                              item={item}
                              onRead={markAchievementNotificationsRead}
                              onGenus={(notification) => {
                                markNotificationRead(notification);
                                setOpenedGenusNotification(notification);
                              }}
                              onTitle={(notification) => {
                                markNotificationRead(notification);
                                setOpenedTitleNotification(notification);
                              }}
                              onRankings={showRankingUpdates}
                            />
                          );
                        }
                        if (item.kind === "genus_group") {
                          const groupIsRead = item.notifications.every(
                            (notification) => notification.read,
                          );

                          const groupRowClassName = `relative block w-full overflow-hidden rounded-2xl border p-4 text-left shadow-sm transition hover:-translate-y-[1px] hover:shadow-md sm:p-5 ${
                            groupIsRead
                              ? "border-[#374049]/15 bg-white"
                              : "border-[#374049]/25 bg-white"
                          }`;

                          return (
                            <GenusNotificationGroup
                              key={item.id}
                              notifications={item.notifications}
                              className={groupRowClassName}
                              onOpenNotification={(notification) => {
                                markNotificationRead(notification);
                                setOpenedGenusNotification(notification);
                              }}
                            />
                          );
                        }

                        const notification = item.notification;

                        const actorInitial =
                          String(notification.actorName || "B")
                            .trim()
                            .charAt(0)
                            .toUpperCase() || "B";

                        const isFriendRequest =
                          notification.type === "friend_request";

                        const isTaxonomicNotice =
                          notification.type === "taxonomic_change" ||
                          notification.type === "taxonomic_split";

                        const isFriendshipAccepted =
                          notification.type === "friend_accepted";

                        const isGenusUnlock =
                          notification.type === "genus_unlocked";

                        const isTitleUnlock =
                          notification.type === "title_unlocked";

                        const currentNotificationTitle =
                          getCurrentTitleDisplay(notification);

                        const isPhotoKudosNotification =
                          notification.type === "kudos_received" &&
                          notification.activityType === "photos_uploaded";

                        const respondingToThisRequest =
                          respondingToFriendRequestId === notification.id;

                        const notificationIcon =
                          notification.type === "comment_reply"
                            ? "↳"
                            : notification.type === "topic_comment"
                              ? "◌"
                              : notification.type === "friend_request"
                                ? "●"
                                : notification.type === "friend_accepted"
                                  ? "✓"
                                  : notification.type === "title_unlocked"
                                    ? "🏆"
                                    : notification.type === "genus_unlocked"
                                      ? "❋"
                                      : "◇";

                        const notificationContent = (
                          <div className="flex items-start gap-4">
                            {notification.type === "genus_unlocked" ? (
                              <div className="flex h-[72px] w-[62px] shrink-0 items-center justify-center overflow-hidden rounded-md border border-[#374049]/30 bg-white p-1 shadow-[0_4px_10px_rgba(55,64,73,0.11)]">
                                <img
                                  src={getGenusImage(notification.genusName)}
                                  alt={notification.genusName}
                                  onError={(e) =>
                                    (e.currentTarget.src = defaultImg)
                                  }
                                  className="max-h-full max-w-full object-contain"
                                />
                              </div>
                            ) : notification.type === "title_unlocked" ? (
                              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#93CA87]/50 bg-white text-xl shadow-sm">
                                <span aria-hidden="true">👑</span>
                              </div>
                            ) : notification.actorPhotoURL ? (
                              <img
                                src={notification.actorPhotoURL}
                                alt=""
                                className="h-12 w-12 shrink-0 rounded-xl border border-[#93CA87]/40 object-cover shadow-sm"
                              />
                            ) : notification.actorName ? (
                              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#374049] text-sm font-semibold text-white shadow-sm">
                                {actorInitial}
                              </div>
                            ) : (
                              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#93CA87]/40 bg-white text-base text-[#24C3BE]">
                                {notificationIcon}
                              </div>
                            )}

                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                                <div className="text-sm font-semibold leading-snug text-[#374049]">
                                  {notification.type === "genus_unlocked" ? (
                                    <>
                                      You unlocked the genus card{" "}
                                      <span className="italic">
                                        {notification.genusName}!
                                      </span>
                                    </>
                                  ) : isTitleUnlock ? (
                                    <>
                                      You unlocked the title{" "}
                                      <span>
                                        “{currentNotificationTitle.label}”
                                      </span>
                                      {currentNotificationTitle.description && (
                                        <>
                                          {" "}
                                          by{" "}
                                          {formatTitleUnlockCondition(
                                            currentNotificationTitle.description,
                                          )}
                                        </>
                                      )}
                                      .
                                    </>
                                  ) : (
                                    getCurrentNotificationMessage(notification)
                                  )}
                                </div>

                                <div className="inline-flex shrink-0 items-center gap-2 text-[10px] text-slate-400">
                                  <span aria-hidden="true">•</span>
                                  {formatRelativeTime(notification.createdAt)}
                                </div>
                              </div>

                              {notification.detail && !isTitleUnlock && (
                                <div
                                  className={`mt-1 text-xs leading-relaxed text-slate-500 ${
                                    isTitleUnlock ? "italic" : ""
                                  }`}
                                  style={
                                    isTitleUnlock
                                      ? {
                                          fontFamily: "Cormorant Garamond",
                                          fontSize: "15px",
                                        }
                                      : undefined
                                  }
                                >
                                  {notification.detail}
                                </div>
                              )}

                              <KudosNotificationSummary
                                message={notification.kudosMessage}
                                updatedAt={notification.kudosUpdatedAt}
                              />

                              {isFriendRequest && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();

                                      respondToFriendRequest(
                                        notification,
                                        "accept",
                                      );
                                    }}
                                    disabled={respondingToThisRequest}
                                    className="rounded-xl border border-[#374049] bg-white px-4 py-2 text-xs font-semibold text-[#374049] transition hover:border-[#24C3BE]/60 hover:bg-[#FDFBF8] disabled:cursor-not-allowed disabled:opacity-60"
                                  >
                                    {respondingToThisRequest
                                      ? "Processing..."
                                      : "Accept"}
                                  </button>

                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();

                                      respondToFriendRequest(
                                        notification,
                                        "deny",
                                      );
                                    }}
                                    disabled={respondingToThisRequest}
                                    className="rounded-xl border border-[#374049] bg-white px-4 py-2 text-xs font-semibold text-[#374049] transition hover:border-[#24C3BE]/60 hover:bg-[#FDFBF8] disabled:cursor-not-allowed disabled:opacity-60"
                                  >
                                    Deny
                                  </button>
                                </div>
                              )}
                            </div>

                            {!notification.read && (
                              <div className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full bg-[#24C3BE]" />
                            )}
                          </div>
                        );

                        const rowClassName = `relative block w-full overflow-hidden rounded-2xl border p-4 text-left shadow-sm transition hover:-translate-y-[1px] hover:shadow-md sm:p-5 ${
                          notification.read
                            ? "border-[#374049]/15 bg-white"
                            : "border-[#374049]/25 bg-white"
                        }`;

                        if (isFriendRequest) {
                          return (
                            <div key={notification.id} className={rowClassName}>
                              {notificationContent}
                            </div>
                          );
                        }

                        // Taxonomic updates are informational here. Their
                        // acknowledgment/review workflow remains separate.
                        if (isTaxonomicNotice) {
                          return (
                            <div
                              key={notification.id}
                              className={`relative w-full overflow-hidden rounded-2xl border bg-white p-4 text-left shadow-sm sm:p-5 ${
                                notification.read
                                  ? "border-[#374049]/15"
                                  : "border-[#374049]/25"
                              }`}
                            >
                              {notificationContent}
                            </div>
                          );
                        }

                        // Reuse Community's existing profile confirmation dialog.
                        if (isFriendshipAccepted) {
                          return (
                            <button
                              key={notification.id}
                              type="button"
                              onClick={() => {
                                markNotificationRead(notification);
                                requestCommunityProfile(notification);
                              }}
                              className={rowClassName}
                            >
                              {notificationContent}
                            </button>
                          );
                        }

                        if (isGenusUnlock) {
                          return (
                            <button
                              key={notification.id}
                              type="button"
                              onClick={() => {
                                markNotificationRead(notification);
                                setOpenedGenusNotification(notification);
                              }}
                              className={rowClassName}
                            >
                              {notificationContent}
                            </button>
                          );
                        }

                        if (isTitleUnlock) {
                          return (
                            <button
                              key={notification.id}
                              type="button"
                              onClick={() => {
                                markNotificationRead(notification);
                                setOpenedTitleNotification(notification);
                              }}
                              className={rowClassName}
                            >
                              {notificationContent}
                            </button>
                          );
                        }

                        if (isPhotoKudosNotification) {
                          return (
                            <button
                              key={notification.id}
                              type="button"
                              onClick={() =>
                                openPhotoKudosNotification(notification)
                              }
                              className={rowClassName}
                            >
                              {notificationContent}
                            </button>
                          );
                        }

                        return notification.targetRoute ? (
                          <Link
                            key={notification.id}
                            to={notification.targetRoute}
                            onClick={() => markNotificationRead(notification)}
                            className={rowClassName}
                          >
                            {notificationContent}
                          </Link>
                        ) : (
                          <button
                            key={notification.id}
                            type="button"
                            onClick={() => markNotificationRead(notification)}
                            className={rowClassName}
                          >
                            {notificationContent}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-3xl border border-dashed border-[#93CA87]/60 bg-[#FDFBF8] px-5 py-16 text-center">
                      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-[#93CA87]/50 bg-white text-2xl text-[#24C3BE] shadow-sm">
                        ◌
                      </div>

                      <h3
                        className="mt-4 text-3xl text-[#374049]"
                        style={{ fontFamily: "Cormorant Garamond" }}
                      >
                        No activity yet
                      </h3>

                      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-500">
                        Your photos, achievements, received kudos, friend
                        requests, and discussion replies will appear here.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* COMMUNITY FEED VIEW */}
              {activeView === "community" && (
                <div className="bioindex-m-friends-section">
                  <div className="mb-4 border-b border-[#93CA87]/30 pb-4">
                    <h2
                      className="text-3xl text-[#374049]"
                      style={{ fontFamily: "Cormorant Garamond" }}
                    >
                      Friends' Updates
                    </h2>
                  </div>

                  {communityActivitiesError && (
                    <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {communityActivitiesError}
                    </div>
                  )}

                  {communityActivitiesLoading || blockRelationshipsLoading ? (
                    <div className="flex min-h-64 flex-col items-center justify-center text-center">
                      <div className="h-9 w-9 animate-spin rounded-full border-4 border-[#93CA87]/40 border-t-[#24C3BE]" />

                      <div className="mt-3 text-sm text-slate-500">
                        Loading community activity...
                      </div>
                    </div>
                  ) : visibleCommunityActivities.length > 0 ? (
                    <div className="space-y-3">
                      {communityFeedItems.map((item) =>
                        item.kind === "daily_group" ? (
                          <CommunityDailyActivityCard
                            key={item.id}
                            activities={item.activities}
                            onOpenUploads={openCommunityUploadActivity}
                            onOpenPhoto={(activity, photoRef) =>
                              openCommunityUploadActivity(activity, photoRef)
                            }
                            onOpenGenus={setOpenedGenusNotification}
                            onRequestProfile={requestCommunityProfile}
                          />
                        ) : item.kind === "genus_group" ? (
                          <CommunityGenusActivityCard
                            key={item.id}
                            activities={item.activities}
                            onOpenGenus={setOpenedGenusNotification}
                            onRequestProfile={requestCommunityProfile}
                          />
                        ) : (
                          <CommunityActivityCard
                            key={item.id}
                            activity={item.activity}
                            onOpenUploads={openCommunityUploadActivity}
                            onOpenPhoto={(activity, photoRef) =>
                              openCommunityUploadActivity(activity, photoRef)
                            }
                            onOpenGenus={setOpenedGenusNotification}
                            onRequestProfile={requestCommunityProfile}
                          />
                        ),
                      )}

                      {(hasMoreCommunityActivities ||
                        loadingMoreCommunityActivities) && (
                        <div
                          ref={communityActivitiesLoadMoreSentinelRef}
                          className="flex h-12 items-center justify-center"
                        >
                          {loadingMoreCommunityActivities && (
                            <>
                              <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#93CA87]/40 border-t-[#24C3BE]" />
                              <span className="sr-only">
                                Loading more community activity
                              </span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-3xl border border-dashed border-[#93CA87]/60 bg-[#FDFBF8] px-5 py-16 text-center">
                      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-[#93CA87]/50 bg-white text-2xl text-[#24C3BE] shadow-sm">
                        ❋
                      </div>

                      <h3
                        className="mt-4 text-3xl text-[#374049]"
                        style={{ fontFamily: "Cormorant Garamond" }}
                      >
                        No community activity
                      </h3>

                      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-500">
                        New photo summaries, title unlocks, and genus cards from
                        your BioIndex friends will appear here.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* RANKINGS VIEW */}
              {activeView === "rankings" && (
                <CommunityRankingsView
                  currentUid={currentUid}
                  profiles={visibleRankingProfiles}
                  communityTotals={rankingCommunityTotals}
                  friends={rankingFriends}
                  sentRequests={rankingSentRequests}
                  receivedRequests={rankingReceivedRequests}
                  loading={rankingsLoading || blockRelationshipsLoading}
                  error={rankingsError}
                  view={rankingView}
                  onChangeView={setRankingView}
                  taxon={rankingTaxon}
                  onChangeTaxon={setRankingTaxon}
                  taxonomicData={taxonomicRankingStats}
                  taxonomicLoading={taxonomicRankingLoading}
                  taxonomicError={taxonomicRankingError}
                  onRequestProfile={requestCommunityProfile}
                  onRequestFriend={setFriendRequestTarget}
                  onAcceptRequest={(request) =>
                    respondToRankingFriendRequest(request, true)
                  }
                  onDeclineRequest={(request) =>
                    respondToRankingFriendRequest(request, false)
                  }
                  actionUid={rankingActionUid}
                />
              )}
            </main>
          </div>

          <footer className="mt-8 border-t border-white/10 pb-2 pt-4 text-center">
            <p className="text-[11px] text-white/55">
              © {new Date().getFullYear()} BioIndex™. All rights reserved.
            </p>

            <div className="mt-1 flex justify-center gap-3 text-[10px] text-white/55">
              <Link to="/terms" className="hover:text-[#24C3BE]">
                Terms
              </Link>

              <Link to="/privacy" className="hover:text-[#24C3BE]">
                Privacy
              </Link>
              <Link to="/community-guidelines" className="hover:text-[#24C3BE]">
                Guidelines
              </Link>
              <Link to="/copyright" className="hover:text-[#24C3BE]">
                Copyright
              </Link>

              <Link to="/contact" className="hover:text-[#24C3BE]">
                Contact
              </Link>
            </div>
          </footer>
        </div>
      </div>

      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
      {voiceOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <SearchModal open voiceOnly onClose={() => setVoiceOpen(false)} />,
          document.body,
        )}

      {profileNavigationTarget && (
        <div
          className="fixed inset-0 z-[130] flex items-center justify-center bg-[#374049]/75 px-4 py-8"
          onClick={() => setProfileNavigationTarget(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="community-profile-navigation-title"
            className="w-full max-w-sm overflow-hidden rounded-3xl border border-[#93CA87]/50 bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-6 pb-6 pt-7 text-center">
              <h2
                id="community-profile-navigation-title"
                className="text-3xl leading-tight text-[#374049]"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                Visit profile?
              </h2>

              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Travel to {profileNavigationTarget.name || "this user"}’s
                profile page?
              </p>

              <div className="mt-6 flex gap-2.5">
                <button
                  type="button"
                  onClick={() => setProfileNavigationTarget(null)}
                  className="flex-1 rounded-xl border border-[#93CA87]/40 bg-white px-3 py-2.5 text-sm font-medium text-[#374049] transition hover:bg-slate-50"
                >
                  No
                </button>

                <button
                  type="button"
                  onClick={confirmCommunityProfileNavigation}
                  className="flex-1 rounded-xl bg-[#01767A] px-3 py-2.5 text-sm font-semibold text-white transition hover:bg-[#025f62]"
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {friendRequestTarget && (
        <div
          className="fixed inset-0 z-[130] flex items-center justify-center bg-[#374049]/75 px-4 py-8"
          onClick={() => {
            if (!rankingActionUid) setFriendRequestTarget(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="ranking-friend-request-title"
            className="w-full max-w-sm overflow-hidden rounded-3xl border border-[#93CA87]/50 bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-6 pb-6 pt-7 text-center">
              <h2
                id="ranking-friend-request-title"
                className="text-3xl leading-tight text-[#374049]"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                Send friend request?
              </h2>

              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Ask {friendRequestTarget.name || "this BioIndex user"} to
                connect with you?
              </p>

              <div className="mt-6 flex gap-2.5">
                <button
                  type="button"
                  disabled={!!rankingActionUid}
                  onClick={() => setFriendRequestTarget(null)}
                  className="flex-1 rounded-xl border border-[#93CA87]/40 bg-white px-3 py-2.5 text-sm font-medium text-[#374049] transition hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={!!rankingActionUid}
                  onClick={sendRankingFriendRequest}
                  className="flex-1 rounded-xl bg-[#01767A] px-3 py-2.5 text-sm font-semibold text-white transition hover:bg-[#025f62] disabled:cursor-wait disabled:opacity-60"
                >
                  {rankingActionUid ? "Sending..." : "Send request"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {openedCommunityUploadActivity && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-[#374049]/75 px-3 py-5 sm:px-6 sm:py-8"
          onClick={closeCommunityUploadActivity}
        >
          <div
            className="flex max-h-full w-full max-w-5xl flex-col overflow-hidden rounded-3xl border border-[#93CA87]/50 bg-[#FDFBF8] shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-[#93CA87]/30 bg-white px-5 py-4 sm:px-7 sm:py-5">
              <div>
                <h2
                  className="text-3xl leading-tight text-[#374049]"
                  style={{ fontFamily: "Cormorant Garamond" }}
                >
                  {openedCommunityUploadActivity.isOwnUpload
                    ? "Your uploads"
                    : `${
                        openedCommunityUploadActivity.actorName ||
                        "BioIndex User"
                      }’s uploads`}
                </h2>

                <p className="mt-1 text-xs text-slate-500">
                  {formatCommunityActivityDate(
                    openedCommunityUploadActivity.dateKey,
                  )}
                  {Number(
                    openedCommunityUploadActivity.uploadedPhotoCount || 0,
                  ) > 0 && (
                    <>
                      {" "}
                      ·{" "}
                      {Number(
                        openedCommunityUploadActivity.uploadedPhotoCount || 0,
                      ).toLocaleString()}{" "}
                      {Number(
                        openedCommunityUploadActivity.uploadedPhotoCount || 0,
                      ) === 1
                        ? "photo"
                        : "photos"}
                    </>
                  )}
                </p>
              </div>

              <button
                type="button"
                onClick={closeCommunityUploadActivity}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#93CA87]/40 bg-white text-lg text-[#374049] transition hover:border-[#24C3BE]/60 hover:bg-[#24C3BE]/10"
                aria-label="Close uploaded photos"
              >
                ×
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-7 sm:py-8">
              {communityUploadLoading ? (
                <div className="flex min-h-64 flex-col items-center justify-center text-center">
                  <div className="h-9 w-9 animate-spin rounded-full border-4 border-[#93CA87]/40 border-t-[#24C3BE]" />
                  <div className="mt-3 text-sm text-slate-500">
                    Loading uploaded photos...
                  </div>
                </div>
              ) : communityUploadError ? (
                <div className="rounded-2xl border border-dashed border-[#93CA87]/60 bg-white px-5 py-14 text-center text-sm text-slate-500">
                  {communityUploadError}
                </div>
              ) : (
                <div
                  className={`mx-auto grid max-w-4xl grid-cols-1 justify-items-center gap-6 sm:gap-8 ${
                    communityUploadSpecies.length === 2
                      ? "sm:grid-cols-2"
                      : communityUploadSpecies.length > 2
                        ? "sm:grid-cols-2 md:grid-cols-3"
                        : ""
                  }`}
                >
                  {communityUploadSpecies.map((species) => {
                    const forms = Array.isArray(species.forms)
                      ? species.forms
                      : [];
                    const activeIndex =
                      communityUploadPreviewBySpecies[species.id] ?? 0;
                    const activeForm = forms[activeIndex] || forms[0];
                    const cardImg = activeForm?.url;
                    const isImgLoaded = communityUploadLoadedImages[species.id];

                    return (
                      <div
                        key={species.id}
                        onClick={() => {
                          const selectedForm = forms[activeIndex] || forms[0];
                          const slideIndex = communityUploadSlides.findIndex(
                            (slide) =>
                              slide.species.id === species.id &&
                              (slide.form?.id || slide.form?.url) ===
                                (selectedForm?.id || selectedForm?.url),
                          );

                          if (slideIndex >= 0) {
                            openCommunityUploadSlide(slideIndex);
                          }
                        }}
                        className="flex w-64 cursor-pointer flex-col items-center overflow-visible rounded-b-2xl border border-[#93CA87]/40 bg-white text-center shadow-sm transition hover:-translate-y-[2px] hover:shadow-md"
                      >
                        <div className="relative flex aspect-[4/3] w-full items-center justify-center bg-white">
                          {!isImgLoaded && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#24C3BE] border-t-transparent" />
                            </div>
                          )}

                          {cardImg ? (
                            <img
                              key={cardImg}
                              src={cardImg}
                              alt={species.name || "Species"}
                              loading="lazy"
                              onLoad={() =>
                                setCommunityUploadLoadedImages((previous) => ({
                                  ...previous,
                                  [species.id]: true,
                                }))
                              }
                              onError={() =>
                                setCommunityUploadLoadedImages((previous) => ({
                                  ...previous,
                                  [species.id]: true,
                                }))
                              }
                              className={`absolute max-h-full max-w-full object-contain transition-opacity duration-300 ${
                                isImgLoaded ? "opacity-100" : "opacity-0"
                              }`}
                            />
                          ) : (
                            <div className="text-sm text-slate-400">
                              No photo
                            </div>
                          )}

                          {forms.length > 1 && (
                            <div
                              className="absolute bottom-2 left-2 z-20 flex justify-start gap-1"
                              onClick={(event) => event.stopPropagation()}
                            >
                              {forms.map((form, formIndex) => (
                                <button
                                  key={form.id || form.url || formIndex}
                                  type="button"
                                  onClick={() => {
                                    setCommunityUploadLoadedImages(
                                      (previous) => ({
                                        ...previous,
                                        [species.id]: false,
                                      }),
                                    );
                                    setCommunityUploadPreviewBySpecies(
                                      (previous) => ({
                                        ...previous,
                                        [species.id]: formIndex,
                                      }),
                                    );
                                  }}
                                  className={`h-5 w-5 overflow-hidden rounded border bg-white/80 transition ${
                                    formIndex === activeIndex
                                      ? "border-[#24C3BE] ring-2 ring-[#24C3BE]/30"
                                      : "border-white/70 hover:border-[#24C3BE]/70"
                                  }`}
                                >
                                  <img
                                    src={form.url}
                                    alt=""
                                    className="h-full w-full object-cover"
                                  />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="px-3 pb-5 pt-3">
                          <h3
                            className="text-xl italic text-[#374049]"
                            style={{ fontFamily: "Cormorant Garamond" }}
                          >
                            {species.name || "—"}
                          </h3>
                          <p className="text-sm text-[#374049]/60">
                            {species.common || ""}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <SpeciesPhotoModal
        selected={selectedCommunitySpecies}
        setSelected={setSelectedCommunitySpecies}
        photoIndex={communityUploadPhotoIndex}
        setPhotoIndex={setCommunityUploadPhotoIndex}
        dataUID={selectedCommunitySpecies?.ownerUid || ""}
        currentUID={auth.currentUser?.uid}
        uid={selectedCommunitySpecies?.ownerUid || ""}
        isOwnCollection={false}
        setSpeciesList={setCommunityUploadSpecies}
      />

      {selectedCommunitySpecies && communityUploadSlides.length > 1 && (
        <div className="pointer-events-none fixed inset-0 z-[65]">
          <div
            className="absolute"
            style={
              communityUploadPhotoBounds
                ? {
                    left: communityUploadPhotoBounds.left,
                    top: communityUploadPhotoBounds.top,
                    width:
                      communityUploadPhotoBounds.right -
                      communityUploadPhotoBounds.left,
                  }
                : {
                    left: "max(22px, calc(50% - 240px))",
                    right: "max(22px, calc(50% - 240px))",
                    top: "50%",
                  }
            }
          >
            <button
              type="button"
              onClick={() => moveCommunityUploadSlide(-1)}
              disabled={communityUploadSlideIndex === 0}
              aria-label="Previous photo from this upload"
              className={`pointer-events-auto absolute left-0 top-0 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border text-2xl shadow-lg backdrop-blur-sm transition sm:h-12 sm:w-12 ${
                communityUploadSlideIndex === 0
                  ? "cursor-not-allowed border-white/15 bg-[#374049]/25 text-white/30"
                  : "border-white/40 bg-[#374049]/85 text-white hover:bg-[#01767A]"
              }`}
            >
              ‹
            </button>
            <button
              type="button"
              onClick={() => moveCommunityUploadSlide(1)}
              disabled={
                communityUploadSlideIndex === communityUploadSlides.length - 1
              }
              aria-label="Next photo from this upload"
              className={`pointer-events-auto absolute right-0 top-0 flex h-10 w-10 translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border text-2xl shadow-lg backdrop-blur-sm transition sm:h-12 sm:w-12 ${
                communityUploadSlideIndex === communityUploadSlides.length - 1
                  ? "cursor-not-allowed border-white/15 bg-[#374049]/25 text-white/30"
                  : "border-white/40 bg-[#374049]/85 text-white hover:bg-[#01767A]"
              }`}
            >
              ›
            </button>
          </div>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border border-white/35 bg-[#374049]/85 px-3 py-1.5 text-[10px] font-semibold text-white shadow-lg backdrop-blur-sm sm:bottom-6">
            {communityUploadSlideIndex + 1} of {communityUploadSlides.length} in
            this upload
          </div>
        </div>
      )}

      {openedGenusNotification && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-[#374049]/75 px-4 py-8"
          onClick={() => setOpenedGenusNotification(null)}
        >
          <div
            className="flex flex-col items-center"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex w-[250px] flex-col overflow-hidden rounded-2xl border border-[#93CA87]/40 bg-white shadow-2xl sm:w-[300px]">
              <div className="flex h-[300px] items-center justify-center p-6 sm:h-[360px] sm:p-8">
                <img
                  src={getGenusImage(openedGenusNotification.genusName)}
                  alt={openedGenusNotification.genusName || ""}
                  onError={(event) => (event.currentTarget.src = defaultImg)}
                  className="max-h-full max-w-full object-contain"
                />
              </div>

              <div className="pt-1 pb-4">
                <div
                  className="text-center text-3xl italic leading-tight text-[#374049]"
                  style={{ fontFamily: "Cormorant Garamond" }}
                >
                  {openedGenusNotification.genusName}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {openedTitleNotification && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-[#374049]/75 px-4 py-8"
          onClick={() => setOpenedTitleNotification(null)}
        >
          <div
            className="w-full max-w-sm overflow-hidden rounded-3xl border border-[#93CA87]/50 bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex flex-col items-center px-6 pb-6 pt-7 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full border border-[#93CA87]/50 bg-[#FDFBF8] text-2xl shadow-sm">
                <span aria-hidden="true">👑</span>
              </div>

              <div className="mt-4 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#374049]">
                Title Unlocked
              </div>

              <div
                className="mt-2 text-3xl leading-tight text-[#374049]"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                {openedTitleDisplay.label}
              </div>

              {openedTitleDisplay.description && (
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">
                  {openedTitleDisplay.description}
                </p>
              )}

              <div className="mt-5 w-full border-t border-[#93CA87]/30 pt-5">
                {openedTitleDisplay.label === currentProfileTitle ? (
                  <div className="rounded-xl bg-[#FDFBF8] px-4 py-3 text-sm text-[#374049]">
                    This is already your current title.
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-[#374049]">
                      Make this your current title?
                    </p>

                    <p className="mt-1 text-xs text-slate-400">
                      Current:{" "}
                      <span className="font-medium text-slate-500">
                        {currentProfileTitle || "Beginner"}
                      </span>
                    </p>

                    {equipNotificationTitleError && (
                      <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                        {equipNotificationTitleError}
                      </div>
                    )}

                    <div className="mt-4 flex w-full gap-2.5">
                      <button
                        type="button"
                        onClick={() => setOpenedTitleNotification(null)}
                        disabled={equippingNotificationTitle}
                        className="flex-1 rounded-xl border border-[#93CA87]/40 bg-white px-3 py-2.5 text-sm font-medium text-[#374049] transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        Not Now
                      </button>

                      <button
                        type="button"
                        onClick={equipTitleFromNotification}
                        disabled={equippingNotificationTitle}
                        className={`flex-1 rounded-xl px-3 py-2.5 text-sm font-semibold text-white transition ${
                          equippingNotificationTitle
                            ? "cursor-not-allowed bg-slate-400"
                            : "bg-[#24C3BE] hover:bg-[#1faeaa]"
                        }`}
                      >
                        {equippingNotificationTitle
                          ? "Changing..."
                          : "Use Title"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {topicPhotoPickerOpen && (
        <div className="fixed inset-0 z-[100] overflow-y-auto bg-[#374049] px-3 py-6 sm:px-6">
          <ProfileFavorites
            isOwnProfile={true}
            profile={{ favorites: [] }}
            savedMessage={false}
            openFavoriteViewer={() => {}}
            openFavoriteSelector={() => {}}
            favSelectorOpen={topicPhotoPickerOpen}
            setFavSelectorOpen={(open) => {
              if (!open) {
                closeTopicPhotoPicker();
              }
            }}
            selectedSpeciesForFav={topicPickerSelectedSpecies}
            setSelectedSpeciesForFav={setTopicPickerSelectedSpecies}
            speciesWithPhotos={topicSpeciesWithPhotos}
            assignFavorite={selectExistingTopicPhoto}
            pickerOnly={true}
            forumPhotoPickerMode={true}
          />
        </div>
      )}

      {newTopicOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 px-3 py-3 sm:px-6 sm:py-6"
          onClick={closeNewTopicModal}
        >
          <div
            className="flex max-h-[calc(100vh-1.5rem)] w-full max-w-4xl flex-col overflow-hidden rounded-3xl border border-[#93CA87]/40 bg-[#FDFBF8] shadow-2xl sm:max-h-[calc(100vh-3rem)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="border-b border-[#93CA87]/30 bg-[#374049] px-5 py-5 text-center sm:px-7">
              <h2
                className="text-3xl text-[#FCFFF6]"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                Start a New Topic
              </h2>

              <p className="mt-1 text-xs text-white/60">
                Share a question, observation, idea, or discussion with the
                BioIndex community.
              </p>
            </div>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5 sm:p-6">
              {newTopicError && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {newTopicError}
                </div>
              )}

              <div>
                <div className="mb-1 flex items-center justify-between gap-3">
                  <label
                    htmlFor="new-topic-title"
                    className="text-xs font-semibold text-slate-600"
                  >
                    Topic Title
                  </label>

                  <span className="text-[10px] text-slate-400">
                    {newTopicTitle.length}/{MAX_TOPIC_TITLE_LENGTH}
                  </span>
                </div>

                <input
                  id="new-topic-title"
                  type="text"
                  value={newTopicTitle}
                  onChange={(event) => {
                    setNewTopicTitle(
                      event.target.value.slice(0, MAX_TOPIC_TITLE_LENGTH),
                    );
                    setNewTopicError("");
                  }}
                  placeholder="Give your discussion a clear title"
                  maxLength={MAX_TOPIC_TITLE_LENGTH}
                  disabled={creatingTopic}
                  className="h-12 w-full rounded-xl border border-[#93CA87]/40 bg-white px-4 text-sm text-[#374049] outline-none transition placeholder:text-slate-400 focus:border-[#24C3BE]/70 focus:ring-2 focus:ring-[#24C3BE]/15 disabled:cursor-not-allowed disabled:opacity-60"
                />
              </div>

              <div>
                <label
                  htmlFor="new-topic-category"
                  className="mb-1 block text-xs font-semibold text-slate-600"
                >
                  Category
                </label>

                <select
                  id="new-topic-category"
                  value={newTopicCategory}
                  onChange={(event) => {
                    setNewTopicCategory(event.target.value);
                    setNewTopicError("");
                  }}
                  disabled={creatingTopic}
                  className="h-12 w-full rounded-xl border border-[#93CA87]/40 bg-white px-4 text-sm text-[#374049] outline-none transition focus:border-[#24C3BE]/70 focus:ring-2 focus:ring-[#24C3BE]/15 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {FORUM_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between gap-3">
                  <label
                    htmlFor="new-topic-body"
                    className="text-xs font-semibold text-slate-600"
                  >
                    Message
                  </label>

                  <span className="text-[10px] text-slate-400">
                    {newTopicBody.length.toLocaleString()}/
                    {MAX_TOPIC_BODY_LENGTH.toLocaleString()}
                  </span>
                </div>

                <textarea
                  ref={newTopicBodyRef}
                  id="new-topic-body"
                  value={newTopicBody}
                  onChange={(event) => {
                    setNewTopicBody(
                      event.target.value.slice(0, MAX_TOPIC_BODY_LENGTH),
                    );
                    setNewTopicError("");
                  }}
                  placeholder="Write your topic here..."
                  rows={8}
                  maxLength={MAX_TOPIC_BODY_LENGTH}
                  disabled={creatingTopic}
                  className="w-full resize-y rounded-xl border border-[#93CA87]/40 bg-white px-4 py-3 text-sm leading-relaxed text-[#374049] outline-none transition placeholder:text-slate-400 focus:border-[#24C3BE]/70 focus:ring-2 focus:ring-[#24C3BE]/15 disabled:cursor-not-allowed disabled:opacity-60"
                />
                <ForumLinkInsert
                  textareaRef={newTopicBodyRef}
                  text={newTopicBody}
                  onChange={(value) => {
                    setNewTopicBody(value);
                    setNewTopicError("");
                  }}
                  maxLength={MAX_TOPIC_BODY_LENGTH}
                  disabled={creatingTopic}
                />
              </div>

              <div>
                <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-xs font-semibold text-slate-600">
                    Optional Image
                  </div>

                  {newTopicCategory === "Species Identification" && (
                    <button
                      type="button"
                      onClick={openTopicPhotoPicker}
                      disabled={creatingTopic || topicPhotoPickerLoading}
                      className="self-start rounded-xl border border-[#93CA87]/40 bg-white px-3 py-1.5 text-xs font-medium text-[#374049] transition hover:border-[#24C3BE]/60 hover:bg-[#24C3BE]/10 disabled:cursor-not-allowed disabled:opacity-60 sm:self-auto"
                    >
                      {topicPhotoPickerLoading
                        ? "Loading Photos..."
                        : newTopicImagePreview
                          ? "Choose Another Uploaded Photo"
                          : "Choose from My Photos"}
                    </button>
                  )}
                </div>

                {topicPhotoPickerError && (
                  <div className="mb-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {topicPhotoPickerError}
                  </div>
                )}

                {!newTopicImagePreview ? (
                  <label
                    onDragEnter={handleTopicImageDragEnter}
                    onDragOver={handleTopicImageDragOver}
                    onDragLeave={handleTopicImageDragLeave}
                    onDrop={handleTopicImageDrop}
                    className={`flex cursor-pointer items-center justify-center gap-4 rounded-2xl border border-dashed px-5 py-4 text-center transition ${
                      topicImageDragging
                        ? "border-[#24C3BE] bg-[#24C3BE]/10 shadow-inner"
                        : "border-[#93CA87]/60 bg-white hover:border-[#24C3BE]/60 hover:bg-[#24C3BE]/5"
                    } ${
                      creatingTopic
                        ? "cursor-not-allowed opacity-60"
                        : "cursor-pointer"
                    }`}
                  >
                    <span className="shrink-0 text-2xl text-[#24C3BE]">▧</span>

                    <span className="min-w-0 text-left">
                      <span className="block text-sm font-medium text-[#374049]">
                        {topicImageDragging
                          ? "Drop the image here"
                          : "Drag and drop an image here"}
                      </span>

                      <span className="mt-0.5 block text-[11px] text-slate-400">
                        or click to choose an image · Maximum 8 MB
                      </span>
                    </span>

                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleTopicImageChange}
                      disabled={creatingTopic}
                      className="hidden"
                    />
                  </label>
                ) : (
                  <div
                    onDragEnter={handleTopicImageDragEnter}
                    onDragOver={handleTopicImageDragOver}
                    onDragLeave={handleTopicImageDragLeave}
                    onDrop={handleTopicImageDrop}
                    className={`overflow-hidden rounded-2xl border bg-white transition ${
                      topicImageDragging
                        ? "border-[#24C3BE] ring-2 ring-[#24C3BE]/15"
                        : "border-[#93CA87]/40"
                    }`}
                  >
                    <div className="relative flex h-36 justify-center bg-[#374049]/5">
                      <img
                        src={newTopicImagePreview}
                        alt="Topic preview"
                        className="h-full max-w-full object-contain"
                      />

                      {topicImageDragging && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/85">
                          <span className="rounded-xl border border-[#24C3BE]/50 bg-[#FDFBF8] px-4 py-2 text-sm font-semibold text-[#374049] shadow-sm">
                            Drop to replace image
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2 border-t border-[#93CA87]/30 px-4 py-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <div className="truncate text-xs font-medium text-[#374049]">
                          {newTopicImage
                            ? newTopicImage.name
                            : selectedExistingTopicPhoto?.scientificName ||
                              "Uploaded BioIndex photo"}
                        </div>

                        <div className="mt-0.5 text-[10px] text-slate-400">
                          {newTopicImage
                            ? `${(newTopicImage.size / (1024 * 1024)).toFixed(
                                2,
                              )} MB`
                            : selectedExistingTopicPhoto
                              ? selectedExistingTopicPhoto.commonName ||
                                "Selected from My Photos"
                              : ""}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeTopicImage}
                        disabled={creatingTopic}
                        className="self-start rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60 sm:self-auto"
                      >
                        Remove Image
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-[#93CA87]/30 bg-white px-5 py-4 sm:flex-row sm:justify-end sm:px-7">
              <button
                type="button"
                onClick={closeNewTopicModal}
                disabled={creatingTopic}
                className="rounded-xl border border-[#93CA87]/40 bg-white px-5 py-2.5 text-sm font-medium text-[#374049] transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={createTopic}
                disabled={creatingTopic}
                className={`rounded-xl px-6 py-2.5 text-sm font-semibold text-white transition ${
                  creatingTopic
                    ? "cursor-not-allowed bg-slate-400"
                    : "bg-[#24C3BE] hover:bg-[#1faeaa]"
                }`}
              >
                {creatingTopic ? "Publishing..." : "Publish Topic"}
              </button>
            </div>
          </div>
        </div>
      )}
      <BioIndexMobilePrimaryNav
        onSearch={() => setSearchOpen(true)}
        onVoiceSearch={() => setVoiceOpen(true)}
      />
    </div>
  );
}
